import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { 
  TextInput, 
  Select, 
  SelectItem, 
  Button,
  InlineNotification,
  TextArea,
  Toggle,
  Modal,
  Tag,
  FileUploader,
  NumberInput,
  Search,
  SkeletonPlaceholder,
  SkeletonText,
  Loading,
  Checkbox,
  ProgressIndicator,
  ProgressStep,
  Tile
} from '@carbon/react';
import { Close, ChevronLeft, ChevronRight, Add, Restaurant, Run, GameConsole, Activity, Time, TrashCan, Package, Checkmark, Filter, ArrowLeft, ArrowRight, Group, CheckmarkFilled, User, UserFollow } from '@carbon/icons-react';
import axiosInstance from '../utils/axiosInstance';
import {
  CONCURRENCY_CONFLICT_MESSAGE,
  CONCURRENCY_CONFLICT_TITLE,
  getApiErrorMessage,
  isConcurrencyConflictError,
} from '../utils/apiErrors';
import authService from '../services/authService';
import NativeDatePicker from '../components/NativeDatePicker';
import './ResidentProfile.css';

// Truncate text to a maximum number of characters, adding "..." if it exceeds the limit
const truncateText = (text, maxLength) => {
  if (!text) return text;
  const str = String(text);
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength) + '...';
};

// ===========================================
// Image upload constraints (same as the employee profile flow)
// ===========================================
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp'];

// ===========================================
// SVG Icon Components (Functional, minimal)
// ===========================================
const Icons = {
  ArrowLeft: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7"/>
    </svg>
  ),
  User: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  CheckCircle: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <path d="M22 4L12 14.01l-3-3"/>
    </svg>
  ),
  AlertTriangle: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  Hospital: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1"/>
      <path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16"/>
    </svg>
  ),
  Calendar: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  MapPin: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  ),
  Heart: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
    </svg>
  ),
  FileText: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14,2 14,8 20,8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10,9 9,9 8,9"/>
    </svg>
  ),
  Download: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7,10 12,15 17,10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  ),
  Upload: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="17,8 12,3 7,8"/>
      <line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  ),
  Camera: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
    </svg>
  ),
  Refresh: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23,4 23,10 17,10"/>
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
    </svg>
  ),
  Home: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9,22 9,12 15,12 15,22"/>
    </svg>
  ),
  Activity: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22,12 18,12 15,21 9,3 6,12 2,12"/>
    </svg>
  ),
  Coffee: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8h1a4 4 0 0 1 0 8h-1"/>
      <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/>
      <line x1="6" y1="1" x2="6" y2="4"/>
      <line x1="10" y1="1" x2="10" y2="4"/>
      <line x1="14" y1="1" x2="14" y2="4"/>
    </svg>
  ),
  Users: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  Plus: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/>
      <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  ),
  Edit: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  ),
  AlertCircle: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  ),
  Eye: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ),
  EyeOff: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  ),
  Phone: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  ),
  Mail: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  ),
  Clock: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12,6 12,12 16,14"/>
    </svg>
  ),
  Shield: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  Star: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26 12,2"/>
    </svg>
  ),
  Utensils: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/>
      <path d="M7 2v20"/>
      <path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/>
    </svg>
  ),
  Bed: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 4v16"/>
      <path d="M2 8h18a2 2 0 0 1 2 2v10"/>
      <path d="M2 17h20"/>
      <path d="M6 8v9"/>
    </svg>
  ),
  Save: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
      <polyline points="17,21 17,13 7,13 7,21"/>
      <polyline points="7,3 7,8 15,8"/>
    </svg>
  ),
  Sun: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  ),
  Moon: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  ),
  MinusCircle: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="8" y1="12" x2="16" y2="12"/>
    </svg>
  ),
  ArrowRight: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"/>
      <polyline points="12,5 19,12 12,19"/>
    </svg>
  ),
  ArrowDown: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/>
      <polyline points="19,12 12,19 5,12"/>
    </svg>
  ),
  MoreHorizontal: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="1"/>
      <circle cx="19" cy="12" r="1"/>
      <circle cx="5" cy="12" r="1"/>
    </svg>
  ),
  ChevronDown: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6,9 12,15 18,9"/>
    </svg>
  ),
  X: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  Pencil: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
    </svg>
  ),
  Copy: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  ),
  Wallet: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/>
      <path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/>
      <path d="M18 12a2 2 0 0 0 0 4h4v-4z"/>
    </svg>
  ),
  Money: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="6" width="20" height="12" rx="2"/>
      <circle cx="12" cy="12" r="2.5"/>
      <path d="M6 12h.01M18 12h.01"/>
    </svg>
  ),
  ArrowUpRight: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="7" y1="17" x2="17" y2="7"/>
      <polyline points="7,7 17,7 17,17"/>
    </svg>
  ),
  ArrowDownRight: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="7" y1="7" x2="17" y2="17"/>
      <polyline points="17,7 17,17 7,17"/>
    </svg>
  ),
  Package: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
      <line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
  ),
  Image: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
      <circle cx="8.5" cy="8.5" r="1.5"/>
      <polyline points="21 15 16 10 5 21"/>
    </svg>
  ),
};

// Flag Icons
const FlagIcons = {
  Serbian: () => (
    <svg viewBox="0 0 21 15" fill="none">
      {/* Horizontal stripes */}
      <rect width="21" height="5" fill="#C6363C"/>
      <rect y="5" width="21" height="5" fill="#0C4076"/>
      <rect y="10" width="21" height="5" fill="#FFFFFF"/>
      {/* Coat of arms - simplified */}
      <g transform="translate(6, 4)">
        {/* Shield background */}
        <path d="M 2.5 0 L 5 0 Q 5.5 0 5.5 0.5 L 5.5 5 Q 5.5 6 4.5 6.5 Q 3.75 7 3.75 7 Q 3.75 7 3 6.5 Q 2 6 2 5 L 2 0.5 Q 2 0 2.5 0 Z" fill="#C6363C"/>
        {/* White cross */}
        <rect x="3.5" y="1" width="0.5" height="4.5" fill="#FFFFFF"/>
        <rect x="2.25" y="2.75" width="3" height="0.5" fill="#FFFFFF"/>
        {/* Four C symbols (očila) simplified as white dots */}
        <circle cx="2.75" cy="1.75" r="0.25" fill="#FFFFFF"/>
        <circle cx="4.75" cy="1.75" r="0.25" fill="#FFFFFF"/>
        <circle cx="2.75" cy="4.25" r="0.25" fill="#FFFFFF"/>
        <circle cx="4.75" cy="4.25" r="0.25" fill="#FFFFFF"/>
        {/* Crown simplified */}
        <path d="M 2.25 0 L 2.25 0.5 L 2.75 0.75 L 3.75 0.75 L 4.25 0.75 L 5.25 0.5 L 5.25 0 Z" fill="#F1C40F"/>
      </g>
    </svg>
  ),
  Hungarian: () => (
    <svg viewBox="0 0 21 15" fill="none">
      <rect width="21" height="5" fill="#CE2939"/>
      <rect y="5" width="21" height="5" fill="#FFFFFF"/>
      <rect y="10" width="21" height="5" fill="#477050"/>
    </svg>
  ),
  German: () => (
    <svg viewBox="0 0 21 15" fill="none">
      <rect width="21" height="5" fill="#000000"/>
      <rect y="5" width="21" height="5" fill="#DD0000"/>
      <rect y="10" width="21" height="5" fill="#FFCE00"/>
    </svg>
  ),
  English: () => (
    <svg viewBox="0 0 21 15" fill="none">
      <rect width="21" height="15" fill="#012169"/>
      <path d="M0 0L21 15M21 0L0 15" stroke="#FFFFFF" strokeWidth="3"/>
      <path d="M0 0L21 15M21 0L0 15" stroke="#C8102E" strokeWidth="2"/>
      <path d="M10.5 0V15M0 7.5H21" stroke="#FFFFFF" strokeWidth="5"/>
      <path d="M10.5 0V15M0 7.5H21" stroke="#C8102E" strokeWidth="3"/>
    </svg>
  ),
};

// ===========================================
// Mock Data - Simulated API Response
// ===========================================
const mockResidentData = {
  id: 1,
  avatar: 'https://images.unsplash.com/photo-1566616213894-2d4e1baee5d8?w=200&h=200&fit=crop',
  firstName: 'Marija',
  lastName: 'Petrović',
  parentName: 'Ivan',
  fullName: 'Marija Petrović',
  gender: 'Ženski',
  dateOfBirth: '1942-03-15',
  age: 83,
  language: 'Srpski',
  nationalId: '1503942785024',
  status: 'active', // active, absent, hospitalized
  careLevel: 'medium', // low, medium, high
  roomNumber: '204',
  floor: 2,
  building: 'A',
  admissionDate: '2023-06-12',
  
  // Administrative
  insuranceNumber: 'ZDR-2024-156789',
  pensionNumber: 'PIO-1942-034521',
  
  // Assigned caregiver
  caregiver: {
    id: 12,
    name: 'Ana Jovanović',
    role: 'Medicinska sestra',
    phone: '+381 64 123 4567',
    initials: 'AJ'
  },
  
  // Quick notes
  notes: '',
  
  // Alerts
  alerts: [
    { type: 'warning', title: 'Dijeta', description: 'Smanjen unos soli' },
    { type: 'error', title: 'Alergija', description: 'Penicilin - strogo izbegavati' },
    { type: 'info', title: 'Mobilnost', description: 'Koristi hodalicu' }
  ],
  
  // Last activities
  lastActivities: [
    { type: 'checkup', title: 'Zdravstveni pregled', time: '2 sata ranije' },
    { type: 'meal', title: 'Ručak serviran', time: '4 sata ranije' },
    { type: 'visit', title: 'Poseta porodice', time: 'Juče u 15:00' }
  ],
  
  // Documents
  documents: [
    { id: 1, name: 'Lična karta', type: 'id', date: '2023-06-12' },
    { id: 2, name: 'Zdravstveni karton', type: 'medical', date: '2024-01-15' },
    { id: 3, name: 'Ugovor o smeštaju', type: 'contract', date: '2023-06-12' },
    { id: 4, name: 'Punomoćje', type: 'legal', date: '2023-06-15' },
    { id: 5, name: 'Otpusna lista', type: 'medical', date: '2024-11-20' }
  ],
  
  // Location details
  location: {
    room: '204',
    floor: '2. sprat',
    building: 'Zgrada A',
    wing: 'Istočno krilo',
    bedPosition: 'Pored prozora',
    roomType: 'Dvokrevetna soba',
    roommates: ['Milica Nikolić']
  },
  
  // Accommodation
  accommodation: {
    moveInDate: '2023-06-12',
    contractType: 'Neograničeno',
    paymentStatus: 'Redovan',
    monthlyFee: '85,000 RSD',
    lastPayment: '2025-12-28',
    amenities: ['TV', 'Mini frižider', 'Privatno kupatilo', 'Balkon']
  },
  
  // Activities for today
  activities: [
    { id: 1, name: 'Jutarnja gimnastika', time: '08:00', location: 'Sala za vežbanje', today: true },
    { id: 2, name: 'Radna terapija', time: '10:30', location: 'Radionica', today: true },
    { id: 3, name: 'Grupna šetnja', time: '15:00', location: 'Vrt', today: true },
    { id: 4, name: 'Muzička večer', time: '18:00', location: 'Dnevni boravak', today: false }
  ],
  
  // Nutrition
  nutrition: {
    dietType: 'Dijabetička dijeta',
    mealPlan: 'Plan B - Smanjen unos šećera',
    restrictions: ['Bez šećera', 'Smanjena so', 'Bez glutena'],
    preferences: ['Domaća kuhinja', 'Topli obroci', 'Voće za užinu'],
    allergies: ['Orasi', 'Školjke'],
    fluidIntake: '1.5L dnevno',
    lastMeal: 'Ručak - 12:30'
  },
  
  // Relatives
  relatives: [
    {
      id: 1,
      name: 'Petar Petrović',
      relation: 'Sin',
      phone: '+381 64 987 6543',
      email: 'petar.petrovic@email.com',
      isPrimary: true,
      initials: 'PP'
    },
    {
      id: 2,
      name: 'Jovana Petrović',
      relation: 'Unuka',
      phone: '+381 65 111 2222',
      email: 'jovana.p@email.com',
      isPrimary: false,
      initials: 'JP'
    }
  ],
  
  // Timeline
  timeline: [
    {
      id: 1,
      type: 'room',
      title: 'Promena sobe',
      description: 'Premještaj iz sobe 201 u sobu 204',
      date: '2024-09-15',
      author: 'Admin sistem'
    },
    {
      id: 2,
      type: 'care',
      title: 'Promena nivoa nege',
      description: 'Nivo nege povećan sa Niskog na Srednji',
      date: '2024-08-20',
      author: 'Dr. Marković'
    },
    {
      id: 3,
      type: 'nutrition',
      title: 'Promena dijete',
      description: 'Uvedena dijabetička dijeta',
      date: '2024-07-10',
      author: 'Nutricionist Ilić'
    },
    {
      id: 4,
      type: 'incident',
      title: 'Prijavljeni incident',
      description: 'Manji pad u kupatilu - bez povreda',
      date: '2024-05-22',
      author: 'Sestra Ana'
    }
  ]
};

// ===========================================
// Tab Configuration
// ===========================================
// `permissions` = any-of lista; tab je vidljiv ako korisnik ima BILO KOJU od
// navedenih dozvola. Tabovi bez `permissions` su uvek vidljivi.
const TABS = [
  { id: 'daily-plan', label: 'Dnevni plan', icon: Icons.Calendar },
  { id: 'personal', label: 'Lični podaci', icon: Icons.User },
  { id: 'health', label: 'Zdravlje', icon: Icons.Heart, permissions: ['residents.health.manage'] },
  { id: 'documents', label: 'Dokumenti', icon: Icons.FileText, permissions: ['documents.view', 'documents.upload'] },
  { id: 'activities', label: 'Aktivnosti', icon: Icons.Activity, badge: 3 },
  { id: 'nutrition', label: 'Ishrana', icon: Icons.Coffee, permissions: ['residents.diet.manage'] },
  { id: 'relatives', label: 'Rodbina', icon: Icons.Users, permissions: ['residents.family.manage'] },
  { id: 'pocket-money', label: 'Džeparac', icon: Icons.Wallet, permissions: ['budget.transactions.manage'] },
  { id: 'inventory', label: 'Zalihe', icon: Icons.Package, permissions: ['supplies.manage'] }
];

// ===========================================
// Utility Functions
// ===========================================
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('sr-Latn', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(date);
};

const maskNationalId = (id) => {
  if (!id) return '-';
  return id.slice(0, 4) + '••••••' + id.slice(-3);
};

const getStatusConfig = (status) => {
  const configs = {
    active: { label: 'Aktivan', class: 'active', icon: Icons.CheckCircle },
    inactive: { label: 'Neaktivan', class: 'inactive', icon: Icons.AlertTriangle },
    absent: { label: 'Privremeno odsutan', class: 'absent', icon: Icons.AlertTriangle },
    hospitalized: { label: 'Hospitalizovan', class: 'hospitalized', icon: Icons.Hospital }
  };
  return configs[status] || configs.active;
};

const getCareLevelConfig = (level) => {
  const configs = {
    // API format: I, II, III
    'I': { label: 'Nivo I', class: 'low' },
    'II': { label: 'Nivo II', class: 'medium' },
    'III': { label: 'Nivo III', class: 'high' },
    // Legacy format
    low: { label: 'Nizak', class: 'low' },
    medium: { label: 'Srednji', class: 'medium' },
    high: { label: 'Visok', class: 'high' }
  };
  return configs[level] || { label: level || '-', class: 'low' };
};

// ===========================================
// Sub-Components
// ===========================================

// Status Badge Component
const StatusBadge = ({ status, customLabel }) => {
  const config = getStatusConfig(status);
  const IconComponent = config.icon;
  
  return (
    <span className={`residents1-status-badge residents1-status-badge--${config.class}`}>
      <IconComponent />
      {customLabel || config.label}
    </span>
  );
};

// Care Level Tag Component
const CareLevelTag = ({ level }) => {
  const config = getCareLevelConfig(level);
  
  return (
    <span className={`residents1-care-level residents1-care-level--${config.class}`}>
      {config.label}
    </span>
  );
};

// Image Modal Component
const ImageModal = ({ isOpen, onClose, imageSrc, altText }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;
  
  return (
    <div 
      className="rph-image-modal-overlay" 
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Uvećana fotografija"
    >
      <div className="rph-image-modal" onClick={(e) => e.stopPropagation()}>
        <button 
          className="rph-image-modal__close" 
          onClick={onClose}
          aria-label="Zatvori"
        >
          <Icons.X />
        </button>
        <img 
          src={imageSrc} 
          alt={altText}
          className="rph-image-modal__image"
        />
      </div>
    </div>
  );
};

// Edit section configurations
const EDIT_SECTIONS = {
  identity: {
    title: 'Izmeni identitet',
    fields: ['firstName', 'lastName', 'parentName', 'birthDate', 'isWoman', 'primaryLang', 'citizenship', 'placeOfBirth']
  },
  administrative: {
    title: 'Izmeni administrativne podatke',
    fields: ['jmbg', 'careLevel', 'isActive'],
    compact: true
  },
  documents: {
    title: 'Izmeni dokumenta',
    fields: ['jmbg', 'identificationNumber', 'identityCardNumber']
  },
  location: {
    title: 'Izmeni lokaciju',
    fields: ['citizenship', 'placeOfBirth', 'placeOfResidence']
  },
  care: {
    title: 'Izmeni negu',
    fields: ['careLevel', 'roomId', 'isActive']
  },
  photo: {
    title: 'Izmeni fotografiju',
    fields: ['imgSrc']
  }
};

// Edit Sidebar Component - Right side panel for editing resident data (ResidentsTable.js style)
const EditSidebar = ({ isOpen, onClose, resident, onSave, isSaving, editSection = 'identity' }) => {
  const [formData, setFormData] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [isImageUploading, setIsImageUploading] = useState(false);
  
  // Get section config
  const sectionConfig = EDIT_SECTIONS[editSection] || EDIT_SECTIONS.identity;
  
  // Initialize form data when resident changes or sidebar opens
  useEffect(() => {
    if (resident && isOpen) {
      setFormData({
        firstName: resident.firstName || '',
        lastName: resident.lastName || '',
        jmbg: resident.jmbg || resident.nationalId || '',
        isWoman: resident.isWoman ?? (resident.gender === 'Ženski'),
        citizenship: resident.citizenship || '',
        placeOfBirth: resident.placeOfBirth || '',
        placeOfResidence: resident.placeOfResidence || '',
        identificationNumber: resident.identificationNumber || '',
        identityCardNumber: resident.identityCardNumber || '',
        parentName: resident.parentName || '',
        primaryLang: resident.primaryLang || resident.language || '',
        careLevel: resident.careLevel || '',
        roomId: resident.roomId || '',
        birthDate: resident.birthDate ? resident.birthDate.split('T')[0] : (resident.dateOfBirth ? resident.dateOfBirth.split('T')[0] : ''),
        imgSrc: resident.imgSrc || resident.avatar || '',
        isActive: resident.isActive ?? (resident.status === 'active'),
      });
      setImageFile(null);
    }
  }, [resident, isOpen]);

  // Upload a new image (replaces the old one) using the same endpoint as creation
  const uploadNewImage = async () => {
    if (!imageFile || !resident?.id) return null;
    setIsImageUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('residentId', resident.id);
      uploadFormData.append('file', imageFile);

      const uploadResponse = await axiosInstance.post('/api/Resident/upload-image', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (uploadResponse.data?.success && uploadResponse.data?.data?.url) {
        return uploadResponse.data.data.url;
      }
      return null;
    } catch (err) {
      console.error('Greška pri uploadu slike:', err);
      return null;
    } finally {
      setIsImageUploading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    // If a new image was selected, upload it first so it replaces the old one
    let updatedFormData = formData;
    if (imageFile) {
      const newImgSrc = await uploadNewImage();
      if (newImgSrc) {
        updatedFormData = { ...formData, imgSrc: newImgSrc };
      }
    }
    onSave(updatedFormData);
  };
  
  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  // Render field based on field name - using Carbon components
  const renderField = (fieldName) => {
    switch (fieldName) {
      case 'firstName':
        return (
          <TextInput
            key="firstName"
            id="edit-firstName"
            labelText="Ime"
            placeholder="Unesite ime"
            value={formData.firstName || ''}
            onChange={(e) => handleChange('firstName', e.target.value)}
            disabled={isSaving}
          />
        );
      case 'lastName':
        return (
          <TextInput
            key="lastName"
            id="edit-lastName"
            labelText="Prezime"
            placeholder="Unesite prezime"
            value={formData.lastName || ''}
            onChange={(e) => handleChange('lastName', e.target.value)}
            disabled={isSaving}
          />
        );
      case 'parentName':
        return (
          <TextInput
            key="parentName"
            id="edit-parentName"
            labelText="Ime roditelja"
            placeholder="Ime oca/majke"
            value={formData.parentName || ''}
            onChange={(e) => handleChange('parentName', e.target.value)}
            disabled={isSaving}
          />
        );
      case 'birthDate':
        // Format date for display - convert to Date object for NativeDatePicker
        const birthDateValue = formData.birthDate ? new Date(formData.birthDate) : null;
        return (
          <NativeDatePicker
            key="birthDate"
            id="edit-birthDate"
            labelText="Datum rođenja"
            value={birthDateValue}
            onChange={(dates) => {
              if (dates && dates[0]) {
                // Format as YYYY-MM-DD for storage
                const date = dates[0];
                const formatted = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                handleChange('birthDate', formatted);
              } else {
                handleChange('birthDate', '');
              }
            }}
            disabled={isSaving}
          />
        );
      case 'isWoman':
        return (
          <Select
            key="isWoman"
            id="edit-isWoman"
            labelText="Pol"
            value={formData.isWoman ? 'true' : 'false'}
            onChange={(e) => handleChange('isWoman', e.target.value === 'true')}
            disabled={isSaving}
          >
            <SelectItem value="false" text="Muški" />
            <SelectItem value="true" text="Ženski" />
          </Select>
        );
      case 'primaryLang':
        return (
          <TextInput
            key="primaryLang"
            id="edit-primaryLang"
            labelText="Jezik"
            placeholder="Srpski"
            value={formData.primaryLang || ''}
            onChange={(e) => handleChange('primaryLang', e.target.value)}
            disabled={isSaving}
          />
        );
      case 'jmbg':
        return (
          <TextInput
            key="jmbg"
            id="edit-jmbg"
            labelText="JMBG (Matični broj)"
            placeholder="1234567890123"
            value={formData.jmbg || ''}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              if (value.length <= 13) {
                handleChange('jmbg', value);
              }
            }}
            maxLength={13}
            helperText="Unesite 13 cifara"
            disabled={isSaving}
          />
        );
      case 'identificationNumber':
        return (
          <TextInput
            key="identificationNumber"
            id="edit-identificationNumber"
            labelText="Identifikacioni broj"
            placeholder="Matični broj"
            value={formData.identificationNumber || ''}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              handleChange('identificationNumber', value);
            }}
            helperText="Samo brojevi"
            disabled={isSaving}
          />
        );
      case 'identityCardNumber':
        return (
          <TextInput
            key="identityCardNumber"
            id="edit-identityCardNumber"
            labelText="Broj lične karte"
            placeholder="Broj lične karte"
            value={formData.identityCardNumber || ''}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              handleChange('identityCardNumber', value);
            }}
            helperText="Samo brojevi"
            disabled={isSaving}
          />
        );
      case 'citizenship':
        return (
          <TextInput
            key="citizenship"
            id="edit-citizenship"
            labelText="Državljanstvo"
            placeholder="Srbija"
            value={formData.citizenship || ''}
            onChange={(e) => handleChange('citizenship', e.target.value)}
            disabled={isSaving}
          />
        );
      case 'placeOfBirth':
        return (
          <TextInput
            key="placeOfBirth"
            id="edit-placeOfBirth"
            labelText="Mesto rođenja"
            placeholder="Beograd"
            value={formData.placeOfBirth || ''}
            onChange={(e) => handleChange('placeOfBirth', e.target.value)}
            disabled={isSaving}
          />
        );
      case 'placeOfResidence':
        return (
          <TextInput
            key="placeOfResidence"
            id="edit-placeOfResidence"
            labelText="Mesto prebivališta"
            placeholder="Beograd, Srbija"
            value={formData.placeOfResidence || ''}
            onChange={(e) => handleChange('placeOfResidence', e.target.value)}
            disabled={isSaving}
          />
        );
      case 'careLevel':
        return (
          <Select
            key="careLevel"
            id="edit-careLevel"
            labelText="Nivo nege"
            value={formData.careLevel || ''}
            onChange={(e) => handleChange('careLevel', e.target.value)}
            disabled={isSaving}
          >
            <SelectItem value="" text="Izaberite..." />
            <SelectItem value="I" text="Nivo I" />
            <SelectItem value="II" text="Nivo II" />
            <SelectItem value="III" text="Nivo III" />
          </Select>
        );
      case 'roomId':
        return (
          <TextInput
            key="roomId"
            id="edit-roomId"
            labelText="ID Sobe"
            placeholder="Broj sobe"
            value={formData.roomId || ''}
            onChange={(e) => handleChange('roomId', e.target.value)}
            disabled={isSaving}
          />
        );
      case 'isActive':
        return (
          <Select
            key="isActive"
            id="edit-isActive"
            labelText="Status"
            value={formData.isActive ? 'true' : 'false'}
            onChange={(e) => handleChange('isActive', e.target.value === 'true')}
            disabled={isSaving}
          >
            <SelectItem value="true" text="Aktivan" />
            <SelectItem value="false" text="Neaktivan" />
          </Select>
        );
      case 'imgSrc':
        return (
          <TextInput
            key="imgSrc"
            id="edit-imgSrc"
            labelText="URL fotografije (opciono)"
            placeholder="https://example.com/image.jpg"
            value={formData.imgSrc || ''}
            onChange={(e) => handleChange('imgSrc', e.target.value)}
            disabled={isSaving}
          />
        );
      default:
        return null;
    }
  };
  
  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div className="edit-sidebar-overlay" onClick={onClose} />
      )}
      
      {/* Sidebar Panel */}
      <div className={`edit-sidebar ${isOpen ? 'open' : ''} ${sectionConfig.compact ? 'edit-sidebar--compact' : ''}`}>
        <div className="edit-sidebar__header">
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
            {sectionConfig.title}
          </h3>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Close}
            iconDescription="Zatvori"
            hasIconOnly
            onClick={onClose}
          />
        </div>

        <div className="edit-sidebar__content">
          <form className={`edit-sidebar__form ${sectionConfig.compact ? 'edit-sidebar__form--compact' : ''}`} onSubmit={handleSubmit}>
            {sectionConfig.fields.map(fieldName => renderField(fieldName))}

            {/* Upload new image - replaces the old one */}
            <div style={{ marginTop: '1rem' }}>
              <label style={{
                display: 'block',
                fontSize: '0.75rem',
                fontWeight: '500',
                marginBottom: '0.5rem',
                color: 'var(--cds-text-secondary)'
              }}>
                Nova fotografija (zamenjuje staru)
              </label>
              <FileUploader
                labelTitle=""
                labelDescription="Samo JPG, PNG, WEBP, GIF, BMP (maks 5MB)"
                buttonLabel="Izaberi sliku"
                filenameStatus="edit"
                accept={['.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp']}
                multiple={false}
                disabled={isSaving || isImageUploading}
                onChange={(e) => setImageFile(e.target.files[0])}
              />
              {isImageUploading && (
                <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginTop: '0.5rem' }}>
                  Upload slike u toku...
                </p>
              )}
            </div>
            
            <div className="edit-sidebar__actions">
              <Button
                kind="secondary"
                onClick={onClose}
                disabled={isSaving}
              >
                Otkaži
              </Button>
              <Button
                kind="primary"
                type="submit"
                disabled={isSaving}
              >
                {isSaving ? 'Čuvanje...' : 'Sačuvaj'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

// Note Sidebar Component - Right side panel for adding a resident note
const NoteSidebar = ({ isOpen, onClose, resident, setNotification, onSuccess }) => {
  const [text, setText] = useState('');
  const [severity, setSeverity] = useState(2);
  const [isSaving, setIsSaving] = useState(false);
  
  // Reset form when sidebar opens
  useEffect(() => {
    if (isOpen) {
      setText('');
      setSeverity(2);
    }
  }, [isOpen]);
  
  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const trimmed = text.trim();
    if (!trimmed) {
      setNotification({
        kind: 'warning',
        title: 'Obavezno polje',
        subtitle: 'Unesite tekst beleške pre nego što je sačuvate.'
      });
      setTimeout(() => setNotification(null), 3000);
      return;
    }
    
    if (!resident?.id) {
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle: 'Nedostaju podaci o stanaru'
      });
      setTimeout(() => setNotification(null), 4000);
      return;
    }
    
    setIsSaving(true);
    try {
      const response = await axiosInstance.post('/api/ResidentNote', {
        residentId: resident.id,
        text: trimmed,
        severity
      });
      
      if (response.data.success) {
        if (onSuccess) onSuccess(response.data.data);
        setNotification({
          kind: 'success',
          title: 'Uspešno dodato',
          subtitle: 'Beleška je uspešno dodata'
        });
        setTimeout(() => setNotification(null), 3000);
        onClose();
      } else {
        setNotification({
          kind: 'error',
          title: 'Greška',
          subtitle: response.data.message || 'Došlo je do greške pri dodavanju beleške'
        });
        setTimeout(() => setNotification(null), 4000);
      }
    } catch (err) {
      console.error('Greška pri dodavanju beleške:', err);
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle: err.response?.data?.message || 'Došlo je do greške pri dodavanju beleške'
      });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div className="edit-sidebar-overlay" onClick={onClose} />
      )}
      
      {/* Sidebar Panel */}
      <div className={`edit-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="edit-sidebar__header">
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
            Dodaj belešku
          </h3>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Close}
            iconDescription="Zatvori"
            hasIconOnly
            onClick={onClose}
          />
        </div>

        <div className="edit-sidebar__content">
          <form className="edit-sidebar__form" onSubmit={handleSubmit}>
            <TextArea
              id="note-text"
              labelText="Tekst beleške"
              placeholder="Unesite belešku o stanaru..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={isSaving}
              rows={6}
              maxLength={100}
            />
            
            <div className="residents1-note-severity">
              <Tag
                type={severity === 1 ? 'red' : 'cool-gray'}
                size="md"
                onClick={() => setSeverity(severity === 1 ? 2 : 1)}
                className={`residents1-note-severity__tag ${severity === 1 ? 'residents1-note-severity__tag--active' : ''}`}
                title="Označi kao važno"
              >
                Važno
              </Tag>
            </div>
            
            <div className="edit-sidebar__actions">
              <Button
                kind="secondary"
                onClick={onClose}
                disabled={isSaving}
              >
                Otkaži
              </Button>
              <Button
                kind="primary"
                type="submit"
                disabled={isSaving}
              >
                {isSaving ? 'Čuvanje...' : 'Dodaj'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

// Transfer Sidebar Component - Right side panel for transferring resident to another room
const TransferSidebar = ({ isOpen, onClose, resident, setNotification, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [stepTransition, setStepTransition] = useState('entering');
  const [currentRoomData, setCurrentRoomData] = useState(null);
  const [loadingRoom, setLoadingRoom] = useState(false);
  const [roomError, setRoomError] = useState(null);
  const [isTransferring, setIsTransferring] = useState(false);
  
  // Step 2 - Room selection state
  const [availableRooms, setAvailableRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [roomsError, setRoomsError] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [areas, setAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedBedsNumber, setSelectedBedsNumber] = useState('');
  const [onlyAvailable, setOnlyAvailable] = useState(true);

  const steps = [
    { label: 'Trenutna soba', description: 'Podaci o trenutnoj sobi' },
    { label: 'Nova soba', description: 'Izaberite novu sobu' },
    { label: 'Potvrda', description: 'Potvrdite premestaj' }
  ];

  // Fetch areas on mount
  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const response = await axiosInstance.get('/api/Room/areas');
        if (response.data.success && Array.isArray(response.data.data)) {
          setAreas(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching areas:', error);
      }
    };
    fetchAreas();
  }, []);

  // Fetch current room data when sidebar opens
  useEffect(() => {
    const fetchRoomData = async () => {
      if (isOpen && resident?.roomId) {
        setLoadingRoom(true);
        setRoomError(null);
        try {
          const response = await axiosInstance.get(`/api/Room/${resident.roomId}`);
          if (response.data.success) {
            setCurrentRoomData(response.data.data);
          }
        } catch (err) {
          console.error('Greška pri učitavanju podataka o sobi:', err);
          setRoomError('Nije moguće učitati podatke o sobi');
        } finally {
          setLoadingRoom(false);
        }
      }
    };

    if (isOpen) {
      setCurrentStep(0);
      setSelectedRoom(null);
      setSearchTerm('');
      setSelectedArea('');
      setSelectedBedsNumber('');
      setOnlyAvailable(true);
      fetchRoomData();
    }
  }, [isOpen, resident?.roomId]);

  // Fetch available rooms when on step 2 or when filters change
  useEffect(() => {
    const fetchAvailableRooms = async () => {
      if (currentStep !== 1) return;
      
      setLoadingRooms(true);
      setRoomsError(null);
      try {
        const params = new URLSearchParams();
        params.append('pageNumber', '1');
        params.append('pageSize', '50');
        
        if (searchTerm) {
          params.append('search', searchTerm);
        }
        if (selectedArea) {
          params.append('area', selectedArea);
        }
        if (selectedBedsNumber) {
          params.append('bedsNumber', selectedBedsNumber);
        }
        if (onlyAvailable) {
          params.append('onlyAvailable', 'true');
        }
        
        const response = await axiosInstance.get(`/api/Room?${params.toString()}`);
        if (response.data.success && Array.isArray(response.data.data)) {
          // Filter out current room
          const filteredRooms = response.data.data.filter(room => room.id !== resident?.roomId);
          setAvailableRooms(filteredRooms);
        }
      } catch (err) {
        console.error('Greška pri učitavanju soba:', err);
        setRoomsError('Nije moguće učitati dostupne sobe');
      } finally {
        setLoadingRooms(false);
      }
    };

    fetchAvailableRooms();
  }, [currentStep, searchTerm, selectedArea, selectedBedsNumber, onlyAvailable, resident?.roomId]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleStepClick = (step) => {
    if (step <= currentStep) {
      setStepTransition('exiting');
      setTimeout(() => {
        setCurrentStep(step);
        setStepTransition('entering');
      }, 150);
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setStepTransition('exiting');
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
        setStepTransition('entering');
      }, 150);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setStepTransition('exiting');
      setTimeout(() => {
        setCurrentStep(currentStep - 1);
        setStepTransition('entering');
      }, 150);
    }
  };

  // Handle confirm transfer
  const handleConfirmTransfer = async () => {
    if (!resident?.id || !selectedRoom?.id) {
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle: 'Nedostaju potrebni podaci za premeštaj'
      });
      setTimeout(() => setNotification(null), 4000);
      return;
    }

    setIsTransferring(true);
    try {
      const response = await axiosInstance.post('/api/Room/move', {
        residentId: resident.id,
        targetRoomId: selectedRoom.id
      });

      if (response.data.success) {
        setNotification({
          kind: 'success',
          title: 'Uspešno premešteno',
          subtitle: `Stanar je uspešno premešten u sobu ${selectedRoom.name}`
        });
        setTimeout(() => setNotification(null), 3000);
        onClose();
        // Refresh resident data in place so the notification stays visible
        if (onSuccess) onSuccess();
      } else {
        setNotification({
          kind: 'error',
          title: 'Greška',
          subtitle: response.data.message || 'Došlo je do greške pri premeštanju stanara'
        });
        setTimeout(() => setNotification(null), 4000);
      }
    } catch (err) {
      console.error('Greška pri premeštanju stanara:', err);
      setNotification({
        kind: 'error',
        title: isConcurrencyConflictError(err) ? CONCURRENCY_CONFLICT_TITLE : 'Greška',
        subtitle: getApiErrorMessage(err, 'Došlo je do greške pri premeštanju stanara')
      });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsTransferring(false);
    }
  };

  // Render Step 1 - Current Room Info
  const renderCurrentRoomStep = () => {
    if (loadingRoom) {
      return (
        <div className="transfer-sidebar__loading">
          <p>Učitavanje podataka o sobi...</p>
        </div>
      );
    }

    if (roomError) {
      return (
        <div className="transfer-sidebar__error">
          <Icons.AlertTriangle />
          <p>{roomError}</p>
        </div>
      );
    }

    if (!currentRoomData) {
      return (
        <div className="transfer-sidebar__empty">
          <Icons.Home />
          <p>Korisnik nije dodeljen nijednoj sobi</p>
        </div>
      );
    }

    const occupancyPercentage = currentRoomData.bedsNumber > 0 
      ? Math.round((currentRoomData.residents?.length || 0) / currentRoomData.bedsNumber * 100)
      : 0;

    return (
      <div className="transfer-sidebar__room-info">
        {/* Room Header */}
        <div className="transfer-sidebar__room-header">
          <div className="transfer-sidebar__room-icon">
            <Icons.Home />
          </div>
          <div className="transfer-sidebar__room-title">
            <h4>Soba {currentRoomData.name}</h4>
            <span className="transfer-sidebar__room-area">{currentRoomData.areaName || 'Nepoznata zona'}</span>
          </div>
        </div>

        {/* Room Stats */}
        <div className="transfer-sidebar__room-stats">
          <div className="transfer-sidebar__stat">
            <span className="transfer-sidebar__stat-label">Kapacitet</span>
            <span className="transfer-sidebar__stat-value">{currentRoomData.bedsNumber} krevet(a)</span>
          </div>
          <div className="transfer-sidebar__stat">
            <span className="transfer-sidebar__stat-label">Popunjenost</span>
            <span className="transfer-sidebar__stat-value">
              {currentRoomData.residents?.length || 0} / {currentRoomData.bedsNumber}
              <span className="transfer-sidebar__stat-percentage">({occupancyPercentage}%)</span>
            </span>
          </div>
          <div className="transfer-sidebar__stat">
            <span className="transfer-sidebar__stat-label">Slobodnih mesta</span>
            <span className="transfer-sidebar__stat-value">
              {currentRoomData.bedsNumber - (currentRoomData.residents?.length || 0)}
            </span>
          </div>
          <div className="transfer-sidebar__stat">
            <span className="transfer-sidebar__stat-label">Stepen nege</span>
            <span className="transfer-sidebar__stat-value">{currentRoomData.careLevel || '-'}</span>
          </div>
        </div>

        {/* Occupancy Bar */}
        <div className="transfer-sidebar__occupancy">
          <div className="transfer-sidebar__occupancy-bar">
            <div 
              className="transfer-sidebar__occupancy-fill"
              style={{ width: `${occupancyPercentage}%` }}
            />
          </div>
        </div>

        {/* Roommates Section */}
        {currentRoomData.residents && currentRoomData.residents.length > 0 && (
          <div className="transfer-sidebar__roommates">
            <h5 className="transfer-sidebar__roommates-title">
              <Icons.Users />
              Stanari sobe ({currentRoomData.residents.length})
            </h5>
            <div className="transfer-sidebar__roommates-list">
              {currentRoomData.residents.map((roommate) => (
                <Link 
                  key={roommate.id}
                  to={`/residents/${roommate.id}`}
                  className={`transfer-sidebar__roommate ${roommate.id === resident?.id ? 'transfer-sidebar__roommate--current' : ''}`}
                  onClick={roommate.id !== resident?.id ? onClose : (e) => e.preventDefault()}
                >
                  <div className="transfer-sidebar__roommate-avatar">
                    {roommate.imgSrc ? (
                      <img src={roommate.imgSrc} alt={roommate.fullName} />
                    ) : (
                      <Icons.User />
                    )}
                  </div>
                  <div className="transfer-sidebar__roommate-info">
                    <span className="transfer-sidebar__roommate-name">
                      {roommate.fullName}
                      {roommate.id === resident?.id && (
                        <span className="transfer-sidebar__roommate-badge">Vi</span>
                      )}
                    </span>
                    <span className="transfer-sidebar__roommate-age">{roommate.age} godina</span>
                  </div>
                  <div className={`transfer-sidebar__roommate-status ${roommate.isActive ? 'active' : 'inactive'}`}>
                    {roommate.isActive ? 'Aktivan' : 'Neaktivan'}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Step 3 - Confirmation
  const renderConfirmationStep = () => {
    const freeBeds = selectedRoom ? selectedRoom.bedsNumber - selectedRoom.takenBeds : 0;
    
    return (
      <div className="transfer-sidebar__confirmation">
        <div className="transfer-sidebar__confirmation-header">
          <h4 className="transfer-sidebar__confirmation-title">Potvrdite premeštaj</h4>
          <p className="transfer-sidebar__confirmation-subtitle">
            Da li ste sigurni da želite da premestite stanara u novu sobu?
          </p>
        </div>

        <div className="transfer-sidebar__confirmation-summary">
          {/* Current Room */}
          <div className="transfer-sidebar__summary-card from">
            <div className="transfer-sidebar__summary-label">
              <Icons.ArrowLeft />
              <span>Iz sobe</span>
            </div>
            <div className="transfer-sidebar__summary-room">
              <div className="transfer-sidebar__summary-room-name">
                <Icons.Home />
                <span>Soba {currentRoomData?.name || '-'}</span>
              </div>
              <div className="transfer-sidebar__summary-room-details">
                <span>{currentRoomData?.areaName || '-'}</span>
                <span>•</span>
                <span>{currentRoomData?.bedsNumber} kreveta</span>
              </div>
            </div>
          </div>

          {/* Arrow */}
          <div className="transfer-sidebar__summary-arrow">
            <Icons.ArrowDown />
          </div>

          {/* New Room */}
          <div className="transfer-sidebar__summary-card to">
            <div className="transfer-sidebar__summary-label">
              <Icons.ArrowRight />
              <span>U sobu</span>
            </div>
            <div className="transfer-sidebar__summary-room">
              <div className="transfer-sidebar__summary-room-name">
                <Icons.Home />
                <span>Soba {selectedRoom?.name || '-'}</span>
              </div>
              <div className="transfer-sidebar__summary-room-details">
                <span>{selectedRoom?.areaName || '-'}</span>
                <span>•</span>
                <span>{freeBeds} slobodnih kreveta</span>
              </div>
            </div>
          </div>
        </div>

        <div className="transfer-sidebar__confirmation-warning">
          <Icons.AlertTriangle />
          <p>Ova akcija će trajno izmeniti raspored smeštaja stanara. Proverite da li su podaci ispravni pre potvrde.</p>
        </div>
      </div>
    );
  };

  // Render step content based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderCurrentRoomStep();
      case 1:
        return renderSelectRoomStep();
      case 2:
        return renderConfirmationStep();
      default:
        return null;
    }
  };

  // Render Step 2 - Select New Room
  const renderSelectRoomStep = () => {
    return (
      <div className="transfer-sidebar__select-room">
        {/* Search and Filters */}
        <div className="transfer-sidebar__filters">
          <TextInput
            id="room-search"
            placeholder="Pretražite sobe..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="sm"
            labelText=""
          />
          
          <div className="transfer-sidebar__filter-row">
            <Select
              id="area-filter"
              labelText=""
              value={selectedArea}
              onChange={(e) => setSelectedArea(e.target.value)}
              size="sm"
            >
              <SelectItem value="" text="Sve oblasti" />
              {areas.map((area) => (
                <SelectItem key={area.id} value={area.id} text={area.name} />
              ))}
            </Select>
            
            <Select
              id="beds-filter"
              labelText=""
              value={selectedBedsNumber}
              onChange={(e) => setSelectedBedsNumber(e.target.value)}
              size="sm"
            >
              <SelectItem value="" text="Svi kreveti" />
              {[...Array(10)].map((_, i) => (
                <SelectItem key={i + 1} value={String(i + 1)} text={`${i + 1} ${i === 0 ? 'krevet' : i < 4 ? 'kreveta' : 'kreveta'}`} />
              ))}
            </Select>
          </div>
          
          <div className="transfer-sidebar__filter-row">
            <Select
              id="availability-filter"
              labelText=""
              value={onlyAvailable ? 'available' : 'all'}
              onChange={(e) => setOnlyAvailable(e.target.value === 'available')}
              size="sm"
            >
              <SelectItem value="all" text="Sve sobe" />
              <SelectItem value="available" text="Samo slobodne" />
            </Select>
          </div>
        </div>

        {/* Rooms List */}
        <div className="transfer-sidebar__rooms-list">
          {loadingRooms ? (
            <div className="transfer-sidebar__loading">
              <p>Učitavanje soba...</p>
            </div>
          ) : roomsError ? (
            <div className="transfer-sidebar__error">
              <Icons.AlertTriangle />
              <p>{roomsError}</p>
            </div>
          ) : availableRooms.length === 0 ? (
            <div className="transfer-sidebar__empty">
              <Icons.Home />
              <p>Nema dostupnih soba za odabir</p>
            </div>
          ) : (
            availableRooms.map((room) => {
              const isFull = room.takenBeds === room.bedsNumber;
              const freeBeds = room.bedsNumber - room.takenBeds;
              const isSelected = selectedRoom?.id === room.id;
              
              return (
                <div
                  key={room.id}
                  className={`transfer-sidebar__room-card ${isSelected ? 'selected' : ''} ${isFull ? 'full' : ''}`}
                  onClick={() => !isFull && setSelectedRoom(isSelected ? null : room)}
                >
                  <div className="transfer-sidebar__room-card-header">
                    <div className="transfer-sidebar__room-card-name">
                      <Icons.Home />
                      <span>Soba {room.name}</span>
                    </div>
                    <span className={`transfer-sidebar__room-card-status ${isFull ? 'full' : 'available'}`}>
                      {isFull ? 'Zauzeta' : `${freeBeds} slobodno`}
                    </span>
                  </div>
                  
                  <div className="transfer-sidebar__room-card-details">
                    <div className="transfer-sidebar__room-card-detail">
                      <span className="label">Oblast:</span>
                      <span className="value">{room.areaName || '-'}</span>
                    </div>
                    <div className="transfer-sidebar__room-card-detail">
                      <span className="label">Kreveta:</span>
                      <span className="value">{room.takenBeds}/{room.bedsNumber}</span>
                    </div>
                    <div className="transfer-sidebar__room-card-detail">
                      <span className="label">Nivo nege:</span>
                      <span className="value">{room.careLevel || '-'}</span>
                    </div>
                  </div>
                  
                  {isSelected && (
                    <div className="transfer-sidebar__room-card-selected">
                      <Icons.CheckCircle />
                      <span>Odabrano</span>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 0: return 'Trenutna soba';
      case 1: return 'Nova soba';
      case 2: return 'Potvrda';
      default: return '';
    }
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && <div className="side-panel-overlay transfer-overlay" onClick={onClose} />}
      
      {/* Sidebar */}
      <div className={`resident-side-panel transfer-sidebar ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="side-panel-header" style={{ borderBottom: 'none', padding: '1rem 1.5rem 0.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
            {getStepTitle()}
          </h3>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Close}
            iconDescription="Zatvori"
            hasIconOnly
            onClick={onClose}
          />
        </div>

        {/* Progress Indicator */}
        <div style={{ 
          padding: '1rem 1.5rem 0.75rem', 
          borderBottom: '1px solid var(--cds-border-subtle)',
          backgroundColor: 'var(--cds-layer-01)'
        }}>
          <div className="custom-progress-bar">
            <div className="progress-steps">
              {[0, 1, 2].map((step) => (
                <React.Fragment key={step}>
                  <div 
                    className={`progress-step ${
                      currentStep >= step ? 'completed' : ''
                    } ${currentStep === step ? 'active' : ''}`}
                    onClick={() => handleStepClick(step)}
                    style={{ cursor: step <= currentStep ? 'pointer' : 'default' }}
                  >
                    <div className="step-circle">
                      {currentStep > step ? <span style={{ fontSize: '16px' }}>✓</span> : step + 1}
                    </div>
                  </div>
                  {step < 2 && (
                    <div className={`progress-line ${currentStep > step ? 'completed' : ''}`}></div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="side-panel-content">
          <div className={`step-content ${stepTransition}`}>
            {renderStepContent()}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="form-actions" style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--cds-border-subtle)' }}>
          <Button
            kind="secondary"
            onClick={currentStep === 0 ? onClose : prevStep}
          >
            {currentStep === 0 ? 'Otkaži' : 'Nazad'}
          </Button>
          <Button
            kind="primary"
            onClick={currentStep === steps.length - 1 ? handleConfirmTransfer : nextStep}
            disabled={(currentStep === 1 && !selectedRoom) || isTransferring}
          >
            {isTransferring ? 'Premeštanje...' : (currentStep === steps.length - 1 ? 'Potvrdi premeštaj' : 'Dalje')}
          </Button>
        </div>
      </div>
    </>
  );
};

// ===========================================
// Add To Group Sidebar Component
// ===========================================
const AddToGroupSidebar = ({ isOpen, onClose, resident, setNotification, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [stepTransition, setStepTransition] = useState('entering');

  // Step 1 - Group selection state
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(5);
  const [pagination, setPagination] = useState({
    totalCount: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Step 2 - Confirmation state
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  const steps = [
    { label: 'Izbor grupe', description: 'Izaberite grupu' },
    { label: 'Potvrda', description: 'Potvrdite dodavanje' }
  ];

  // Fetch groups when sidebar opens or page changes
  useEffect(() => {
    if (isOpen) {
      fetchGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, page]);

  const fetchGroups = async () => {
    if (!resident?.id) return;
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('pageNumber', String(page));
      params.append('pageSize', String(pageSize));

      const response = await axiosInstance.get(`/api/Group/resident/${resident.id}/not-in-group?${params.toString()}`);
      console.log('API Response:', response);

      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data && typeof response.data === 'object') {
        if (Array.isArray(response.data.data)) {
          data = response.data.data;
        } else if (Array.isArray(response.data.$values)) {
          data = response.data.$values;
        } else if (Array.isArray(response.data.value)) {
          data = response.data.value;
        } else if (Array.isArray(response.data.items)) {
          data = response.data.items;
        }
      }
      setGroups(data);

      // Pagination metadata
      const pagination = response.data?.pagination;
      if (pagination) {
        setPagination({
          totalCount: pagination.totalCount || 0,
          totalPages: pagination.totalPages || 1,
          hasNextPage: pagination.hasNextPage || false,
          hasPreviousPage: pagination.hasPreviousPage || false
        });
      } else {
        setPagination({
          totalCount: data.length,
          totalPages: Math.ceil(data.length / pageSize) || 1,
          hasNextPage: page < Math.ceil(data.length / pageSize),
          hasPreviousPage: page > 1
        });
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      setError('Greška pri učitavanju grupa');
    } finally {
      setLoading(false);
    }
  };

  const nextPage = () => {
    if (pagination.hasNextPage) {
      setPage(prev => prev + 1);
    }
  };

  const prevPage = () => {
    if (pagination.hasPreviousPage) {
      setPage(prev => prev - 1);
    }
  };

  const goToStep = (step) => {
    setStepTransition('exiting');
    setTimeout(() => {
      setCurrentStep(step);
      setStepTransition('entering');
    }, 150);
  };

  const handleSelectGroup = (group) => {
    setSelectedGroup(group);
    goToStep(1);
  };

  const handleConfirmAdd = async () => {
    if (!selectedGroup || !resident?.id) return;

    setIsAdding(true);
    setAddError(null);

    try {
      const payload = {
        residentId: resident.id
      };
      await axiosInstance.post(`/api/Group/${selectedGroup.id}/residents`, payload);

      setNotification({
        kind: 'success',
        title: 'Uspešno!',
        subtitle: `Korisnik je uspešno dodat u grupu "${selectedGroup.name}"`
      });

      onClose();

      // Refresh the page after 3 seconds so the notification is visible
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (error) {
      console.error('Error adding resident to group:', error);
      setAddError(error.response?.data?.message || 'Greška pri dodavanju korisnika u grupu');
    } finally {
      setIsAdding(false);
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    setSelectedGroup(null);
    setAddError(null);
    setPage(1);
    onClose();
  };

  const getStepTitle = () => {
    if (currentStep === 0) return 'Dodaj u grupu';
    return 'Potvrda dodavanja';
  };

  return (
    <>
      <div className={`resident-side-panel add-to-group-sidebar ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="side-panel-header" style={{ borderBottom: 'none', padding: '1rem 1.5rem 0.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
            {getStepTitle()}
          </h3>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Close}
            iconDescription="Zatvori"
            hasIconOnly
            onClick={handleClose}
          />
        </div>

        {/* Progress Indicator */}
        <div style={{
          padding: '1rem 1.5rem 0.75rem',
          borderBottom: '1px solid var(--cds-border-subtle)',
          backgroundColor: 'var(--cds-layer-01)'
        }}>
          <div className="custom-progress-bar">
            <div className="progress-steps">
              {[0, 1].map((step) => (
                <React.Fragment key={step}>
                  <div
                    className={`progress-step ${
                      currentStep >= step ? 'completed' : ''
                    } ${currentStep === step ? 'active' : ''}`}
                  >
                    <div className="step-circle">
                      {currentStep > step ? <span style={{ fontSize: '16px' }}>✓</span> : step + 1}
                    </div>
                  </div>
                  {step < 1 && (
                    <div className={`progress-line ${currentStep > step ? 'completed' : ''}`}></div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="side-panel-content">
          <div className={`step-content ${stepTransition}`}>
            {/* Step 0: Select group */}
            {currentStep === 0 && (
              <>
                <div className="add-to-group-step-title">
                  <h4>Izaberite grupu</h4>
                  <div className="add-to-group-pagination">
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={ChevronLeft}
                      onClick={prevPage}
                      hasIconOnly
                      disabled={!pagination.hasPreviousPage}
                      iconDescription="Prethodna stranica"
                    />
                    <span className="add-to-group-page">
                      {page}/{pagination.totalPages}
                    </span>
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={ChevronRight}
                      onClick={nextPage}
                      hasIconOnly
                      disabled={!pagination.hasNextPage}
                      iconDescription="Sledeća stranica"
                    />
                  </div>
                </div>

                {error && (
                  <InlineNotification
                    kind="error"
                    title="Greška"
                    subtitle={error}
                    onCloseButtonClick={() => setError(null)}
                    hideCloseButton={false}
                    lowContrast
                  />
                )}

                {loading && (
                  <div className="add-to-group-loading">
                    <Loading description="Učitavanje grupa..." withOverlay={false} small />
                  </div>
                )}

                {!loading && !error && groups.length > 0 && (
                  <div className="add-to-group-list">
                    {groups.map((group) => (
                      <div
                        key={group.id}
                        className="add-to-group-item"
                        onClick={() => handleSelectGroup(group)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSelectGroup(group);
                          }
                        }}
                      >
                        <div className="add-to-group-item-icon">
                          <Group size={20} />
                        </div>
                        <div className="add-to-group-item-info">
                          <span className="add-to-group-item-name">{group.name}</span>
                          {group.description && (
                            <span className="add-to-group-item-desc">{group.description}</span>
                          )}
                        </div>
                        {group.isDefault && (
                          <Tag type="blue" size="sm">
                            Podrazumevana
                          </Tag>
                        )}
                        <ArrowRight size={16} className="add-to-group-item-arrow" />
                      </div>
                    ))}
                  </div>
                )}

                {!loading && !error && groups.length === 0 && (
                  <div className="add-to-group-empty">
                    <Group size={24} />
                    <span>Nema dostupnih grupa</span>
                  </div>
                )}
              </>
            )}

            {/* Step 1: Confirm */}
            {currentStep === 1 && (
              <>
                {addError && (
                  <InlineNotification
                    kind="error"
                    title="Greška"
                    subtitle={addError}
                    onCloseButtonClick={() => setAddError(null)}
                    hideCloseButton={false}
                    lowContrast
                  />
                )}

                <div className="add-to-group-confirm">
                  <h4>Potvrda dodavanja</h4>
                  <p>
                    Da li ste sigurni da želite da dodate korisnika{' '}
                    <strong>{resident?.fullName || `${resident?.firstName || ''} ${resident?.lastName || ''}`}</strong>{' '}
                    u grupu <strong>{selectedGroup?.name}</strong>?
                  </p>

                  {selectedGroup && (
                    <div className="add-to-group-confirm-group">
                      <div className="add-to-group-item-icon">
                        <Group size={20} />
                      </div>
                      <div className="add-to-group-item-info">
                        <span className="add-to-group-item-name">{selectedGroup.name}</span>
                        {selectedGroup.description && (
                          <span className="add-to-group-item-desc">{selectedGroup.description}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="form-actions" style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--cds-border-subtle)' }}>
          <Button
            kind="secondary"
            onClick={currentStep === 0 ? handleClose : () => goToStep(0)}
          >
            {currentStep === 0 ? 'Otkaži' : 'Nazad'}
          </Button>
          {currentStep === 1 && (
            <Button
              kind="primary"
              renderIcon={CheckmarkFilled}
              onClick={handleConfirmAdd}
              disabled={isAdding}
            >
              {isAdding ? 'Dodavanje...' : 'Potvrdi'}
            </Button>
          )}
        </div>
      </div>

      {/* Overlay */}
      {isOpen && (
        <div
          className="transfer-overlay"
          onClick={handleClose}
        />
      )}
    </>
  );
};

// ===========================================
// Deactivate Sidebar Component
// (Right side panel for deactivating a resident)
// ===========================================
const DeactivateSidebar = ({ isOpen, onClose, resident, setNotification, onSuccess }) => {
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0); // 0 = details, 1 = confirmation summary

  // Reset form when sidebar opens
  useEffect(() => {
    if (isOpen) {
      setDescription('');
      setError(null);
      setStep(0);
    }
  }, [isOpen]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!resident?.id) {
      setError('Nedostaju podaci o stanaru');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const payload = {};
      const trimmed = description.trim();
      if (trimmed) {
        payload.description = trimmed;
      }

      await axiosInstance.post(`/api/Resident/${resident.id}/deactivate`, payload);

      setNotification({
        kind: 'success',
        title: 'Uspešno deaktiviran',
        subtitle: 'Stanar je uspešno deaktiviran'
      });
      setTimeout(() => setNotification(null), 4000);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Greška pri deaktivaciji stanara:', err);
      setError(
        isConcurrencyConflictError(err)
          ? CONCURRENCY_CONFLICT_MESSAGE
          : getApiErrorMessage(err, 'Došlo je do greške pri deaktivaciji stanara')
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div className="edit-sidebar-overlay" onClick={onClose} />
      )}

      {/* Sidebar Panel */}
      <div className={`edit-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="edit-sidebar__header">
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
            {step === 0 ? 'Deaktivacija stanara' : 'Potvrda deaktivacije'}
          </h3>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Close}
            iconDescription="Zatvori"
            hasIconOnly
            onClick={onClose}
          />
        </div>

        <div className="edit-sidebar__content">
          {step === 0 ? (
            <form className="edit-sidebar__form" onSubmit={(e) => { e.preventDefault(); setStep(1); }}>
              <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: 'var(--cds-text-secondary, #525252)' }}>
                Da li ste sigurni da želite da deaktivirate stanara{' '}
                <strong style={{ color: 'var(--cds-text-primary, #161616)' }}>{resident?.fullName}</strong>?
                Deaktiviran stanar više neće biti aktivan u sistemu.
              </p>

              <TextArea
                id="deactivate-description"
                labelText="Razlog deaktivacije (opciono)"
                placeholder="Unesite razlog deaktivacije..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isSaving}
                rows={4}
              />

              {error && (
                <div style={{ color: '#da1e28', fontSize: '0.8125rem', padding: '0.5rem 0.75rem', backgroundColor: 'rgba(218,30,40,0.08)', borderRadius: '4px', borderLeft: '3px solid #da1e28' }}>
                  {error}
                </div>
              )}

              <div className="edit-sidebar__actions">
                <Button
                  kind="secondary"
                  onClick={onClose}
                  disabled={isSaving}
                >
                  Otkaži
                </Button>
                <Button
                  kind="danger"
                  type="submit"
                  disabled={isSaving}
                >
                  Dalje
                </Button>
              </div>
            </form>
          ) : (
            <form className="edit-sidebar__form" onSubmit={handleSubmit}>
              {/* Warning summary */}
              <div style={{
                padding: '1rem',
                backgroundColor: 'rgba(218, 30, 40, 0.08)',
                border: '1px solid rgba(218, 30, 40, 0.3)',
                borderLeft: '4px solid #da1e28',
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.9375rem', fontWeight: '600', color: '#da1e28' }}>
                  ⚠️ Ova akcija je trajna i ne može se poništiti!
                </p>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', color: 'var(--cds-text-primary, #161616)' }}>
                  Deaktivacijom stanara <strong>{resident?.fullName}</strong>:
                </p>
                <ul style={{ margin: '0 0 0.5rem', paddingLeft: '1.25rem', fontSize: '0.875rem', color: 'var(--cds-text-primary, #161616)' }}>
                  <li>Stanar se <strong>uklanja iz svih grupa</strong></li>
                  <li>Stanar se <strong>uklanja iz svih lista</strong></li>
                  <li>Stanar <strong>više neće biti aktivan</strong> u sistemu</li>
                </ul>
                <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--cds-text-secondary, #525252)' }}>
                  Ova akcija se ne može poništiti.
                </p>
              </div>

              {/* Summary of entered details */}
              <div style={{
                padding: '1rem',
                backgroundColor: 'var(--cds-layer-02, #f4f4f4)',
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--cds-text-primary, #161616)' }}>
                  Pregled deaktivacije
                </p>
                <p style={{ margin: '0 0 0.25rem', fontSize: '0.875rem', color: 'var(--cds-text-primary, #161616)' }}>
                  <strong>Stanar:</strong> {resident?.fullName}
                </p>
                <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--cds-text-primary, #161616)' }}>
                  <strong>Razlog:</strong> {description.trim() || 'Nije naveden'}
                </p>
              </div>

              {error && (
                <div style={{ color: '#da1e28', fontSize: '0.8125rem', padding: '0.5rem 0.75rem', backgroundColor: 'rgba(218,30,40,0.08)', borderRadius: '4px', borderLeft: '3px solid #da1e28' }}>
                  {error}
                </div>
              )}

              <div className="edit-sidebar__actions">
                <Button
                  kind="secondary"
                  onClick={() => setStep(0)}
                  disabled={isSaving}
                >
                  Nazad
                </Button>
                <Button
                  kind="danger"
                  type="submit"
                  disabled={isSaving}
                >
                  {isSaving ? 'Deaktivacija...' : 'Potvrdi deaktivaciju'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

// ===========================================
// Health Record Sidebar Component
// ===========================================
const HealthRecordSidebar = ({ isOpen, onClose, resident, setNotification, onSuccess }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    mobility: 'Mobile',
    selfServiceAbility: 'Capable',
    personalHygieneAbility: 'Independent',
    communicationAbility: 'Preserved',
    usesSpecialAids: false,
    specialAidsDescription: '',
    mentalState: 'MentallyPreserved',
    diseases: '',
    diseasesDescription: ''
  });

  // Enum options (value = English enum member, label = Serbian display)
  const mobilityOptions = [
    { value: 'Mobile', label: 'Pokretan' },
    { value: 'ImpairedMobility', label: 'Otežano pokretan' },
    { value: 'Immobile', label: 'Nepokretan' }
  ];

  const selfServiceOptions = [
    { value: 'Capable', label: 'Sposoban' },
    { value: 'PartiallyCapable', label: 'Delimično sposoban' },
    { value: 'NotCapable', label: 'Nije sposoban' }
  ];

  const personalHygieneOptions = [
    { value: 'Independent', label: 'Samostalan' },
    { value: 'PartiallyIndependent', label: 'Delimično samostalan' },
    { value: 'NotIndependent', label: 'Nije samostalan' }
  ];

  const communicationOptions = [
    { value: 'Preserved', label: 'Očuvana' },
    { value: 'ImpairedCommunication', label: 'Otežana komunikacija' },
    { value: 'CommunicationCannotBeEstablished', label: 'Komunikacija se ne uspostavlja' }
  ];

  const mentalStateOptions = [
    { value: 'MentallyPreserved', label: 'Psihički očuvan' },
    { value: 'MildDementia', label: 'Demencija - blaga' },
    { value: 'ModerateDementia', label: 'Demencija - umerena' },
    { value: 'SevereDementia', label: 'Demencija - teška' },
    { value: 'Psychosis', label: 'Psihoza' },
    { value: 'NeurologicalDisorder', label: 'Neuronski poremećaj' },
    { value: 'PersonalityDisorder', label: 'Poremećaj ličnosti' },
    { value: 'AddictionDisorders', label: 'Bolesti zavisnosti' }
  ];

  // Reset form when sidebar opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        mobility: 'Mobile',
        selfServiceAbility: 'Capable',
        personalHygieneAbility: 'Independent',
        communicationAbility: 'Preserved',
        usesSpecialAids: false,
        specialAidsDescription: '',
        mentalState: 'MentallyPreserved',
        diseases: '',
        diseasesDescription: ''
      });
    }
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!resident?.id) {
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle: 'Nedostaje ID stanara'
      });
      setTimeout(() => setNotification(null), 4000);
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        residentId: resident.id,
        mobility: formData.mobility,
        selfServiceAbility: formData.selfServiceAbility,
        personalHygieneAbility: formData.personalHygieneAbility,
        communicationAbility: formData.communicationAbility,
        usesSpecialAids: formData.usesSpecialAids,
        specialAidsDescription: formData.usesSpecialAids ? formData.specialAidsDescription : '',
        mentalState: formData.mentalState,
        diseases: formData.diseases || '',
        diseasesDescription: formData.diseasesDescription || ''
      };

      const response = await axiosInstance.post('/api/ResidentHealth', payload);

      if (response.data.success) {
        setNotification({
          kind: 'success',
          title: 'Uspešno sačuvano',
          subtitle: 'Zdravstveni karton je uspešno kreiran'
        });
        setTimeout(() => setNotification(null), 3000);
        onClose();
        if (onSuccess) {
          onSuccess(response.data.data);
        }
      } else {
        setNotification({
          kind: 'error',
          title: 'Greška',
          subtitle: response.data.message || 'Došlo je do greške pri kreiranju zdravstvenog kartona'
        });
        setTimeout(() => setNotification(null), 4000);
      }
    } catch (err) {
      console.error('Greška pri kreiranju zdravstvenog kartona:', err);
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle: err.response?.data?.message || 'Došlo je do greške pri kreiranju zdravstvenog kartona'
      });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div className="edit-sidebar-overlay" onClick={onClose} />
      )}
      
      {/* Sidebar Panel */}
      <div className={`edit-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="edit-sidebar__header">
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
            Dodaj zdravstveni karton
          </h3>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Close}
            iconDescription="Zatvori"
            hasIconOnly
            onClick={onClose}
          />
        </div>

        <div className="edit-sidebar__content">
          <form className="edit-sidebar__form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            {/* Mobility */}
            <Select
              id="mobility"
              labelText="Pokretljivost *"
              value={formData.mobility}
              onChange={(e) => handleInputChange('mobility', e.target.value)}
              disabled={isSaving}
            >
              {mobilityOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value} text={opt.label} />
              ))}
            </Select>

            {/* Self Service Ability */}
            <Select
              id="selfServiceAbility"
              labelText="Sposobnost samoposluživanja *"
              value={formData.selfServiceAbility}
              onChange={(e) => handleInputChange('selfServiceAbility', e.target.value)}
              disabled={isSaving}
            >
              {selfServiceOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value} text={opt.label} />
              ))}
            </Select>

            {/* Personal Hygiene Ability */}
            <Select
              id="personalHygieneAbility"
              labelText="Sposobnost održavanja lične higijene *"
              value={formData.personalHygieneAbility}
              onChange={(e) => handleInputChange('personalHygieneAbility', e.target.value)}
              disabled={isSaving}
            >
              {personalHygieneOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value} text={opt.label} />
              ))}
            </Select>

            {/* Communication Ability */}
            <Select
              id="communicationAbility"
              labelText="Sposobnost komunikacije *"
              value={formData.communicationAbility}
              onChange={(e) => handleInputChange('communicationAbility', e.target.value)}
              disabled={isSaving}
            >
              {communicationOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value} text={opt.label} />
              ))}
            </Select>

            {/* Mental State */}
            <Select
              id="mentalState"
              labelText="Psihičko stanje *"
              value={formData.mentalState}
              onChange={(e) => handleInputChange('mentalState', e.target.value)}
              disabled={isSaving}
            >
              {mentalStateOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value} text={opt.label} />
              ))}
            </Select>

            {/* Uses Special Aids */}
            <Toggle
              id="usesSpecialAids"
              labelText="Korišćenje specijalnih pomagala"
              labelA="Ne"
              labelB="Da"
              toggled={formData.usesSpecialAids}
              onToggle={(checked) => handleInputChange('usesSpecialAids', checked)}
              disabled={isSaving}
            />

            {/* Special Aids Description - conditional */}
            {formData.usesSpecialAids && (
              <TextArea
                id="specialAidsDescription"
                labelText="Opis specijalnih pomagala"
                placeholder="Opišite specijalna pomagala koja stanar koristi..."
                value={formData.specialAidsDescription}
                onChange={(e) => handleInputChange('specialAidsDescription', e.target.value)}
                rows={3}
                disabled={isSaving}
              />
            )}

            {/* Diseases */}
            <TextInput
              id="diseases"
              labelText="Bolesti (opciono)"
              placeholder="Lista bolesti..."
              value={formData.diseases}
              onChange={(e) => handleInputChange('diseases', e.target.value)}
              maxLength={500}
              disabled={isSaving}
            />

            {/* Diseases Description */}
            <TextArea
              id="diseasesDescription"
              labelText="Detaljan opis bolesti (opciono)"
              placeholder="Detaljnije opišite zdravstveno stanje stanara..."
              value={formData.diseasesDescription}
              onChange={(e) => handleInputChange('diseasesDescription', e.target.value)}
              rows={4}
              maxLength={2000}
              disabled={isSaving}
            />

            <div className="edit-sidebar__actions">
              <Button
                kind="secondary"
                onClick={onClose}
                disabled={isSaving}
              >
                Otkaži
              </Button>
              <Button
                kind="primary"
                type="submit"
                disabled={isSaving}
              >
                {isSaving ? 'Čuvanje...' : 'Sačuvaj'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

// Health enum options (value = English enum member, label = Serbian display)
const HEALTH_MOBILITY_OPTIONS = [
  { value: 'Mobile', label: 'Pokretan' },
  { value: 'ImpairedMobility', label: 'Otežano pokretan' },
  { value: 'Immobile', label: 'Nepokretan' }
];

const HEALTH_SELF_SERVICE_OPTIONS = [
  { value: 'Capable', label: 'Sposoban' },
  { value: 'PartiallyCapable', label: 'Delimično sposoban' },
  { value: 'NotCapable', label: 'Nije sposoban' }
];

const HEALTH_HYGIENE_OPTIONS = [
  { value: 'Independent', label: 'Samostalan' },
  { value: 'PartiallyIndependent', label: 'Delimično samostalan' },
  { value: 'NotIndependent', label: 'Nije samostalan' }
];

const HEALTH_COMMUNICATION_OPTIONS = [
  { value: 'Preserved', label: 'Očuvana' },
  { value: 'ImpairedCommunication', label: 'Otežana komunikacija' },
  { value: 'CommunicationCannotBeEstablished', label: 'Komunikacija se ne uspostavlja' }
];

const HEALTH_MENTAL_STATE_OPTIONS = [
  { value: 'MentallyPreserved', label: 'Psihički očuvan' },
  { value: 'MildDementia', label: 'Demencija - blaga' },
  { value: 'ModerateDementia', label: 'Demencija - umerena' },
  { value: 'SevereDementia', label: 'Demencija - teška' },
  { value: 'Psychosis', label: 'Psihoza' },
  { value: 'NeurologicalDisorder', label: 'Neuronski poremećaj' },
  { value: 'PersonalityDisorder', label: 'Poremećaj ličnosti' },
  { value: 'AddictionDisorders', label: 'Bolesti zavisnosti' }
];

// Map Serbian display value (from GET response) to English enum value (for PUT)
const serbianToEnum = (value, options) => {
  if (!value) return options[0]?.value;
  // If already an English enum value, return as-is
  const found = options.find(opt => opt.value === value);
  if (found) return found.value;
  // Otherwise map from Serbian label to English enum value
  const byLabel = options.find(opt => opt.label === value);
  return byLabel ? byLabel.value : options[0]?.value;
};

// Health Edit Sidebar Component - Edit existing health record (PUT)
// ===========================================
const HealthEditSidebar = ({ isOpen, onClose, healthData, resident, setNotification, onSuccess, section = 'mobility' }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    mobility: 'Mobile',
    selfServiceAbility: 'Capable',
    personalHygieneAbility: 'Independent',
    communicationAbility: 'Preserved',
    usesSpecialAids: false,
    specialAidsDescription: '',
    mentalState: 'MentallyPreserved',
    diseases: '',
    diseasesDescription: ''
  });

  // Section titles
  const sectionTitles = {
    mobility: 'Pokretljivost i nezavisnost',
    communication: 'Komunikacija i mentalno stanje',
    aids: 'Specijalna pomagala',
    diseases: 'Bolesti i dijagnoze'
  };

  // Initialize form with existing health data when sidebar opens
  useEffect(() => {
    if (isOpen && healthData) {
      setFormData({
        mobility: serbianToEnum(healthData.mobility, HEALTH_MOBILITY_OPTIONS),
        selfServiceAbility: serbianToEnum(healthData.selfServiceAbility, HEALTH_SELF_SERVICE_OPTIONS),
        personalHygieneAbility: serbianToEnum(healthData.personalHygieneAbility, HEALTH_HYGIENE_OPTIONS),
        communicationAbility: serbianToEnum(healthData.communicationAbility, HEALTH_COMMUNICATION_OPTIONS),
        usesSpecialAids: healthData.usesSpecialAids ?? false,
        specialAidsDescription: healthData.specialAidsDescription || '',
        mentalState: serbianToEnum(healthData.mentalState, HEALTH_MENTAL_STATE_OPTIONS),
        diseases: healthData.diseases || '',
        diseasesDescription: healthData.diseasesDescription || ''
      });
    }
  }, [isOpen, healthData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!resident?.id) {
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle: 'Nedostaje ID stanara'
      });
      setTimeout(() => setNotification(null), 4000);
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        mobility: formData.mobility,
        selfServiceAbility: formData.selfServiceAbility,
        personalHygieneAbility: formData.personalHygieneAbility,
        communicationAbility: formData.communicationAbility,
        usesSpecialAids: formData.usesSpecialAids,
        specialAidsDescription: formData.usesSpecialAids ? formData.specialAidsDescription : '',
        mentalState: formData.mentalState,
        diseases: formData.diseases || '',
        diseasesDescription: formData.diseasesDescription || ''
      };

      const response = await axiosInstance.put(`/api/ResidentHealth/resident/${resident.id}`, payload);

      if (response.data.success) {
        setNotification({
          kind: 'success',
          title: 'Uspešno sačuvano',
          subtitle: 'Zdravstveni podaci su uspešno ažurirani'
        });
        setTimeout(() => setNotification(null), 3000);
        onClose();
        if (onSuccess) {
          onSuccess(response.data.data);
        }
      } else {
        setNotification({
          kind: 'error',
          title: 'Greška',
          subtitle: response.data.message || 'Došlo je do greške pri ažuriranju zdravstvenih podataka'
        });
        setTimeout(() => setNotification(null), 4000);
      }
    } catch (err) {
      console.error('Greška pri ažuriranju zdravstvenih podataka:', err);
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle: err.response?.data?.message || 'Došlo je do greške pri ažuriranju zdravstvenih podataka'
      });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div className="edit-sidebar-overlay" onClick={onClose} />
      )}
      
      {/* Sidebar Panel */}
      <div className={`edit-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="edit-sidebar__header">
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
            Izmeni: {sectionTitles[section] || 'Zdravstvene podatke'}
          </h3>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Close}
            iconDescription="Zatvori"
            hasIconOnly
            onClick={onClose}
          />
        </div>

        <div className="edit-sidebar__content">
          <form className="edit-sidebar__form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            {/* Mobility Section */}
            {section === 'mobility' && (
              <>
                {/* Mobility */}
                <Select
                  id="edit-mobility"
                  labelText="Pokretljivost *"
                  value={formData.mobility}
                  onChange={(e) => handleInputChange('mobility', e.target.value)}
                  disabled={isSaving}
                >
                  {HEALTH_MOBILITY_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} text={opt.label} />
                  ))}
                </Select>

                {/* Self Service Ability */}
                <Select
                  id="edit-selfServiceAbility"
                  labelText="Sposobnost samoposluživanja *"
                  value={formData.selfServiceAbility}
                  onChange={(e) => handleInputChange('selfServiceAbility', e.target.value)}
                  disabled={isSaving}
                >
                  {HEALTH_SELF_SERVICE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} text={opt.label} />
                  ))}
                </Select>

                {/* Personal Hygiene Ability */}
                <Select
                  id="edit-personalHygieneAbility"
                  labelText="Sposobnost održavanja lične higijene *"
                  value={formData.personalHygieneAbility}
                  onChange={(e) => handleInputChange('personalHygieneAbility', e.target.value)}
                  disabled={isSaving}
                >
                  {HEALTH_HYGIENE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} text={opt.label} />
                  ))}
                </Select>
              </>
            )}

            {/* Communication Section */}
            {section === 'communication' && (
              <>
                {/* Communication Ability */}
                <Select
                  id="edit-communicationAbility"
                  labelText="Sposobnost komunikacije *"
                  value={formData.communicationAbility}
                  onChange={(e) => handleInputChange('communicationAbility', e.target.value)}
                  disabled={isSaving}
                >
                  {HEALTH_COMMUNICATION_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} text={opt.label} />
                  ))}
                </Select>

                {/* Mental State */}
                <Select
                  id="edit-mentalState"
                  labelText="Psihičko stanje *"
                  value={formData.mentalState}
                  onChange={(e) => handleInputChange('mentalState', e.target.value)}
                  disabled={isSaving}
                >
                  {HEALTH_MENTAL_STATE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} text={opt.label} />
                  ))}
                </Select>
              </>
            )}

            {/* Special Aids Section */}
            {section === 'aids' && (
              <>
                {/* Uses Special Aids */}
                <Toggle
                  id="edit-usesSpecialAids"
                  labelText="Korišćenje specijalnih pomagala"
                  labelA="Ne"
                  labelB="Da"
                  toggled={formData.usesSpecialAids}
                  onToggle={(checked) => handleInputChange('usesSpecialAids', checked)}
                  disabled={isSaving}
                />

                {/* Special Aids Description - conditional */}
                {formData.usesSpecialAids && (
                  <TextArea
                    id="edit-specialAidsDescription"
                    labelText="Opis specijalnih pomagala"
                    placeholder="Opišite specijalna pomagala koja stanar koristi..."
                    value={formData.specialAidsDescription}
                    onChange={(e) => handleInputChange('specialAidsDescription', e.target.value)}
                    rows={3}
                    disabled={isSaving}
                  />
                )}
              </>
            )}

            {/* Diseases Section */}
            {section === 'diseases' && (
              <>
                {/* Diseases */}
                <TextInput
                  id="edit-diseases"
                  labelText="Bolesti (opciono)"
                  placeholder="Lista bolesti..."
                  value={formData.diseases}
                  onChange={(e) => handleInputChange('diseases', e.target.value)}
                  maxLength={500}
                  disabled={isSaving}
                />

                {/* Diseases Description */}
                <TextArea
                  id="edit-diseasesDescription"
                  labelText="Detaljan opis bolesti (opciono)"
                  placeholder="Detaljnije opišite zdravstveno stanje stanara..."
                  value={formData.diseasesDescription}
                  onChange={(e) => handleInputChange('diseasesDescription', e.target.value)}
                  rows={4}
                  maxLength={2000}
                  disabled={isSaving}
                />
              </>
            )}

            <div className="edit-sidebar__actions">
              <Button
                kind="secondary"
                onClick={onClose}
                disabled={isSaving}
              >
                Otkaži
              </Button>
              <Button
                kind="primary"
                type="submit"
                disabled={isSaving}
              >
                {isSaving ? 'Čuvanje...' : 'Sačuvaj'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

// Avatar Component with fallback to initials.
// Renders a clickable avatar with a camera badge overlay and an options menu
// ("Pogledaj sliku" / "Nova profilna") — same behaviour as the employee profile.
// When `canUpload` is false (e.g. caregivers), the camera badge and the upload
// option are hidden; clicking the avatar only previews the existing photo.
const ResidentAvatar = ({
  avatar,
  fullName,
  onClick,
  clickable = false,
  canUpload = true,
  isUploading = false,
  menuOpen = false,
  onViewImage,
  onNewPhoto,
}) => {
  const [imageError, setImageError] = useState(false);
  
  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };
  
  const handleImageError = () => {
    setImageError(true);
  };
  
  const hasImage = !!avatar && !imageError;
  const isInteractive = clickable && (canUpload || hasImage);
  
  const handleClick = () => {
    if (!isInteractive) return;
    // Without upload rights, clicking only previews the existing photo.
    if (!canUpload) {
      if (hasImage && onViewImage) onViewImage();
      return;
    }
    if (onClick) onClick();
  };
  
  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && isInteractive) {
      e.preventDefault();
      handleClick();
    }
  };
  
  return (
    <>
      <button
        type="button"
        className="rph-avatar-btn"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={isUploading}
        aria-haspopup={canUpload && hasImage ? 'menu' : undefined}
        aria-expanded={canUpload && hasImage ? menuOpen : undefined}
        aria-label={
          !canUpload
            ? (hasImage ? `Prikaži fotografiju: ${fullName}` : undefined)
            : (hasImage ? 'Opcije fotografije' : 'Dodaj fotografiju')
        }
        title={
          !canUpload
            ? (hasImage ? 'Prikaži fotografiju' : undefined)
            : (hasImage ? 'Opcije fotografije' : 'Dodaj fotografiju')
        }
      >
        {hasImage ? (
          <img
            src={avatar}
            alt={`Fotografija: ${fullName}`}
            className="residents1-avatar"
            onError={handleImageError}
          />
        ) : (
          <div className="residents1-avatar-placeholder" aria-hidden="true">
            {getInitials(fullName)}
          </div>
        )}
        {canUpload && (
          <span className="rph-avatar-badge" aria-hidden="true">
            {isUploading ? <span className="rph-avatar-spinner" /> : <Icons.Camera />}
          </span>
        )}
      </button>

      {/* Avatar options menu (only when a photo exists and upload is allowed) */}
      {canUpload && menuOpen && hasImage && (
        <div className="rph-avatar-menu" role="menu" aria-label="Opcije fotografije">
          <button
            type="button"
            className="rph-avatar-menu__item"
            role="menuitem"
            onClick={onViewImage}
          >
            <span className="rph-avatar-menu__icon"><Icons.Eye /></span>
            Pogledaj sliku
          </button>
          <button
            type="button"
            className="rph-avatar-menu__item"
            role="menuitem"
            onClick={onNewPhoto}
          >
            <span className="rph-avatar-menu__icon"><Icons.Upload /></span>
            Nova profilna
          </button>
        </div>
      )}
    </>
  );
};

// Info Chip Component
const InfoChip = ({ icon: Icon, children }) => (
  <span className="residents1-chip">
    {Icon && <Icon />}
    {children}
  </span>
);

// Custom Button Component (for non-Carbon styled buttons)
const CustomButton = ({ variant = 'secondary', icon: Icon, children, onClick, ...props }) => (
  <button 
    className={`residents1-btn residents1-btn--${variant}`}
    onClick={onClick}
    {...props}
  >
    {Icon && <span className="residents1-btn__icon"><Icon /></span>}
    {children}
  </button>
);

// ===========================================
// Enterprise Header Subcomponents (Fluent/Azure Portal Style)
// ===========================================

// Status Chip Component - Modern pill-style inline status indicator
const StatusChip = ({ status }) => {
  const getStatusConfig = (status) => {
    const configs = {
      active: { label: 'Aktivan', icon: Icons.CheckCircle },
      absent: { label: 'Odsutan', icon: Icons.Clock },
      hospitalized: { label: 'Hospitalizovan', icon: Icons.Hospital }
    };
    return configs[status] || configs.active;
  };
  
  const config = getStatusConfig(status);
  const StatusIcon = config.icon;
  
  return (
    <span 
      className={`rph-status-chip rph-status-chip--${status}`}
      role="status"
      aria-label={`Status: ${config.label}`}
    >
      <span className="rph-status-chip__dot" aria-hidden="true" />
      <StatusIcon />
      <span className="rph-status-chip__label">{config.label}</span>
    </span>
  );
};

// Meta Row Component - Single line metadata with separators
// Identity Section Component - Avatar + Name + Status
const IdentitySection = ({
  avatar,
  fullName,
  status,
  age,
  roomNumber,
  careLevel,
  isCollapsed,
  onAvatarClick,
  canUploadAvatar = true,
  isUploading,
  avatarMenuOpen,
  onViewImage,
  onNewPhoto,
  avatarWrapperRef,
  fileInputRef,
  onFileChange,
}) => (
  <div className="rph-identity">
    <div
      className={`rph-avatar-wrapper ${isCollapsed ? 'rph-avatar-wrapper--collapsed' : ''}`}
      ref={avatarWrapperRef}
    >
      <ResidentAvatar 
        avatar={avatar} 
        fullName={fullName} 
        clickable
        canUpload={canUploadAvatar}
        onClick={onAvatarClick}
        isUploading={isUploading}
        menuOpen={avatarMenuOpen}
        onViewImage={onViewImage}
        onNewPhoto={onNewPhoto}
      />
      {canUploadAvatar && (
        <input
          ref={fileInputRef}
          type="file"
          className="rph-avatar-input"
          accept=".jpg,.jpeg,.png,.webp,.gif,.bmp"
          onChange={onFileChange}
          tabIndex={-1}
          aria-hidden="true"
        />
      )}
    </div>
    <div className="rph-identity-content">
      <div className="rph-identity-primary">
        <h1 className={`rph-name ${isCollapsed ? 'rph-name--collapsed' : ''}`}>
          {fullName}
        </h1>
      </div>
    </div>
  </div>
);

// Header Action Button - Compact enterprise-style button
const HeaderButton = ({ variant = 'ghost', icon: Icon, children, onClick, iconOnly = false, title, className = '', ...props }) => (
  <button 
    className={`rph-action-btn rph-action-btn--${variant} ${iconOnly ? 'rph-action-btn--icon-only' : ''} ${className}`}
    onClick={onClick}
    title={title}
    aria-label={iconOnly ? title : undefined}
    {...props}
  >
    {Icon && <span className="rph-action-btn__icon"><Icon /></span>}
    {!iconOnly && <span className="rph-action-btn__label">{children}</span>}
  </button>
);

// Overflow Menu Component with keyboard accessibility
const OverflowMenu = ({ isOpen, onToggle, onClose, menuRef, children }) => {
  const menuItemsRef = useRef([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  
  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    const items = menuItemsRef.current.filter(Boolean);
    
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        onClose?.();
        break;
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => (prev + 1) % items.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => (prev - 1 + items.length) % items.length);
        break;
      case 'Home':
        e.preventDefault();
        setFocusedIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setFocusedIndex(items.length - 1);
        break;
      case 'Tab':
        onClose?.();
        break;
      default:
        break;
    }
  };
  
  // Focus the item when focusedIndex changes
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && menuItemsRef.current[focusedIndex]) {
      menuItemsRef.current[focusedIndex].focus();
    }
  }, [focusedIndex, isOpen]);
  
  // Reset focus when menu closes (don't auto-focus on open)
  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(-1);
    }
  }, [isOpen]);
  
  return (
    <div className="rph-overflow-menu" ref={menuRef}>
      <button 
        className="rph-overflow-trigger"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="Više opcija"
      >
        <Icons.MoreHorizontal />
      </button>
      {isOpen && (
        <div 
          className="rph-overflow-dropdown" 
          role="menu"
          aria-label="Dodatne akcije"
          onKeyDown={handleKeyDown}
        >
          {/* `toArray` + `isValidElement` uklanja null/false decu (uslovno
              renderovane stavke). React.Children.map poziva callback i za njih,
              pa bi `cloneElement(null, ...)` pukao. */}
          {React.Children.toArray(children)
            .filter((child) => React.isValidElement(child))
            .map((child, index) =>
              React.cloneElement(child, {
                ref: (el) => (menuItemsRef.current[index] = el),
                tabIndex: focusedIndex === index ? 0 : -1
              })
            )}
        </div>
      )}
    </div>
  );
};

// Overflow Menu Item with forwardRef for keyboard navigation
const OverflowMenuItem = React.forwardRef(({ icon: Icon, children, onClick, variant = 'default', tabIndex = -1 }, ref) => (
  <button 
    ref={ref}
    className={`rph-overflow-item ${variant === 'danger' ? 'rph-overflow-item--danger' : ''}`}
    onClick={onClick}
    role="menuitem"
    tabIndex={tabIndex}
  >
    {Icon && <span className="rph-overflow-item__icon"><Icon /></span>}
    <span className="rph-overflow-item__label">{children}</span>
  </button>
));
OverflowMenuItem.displayName = 'OverflowMenuItem';

// Actions Section Component - Primary actions + overflow
const ActionsSection = ({ isCollapsed, isOverflowOpen, onOverflowToggle, onOverflowClose, overflowMenuRef, onEditProfile, onTransferClick, onAddNote, onAddToGroup, onDeactivate, canDeactivate = false, canTransfer = false, canUpdate = false }) => (
  <div className={`rph-actions ${isCollapsed ? 'rph-actions--collapsed' : ''}`}>
    {/* Primary Action - Hidden on mobile (moved into overflow menu) */}
    <HeaderButton 
      variant="primary" 
      icon={Icons.Plus}
      iconOnly={isCollapsed}
      title="Dodaj belešku"
      onClick={onAddNote}
      className="rph-action-btn--add-note"
    >
      Dodaj belešku
    </HeaderButton>
    
    {/* Overflow Menu */}
    <OverflowMenu 
      isOpen={isOverflowOpen} 
      onToggle={onOverflowToggle}
      onClose={onOverflowClose}
      menuRef={overflowMenuRef}
    >
      <OverflowMenuItem icon={Icons.Plus} onClick={onAddNote}>
        Dodaj belešku
      </OverflowMenuItem>
      <OverflowMenuItem icon={Icons.Users} onClick={onAddToGroup}>
        Dodaj u grupu
      </OverflowMenuItem>
      {canUpdate && (
        <OverflowMenuItem icon={Icons.Edit} onClick={onEditProfile}>
          Izmeni profil
        </OverflowMenuItem>
      )}
      {canTransfer && (
        <OverflowMenuItem icon={Icons.ArrowRight} onClick={onTransferClick}>
          Premesti
        </OverflowMenuItem>
      )}
      <OverflowMenuItem icon={Icons.FileText}>
        Izveštaj
      </OverflowMenuItem>
      {canDeactivate && (
        <OverflowMenuItem icon={Icons.MinusCircle} variant="danger" onClick={onDeactivate}>
          Deaktivacija
        </OverflowMenuItem>
      )}
    </OverflowMenu>
  </div>
);

// Card Component
const Card = ({ title, icon: Icon, children, actions, onEdit, disabled = false }) => (
  <div className="residents1-card">
    <div className="residents1-card__header">
      <h3 className="residents1-card__title">
        {Icon && <span className="residents1-card__title-icon"><Icon /></span>}
        {title}
      </h3>
      <div className="residents1-card__actions">
        {onEdit && (
          <button 
            className="residents1-card__edit-btn"
            onClick={onEdit}
            disabled={disabled}
            aria-label={`Izmeni ${title}`}
            title="Izmeni"
            style={disabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
          >
            <Icons.Pencil />
          </button>
        )}
        {actions}
      </div>
    </div>
    <div className="residents1-card__body">
      {children}
    </div>
  </div>
);

// Sidebar Card Component
const SidebarCard = ({ title, icon: Icon, children, className = '' }) => (
  <div className={`residents1-sidebar-card ${className}`}>
    <div className="residents1-sidebar-card__header">
      <h4 className="residents1-sidebar-card__title">
        {Icon && <span className="residents1-card__title-icon"><Icon /></span>}
        {title}
      </h4>
    </div>
    <div className="residents1-sidebar-card__body">
      {children}
    </div>
  </div>
);

// Data Item Component
const DataItem = ({ label, value, masked = false, onReveal, isRevealed, onCopy, copyable = false }) => {
  const [copied, setCopied] = React.useState(false);
  
  const handleCopy = () => {
    if (onCopy) {
      onCopy();
    } else if (value && value !== '-') {
      const copyValue = typeof value === 'object' && value.text ? value.text : value;
      navigator.clipboard.writeText(copyValue);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  // Handle language object with flag
  const isLanguageObject = typeof value === 'object' && value !== null && 'text' in value && 'flag' in value;
  const displayValue = isLanguageObject ? value.text : value;
  const languageFlag = isLanguageObject ? value.flag : null;
  
  return (
    <div className="residents1-data-item">
      <span className="residents1-data-label">{label}</span>
      <span className={`residents1-data-value ${masked ? 'residents1-data-value--masked' : ''}`}>
        {languageFlag && (
          <span className="residents1-language-flag">
            {languageFlag}
          </span>
        )}
        {displayValue}
        {onReveal && (
          <button className="residents1-reveal-btn" onClick={onReveal} aria-label={isRevealed ? 'Sakrij' : 'Prikaži'} title={isRevealed ? 'Sakrij' : 'Prikaži'}>
            <span className="residents1-reveal-btn__icon">
              {isRevealed ? <Icons.EyeOff /> : <Icons.Eye />}
            </span>
          </button>
        )}
        {copyable && (
          <div className="residents1-copy-wrapper">
            <button 
              className="residents1-copy-btn" 
              onClick={handleCopy} 
              aria-label="Kopiraj"
              title="Kopiraj"
            >
              <span className="residents1-copy-btn__icon">
                <Icons.Copy />
              </span>
            </button>
            {copied && (
              <div className="residents1-copy-toast">
                <Icons.CheckCircle />
                Kopirano!
              </div>
            )}
          </div>
        )}
      </span>
    </div>
  );
};

// Timeline Item Component
const TimelineItem = ({ item }) => {
  const getIconClass = (type) => {
    const classes = {
      room: 'room',
      care: 'care',
      nutrition: 'nutrition',
      incident: 'incident'
    };
    return classes[type] || '';
  };
  
  const getIcon = (type) => {
    const icons = {
      room: Icons.Home,
      care: Icons.Heart,
      nutrition: Icons.Utensils,
      incident: Icons.AlertCircle
    };
    const IconComponent = icons[type] || Icons.Activity;
    return <IconComponent />;
  };
  
  return (
    <div className="residents1-timeline-item">
      <div className={`residents1-timeline-icon residents1-timeline-icon--${getIconClass(item.type)}`}>
        {getIcon(item.type)}
      </div>
      <div className="residents1-timeline-content">
        <h4 className="residents1-timeline-title">{item.title}</h4>
        <p className="residents1-timeline-description">{item.description}</p>
        <div className="residents1-timeline-meta">
          <span><Icons.Calendar /> {formatDate(item.date)}</span>
          <span><Icons.User /> {item.author}</span>
        </div>
      </div>
    </div>
  );
};

// ===========================================
// Tab Content Components
// ===========================================

// Animated Activity Icons - Professional SVG Animations
const AnimatedActivityIcons = {
  // Walking person animation
  Walk: () => (
    <svg viewBox="0 0 64 64" className="animated-icon animated-icon--walk">
      <g className="walk-figure">
        <circle cx="32" cy="12" r="6" fill="currentColor" />
        <line x1="32" y1="18" x2="32" y2="36" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        <g className="walk-arms">
          <line x1="32" y1="24" x2="22" y2="32" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          <line x1="32" y1="24" x2="42" y2="28" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </g>
        <g className="walk-legs">
          <line x1="32" y1="36" x2="24" y2="52" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          <line x1="32" y1="36" x2="40" y2="52" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </g>
      </g>
      <g className="walk-ground">
        <circle cx="18" cy="56" r="2" fill="currentColor" opacity="0.3" />
        <circle cx="32" cy="56" r="2" fill="currentColor" opacity="0.5" />
        <circle cx="46" cy="56" r="2" fill="currentColor" opacity="0.3" />
      </g>
    </svg>
  ),
  Meal: () => (
    <svg viewBox="0 0 64 64" className="animated-icon animated-icon--meal">
      {/* Fork */}
      <g className="meal-fork">
        <line x1="22" y1="12" x2="22" y2="52" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <line x1="18" y1="12" x2="20" y2="24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="22" y1="12" x2="22" y2="24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="26" y1="12" x2="24" y2="24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </g>
      {/* Knife */}
      <g className="meal-knife">
        <line x1="42" y1="12" x2="42" y2="52" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <ellipse cx="44" cy="20" rx="3" ry="10" fill="currentColor" opacity="0.4" />
      </g>
    </svg>
  ),
  Therapy: () => (
    <svg viewBox="0 0 64 64" className="animated-icon animated-icon--therapy">
      <g className="therapy-person">
        <circle cx="20" cy="16" r="6" fill="currentColor" />
        <path d="M20 22 L20 34 L14 48" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" />
        <g className="therapy-arm">
          <path d="M20 28 L32 26" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" />
        </g>
      </g>
      <g className="therapy-pill">
        <rect x="36" y="22" width="16" height="10" rx="5" fill="currentColor" opacity="0.7" />
        <line x1="44" y1="22" x2="44" y2="32" stroke="currentColor" strokeWidth="1" opacity="0.5" />
      </g>
      <g className="therapy-pulse">
        <path d="M36 44 L40 44 L42 38 L46 50 L48 44 L52 44" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <g className="therapy-cross">
        <rect x="40" y="8" width="8" height="2" fill="currentColor" opacity="0.4" />
        <rect x="43" y="5" width="2" height="8" fill="currentColor" opacity="0.4" />
      </g>
    </svg>
  ),
  Rest: () => (
    <svg viewBox="0 0 64 64" className="animated-icon animated-icon--rest">
      <rect x="8" y="40" width="48" height="6" rx="2" fill="currentColor" opacity="0.3" />
      <rect x="8" y="46" width="4" height="10" fill="currentColor" opacity="0.3" />
      <rect x="52" y="46" width="4" height="10" fill="currentColor" opacity="0.3" />
      <ellipse cx="18" cy="38" rx="8" ry="4" fill="currentColor" opacity="0.4" />
      <g className="rest-person">
        <circle cx="20" cy="34" r="6" fill="currentColor" />
        <path d="M26 36 Q40 34 52 38" stroke="currentColor" strokeWidth="8" strokeLinecap="round" fill="none" opacity="0.5" />
      </g>
      <g className="rest-zzz">
        <text x="36" y="20" fontSize="10" fill="currentColor" opacity="0.6" className="zzz-1">Z</text>
        <text x="44" y="14" fontSize="8" fill="currentColor" opacity="0.4" className="zzz-2">Z</text>
        <text x="50" y="10" fontSize="6" fill="currentColor" opacity="0.3" className="zzz-3">Z</text>
      </g>
      <g className="rest-moon">
        <path d="M52 6 Q58 8 56 14 Q50 12 52 6" fill="currentColor" opacity="0.3" />
      </g>
    </svg>
  ),
  Care: () => (
    <svg viewBox="0 0 64 64" className="animated-icon animated-icon--care">
      <g className="care-person">
        <circle cx="24" cy="16" r="6" fill="currentColor" />
        <line x1="24" y1="22" x2="24" y2="36" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        <line x1="24" y1="36" x2="18" y2="52" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        <line x1="24" y1="36" x2="30" y2="52" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </g>
      <g className="care-arm">
        <path d="M24 28 L36 22" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" />
      </g>
      <g className="care-brush">
        <rect x="36" y="16" width="3" height="14" rx="1" fill="currentColor" opacity="0.7" />
        <rect x="34" y="14" width="7" height="4" rx="1" fill="currentColor" opacity="0.5" />
      </g>
      <g className="care-drops">
        <circle cx="46" cy="24" r="2" fill="currentColor" opacity="0.3" />
        <circle cx="50" cy="28" r="1.5" fill="currentColor" opacity="0.2" />
        <circle cx="48" cy="32" r="1" fill="currentColor" opacity="0.2" />
      </g>
      <g className="care-sun">
        <circle cx="52" cy="12" r="4" fill="currentColor" opacity="0.4" />
        <g className="sun-rays">
          <line x1="52" y1="4" x2="52" y2="6" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
          <line x1="58" y1="12" x2="60" y2="12" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
          <line x1="56" y1="8" x2="58" y2="6" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
          <line x1="56" y1="16" x2="58" y2="18" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
        </g>
      </g>
    </svg>
  ),
  Activity: () => (
    <svg viewBox="0 0 64 64" className="animated-icon animated-icon--activity">
      <g className="activity-person">
        <circle cx="22" cy="18" r="6" fill="currentColor" />
        <path d="M22 24 L22 38 L16 52" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" />
        <line x1="22" y1="38" x2="28" y2="50" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </g>
      <g className="activity-arms">
        <path d="M22 30 L34 28" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" />
        <path d="M22 32 L32 34" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" />
      </g>
      <g className="activity-book">
        <rect x="34" y="24" width="18" height="14" rx="1" fill="currentColor" opacity="0.3" />
        <line x1="43" y1="24" x2="43" y2="38" stroke="currentColor" strokeWidth="1" opacity="0.2" />
        <line x1="37" y1="28" x2="41" y2="28" stroke="currentColor" strokeWidth="1" opacity="0.4" />
        <line x1="37" y1="31" x2="41" y2="31" stroke="currentColor" strokeWidth="1" opacity="0.4" />
        <line x1="37" y1="34" x2="40" y2="34" stroke="currentColor" strokeWidth="1" opacity="0.4" />
        <line x1="45" y1="28" x2="49" y2="28" stroke="currentColor" strokeWidth="1" opacity="0.4" />
        <line x1="45" y1="31" x2="49" y2="31" stroke="currentColor" strokeWidth="1" opacity="0.4" />
      </g>
      <g className="activity-coffee">
        <path d="M46 44 L44 54 L54 54 L52 44 Z" fill="currentColor" opacity="0.4" />
        <path d="M54 46 Q58 48 54 52" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.3" />
        <g className="coffee-steam">
          <path d="M48 40 Q49 38 48 36" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.3" />
          <path d="M51 42 Q52 40 51 38" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.2" />
        </g>
      </g>
    </svg>
  )
};

// Current Activity Display Component
const CurrentActivityDisplay = ({ residentId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchCurrentNext = useCallback(async () => {
    if (!residentId) return;
    setLoading(true);
    setError(false);
    try {
      const res = await axiosInstance.get(`/api/ResidentActivity/resident/${residentId}/current-next`);
      setData(res.data?.data || null);
    } catch {
      setError(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [residentId]);

  useEffect(() => {
    fetchCurrentNext();
    // Refresh periodically so the "current" activity stays in sync
    const interval = setInterval(fetchCurrentNext, 60000);
    return () => clearInterval(interval);
  }, [fetchCurrentNext]);

  const formatTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('sr-Latn-RS', { hour: '2-digit', minute: '2-digit' });
  };

  const getActivityTypeLabel = (activityType) => {
    const t = (activityType || '').toLowerCase();
    if (t === 'nutrition' || t === 'meal') return 'Ishrana';
    if (t === 'physical' || t === 'exercise') return 'Fizička aktivnost';
    if (t === 'entertainment') return 'Zabava';
    if (t === 'therapy' || t === 'medical') return 'Terapija';
    if (t === 'rest') return 'Odmor';
    if (t === 'care') return 'Nega';
    if (t === 'visit' || t === 'poseta') return 'Poseta';
    return activityType || 'Aktivnost';
  };

  const getActivityIcon = (activityType) => {
    const t = (activityType || '').toLowerCase();
    if (t === 'nutrition' || t === 'meal') return Restaurant;
    if (t === 'physical' || t === 'exercise' || t === 'walk') return Run;
    if (t === 'entertainment') return GameConsole;
    return Activity;
  };

  if (loading) {
    return (
      <div className="residents1-current-activity" style={{ minHeight: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="residents1-spinner" />
      </div>
    );
  }

  if (error || !data || (!data.current && !data.next)) {
    return (
      <div className="residents1-current-activity" style={{ minHeight: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: 'var(--r1-text-secondary)' }}>
        <div>
          <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem', opacity: 0.4 }}>📅</div>
          <div style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>Nema zakazanih aktivnosti</div>
        </div>
      </div>
    );
  }

  const current = data.current;
  const next = data.next;
  // "U toku" se određuje po vremenskom prozoru (da li "now" pada unutar startTime–endTime),
  // a ne po polju status, jer prave aktivnosti imaju status "planned" i dok traju.
  // Placeholder "Odmor i slobodno vreme" (activityType === 'rest') uvek predstavlja sadašnji trenutak.
  const now = data.now ? new Date(data.now) : new Date();
  const isRestPlaceholder = current && (current.activityType || '').toLowerCase() === 'rest';
  const isInProgress = current && (
    isRestPlaceholder ||
    (now >= new Date(current.startTime) && now < new Date(current.endTime))
  );
  const statusLabel = isInProgress ? 'U toku' : 'Predstoji';
  const Icon = getActivityIcon(current?.activityType);
  const timeRange = current
    ? `${formatTime(current.startTime)}${current.endTime ? ` – ${formatTime(current.endTime)}` : ''}`
    : '';

  return (
    <div
      className={`residents1-current-activity ${current ? `residents1-current-activity--${(current.activityType || '').toLowerCase()}` : ''}`}
      data-status={isInProgress ? 'in-progress' : 'upcoming'}
    >
      {/* Status header */}
      <div className="residents1-current-activity-header">
        <span className="residents1-current-activity-status">
          <span className="residents1-current-activity-status-dot" />
          {statusLabel}
        </span>
        <span className="residents1-current-activity-type">
          {getActivityTypeLabel(current?.activityType)}
        </span>
      </div>

      {/* Main activity */}
      <div className="residents1-current-activity-main">
        <div className="residents1-current-activity-icon">
          <Icon size={26} />
        </div>
        <div className="residents1-current-activity-content">
          <h4 className="residents1-current-activity-title">
            {current?.mealName || current?.name || '—'}
          </h4>
          <div className="residents1-current-activity-time">
            <Time size={14} />
            <span>{timeRange}</span>
          </div>
        </div>
      </div>

    </div>
  );
};

// Daily Plan Tab
const DailyPlanTab = ({ resident }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [mealImgUrl, setMealImgUrl] = useState(null);

  const activityTypeColorMap = {
    'nutrition': '#0f62fe', 'meal': '#0f62fe', 'ishrana': '#0f62fe',
    'therapy': '#da1e28', 'terapija': '#da1e28', 'medical': '#da1e28',
    'exercise': '#9fb746', 'vezbe': '#9fb746', 'physical': '#9fb746',
    'social': '#8a3ffc', 'entertainment': '#8a3ffc',
  };
  const getTypeColor = (type) => activityTypeColorMap[(type || '').toLowerCase()] || '#6f6f6f';

  const getStatusStyle = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'completed' || s === 'završeno') return { label: 'Završeno', bg: 'rgba(159,183,70,0.15)', color: '#9fb746' };
    if (s === 'cancelled' || s === 'otkazano') return { label: 'Otkazano', bg: 'rgba(218,30,40,0.15)', color: '#da1e28' };
    return { label: 'Planirano', bg: 'rgba(255,138,0,0.15)', color: '#ff8a00' };
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('sr-Latn-RS', { hour: '2-digit', minute: '2-digit' });
  };

  useEffect(() => {
    if (!resident?.id) return;
    setLoading(true);
    axiosInstance.get(`/api/ResidentActivity/resident/${resident.id}/today`)
      .then(res => setActivities(res.data?.data?.activities || []))
      .catch(() => setError('Greška pri učitavanju dnevnog plana'))
      .finally(() => setLoading(false));
  }, [resident?.id]);

  const openDetail = async (act) => {
    setSelectedActivity(act);
    setMealImgUrl(null);
    setIsDetailOpen(true);
    setDetailLoading(true);
    try {
      const res = await axiosInstance.get(`/api/ResidentActivity/${act.id}`);
      const full = res.data?.data || act;
      setSelectedActivity(full);
      if (full.mealId) {
        axiosInstance.get(`/api/Meal/${full.mealId}`)
          .then(r => setMealImgUrl(r.data?.data?.imgUrl || null))
          .catch(() => {});
      }
    } catch {
      if (act.mealId) {
        axiosInstance.get(`/api/Meal/${act.mealId}`)
          .then(r => setMealImgUrl(r.data?.data?.imgUrl || null))
          .catch(() => {});
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const sorted = [...activities].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  const now = new Date();

  const today = new Date();
  const dayNames = ['Nedelja', 'Ponedeljak', 'Utorak', 'Sreda', 'Četvrtak', 'Petak', 'Subota'];
  const months = ['januar', 'februar', 'mart', 'april', 'maj', 'jun', 'jul', 'avgust', 'septembar', 'oktobar', 'novembar', 'decembar'];
  const todayLabel = `${dayNames[today.getDay()]}, ${today.getDate()}. ${months[today.getMonth()]} ${today.getFullYear()}`;

  return (
    <div className="residents1-tab-content">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: 'var(--r1-text-primary)' }}>
            Dnevni plan
          </h3>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: 'var(--r1-text-secondary)' }}>
            {todayLabel}
          </p>
        </div>
        <div style={{
          padding: '0.25rem 0.875rem',
          borderRadius: '12px',
          backgroundColor: 'rgba(159,183,70,0.12)',
          border: '1px solid rgba(159,183,70,0.35)',
          fontSize: '0.8125rem',
          fontWeight: 600,
          color: '#9fb746'
        }}>
          {activities.length} {activities.length === 1 ? 'aktivnost' : 'aktivnosti'}
        </div>
      </div>

      {loading ? (
        <div className="r1-dailyplan-skeleton" style={{ position: 'relative' }}>
          {/* Vertical timeline line */}
          <div style={{
            position: 'absolute',
            left: '71px',
            top: 0,
            bottom: 0,
            width: '2px',
            backgroundColor: 'var(--r1-border-subtle)',
            zIndex: 0
          }} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} style={{ display: 'flex', gap: '1rem', position: 'relative', zIndex: 1 }}>
                {/* Time column */}
                <div style={{ width: '60px', flexShrink: 0, textAlign: 'right', paddingTop: '0.875rem' }}>
                  <div className="r1-skel-line r1-skel-line--time" />
                </div>

                {/* Timeline dot */}
                <div style={{ width: '24px', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '1rem' }}>
                  <div className="r1-skel-dot" />
                </div>

                {/* Activity card */}
                <div style={{
                  flex: 1,
                  padding: '0.875rem 1rem',
                  borderRadius: '8px',
                  backgroundColor: 'var(--r1-bg-secondary)',
                  border: '1px solid var(--r1-border-subtle)',
                  borderLeft: '4px solid var(--r1-border-subtle)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem'
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="r1-skel-line r1-skel-line--act-title" />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <div className="r1-skel-chip" />
                      <div className="r1-skel-chip" />
                    </div>
                    <div className="r1-skel-line r1-skel-line--act-notes" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : error ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--r1-status-error)' }}>{error}</div>
      ) : sorted.length === 0 ? (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px'
        }}>
          <div style={{
            textAlign: 'center', padding: '3rem 2rem', borderRadius: '12px',
            backgroundColor: 'var(--r1-bg-secondary)',
            border: '1px dashed var(--r1-border-subtle)'
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem', opacity: 0.3 }}>📅</div>
            <div style={{ fontSize: '1rem', color: 'var(--r1-text-secondary)', fontStyle: 'italic' }}>
              Nema aktivnosti zakazanih za danas
            </div>
          </div>
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          {/* Vertical timeline line */}
          <div style={{
            position: 'absolute',
            left: '71px',
            top: 0,
            bottom: 0,
            width: '2px',
            backgroundColor: 'var(--r1-border-subtle)',
            zIndex: 0
          }} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {sorted.map((act, i) => {
              const start = new Date(act.startTime);
              const end = act.endTime ? new Date(act.endTime) : null;
              const isInProgress = start <= now && end && end >= now;
              const isPast = end ? end < now : start < now;
              const isMeal = act.source === 'Meal' || (act.activityType || '').toLowerCase() === 'nutrition';
              const color = getTypeColor(act.activityType);
              const statusStyle = getStatusStyle(act.status);
              const displayName = isMeal && act.mealName ? act.mealName : act.name;

              return (
                <div key={act.id || i} style={{ display: 'flex', gap: '1rem', position: 'relative', zIndex: 1 }}>
                  {/* Time column */}
                  <div style={{
                    width: '60px',
                    flexShrink: 0,
                    textAlign: 'right',
                    paddingTop: '0.875rem'
                  }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: isInProgress ? '#9fb746' : 'var(--r1-text-primary)' }}>
                      {formatTime(act.startTime)}
                    </div>
                    {act.endTime && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--r1-text-secondary)' }}>
                        {formatTime(act.endTime)}
                      </div>
                    )}
                  </div>

                  {/* Timeline dot */}
                  <div style={{
                    width: '24px', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '1rem'
                  }}>
                    <div style={{
                      width: '14px', height: '14px', borderRadius: '50%',
                      backgroundColor: isInProgress ? '#9fb746' : isPast ? 'var(--r1-border-strong)' : color,
                      border: isInProgress ? '3px solid rgba(159,183,70,0.3)' : `2px solid ${isPast ? 'var(--r1-border-subtle)' : color}`,
                      flexShrink: 0,
                      boxShadow: isInProgress ? '0 0 0 4px rgba(159,183,70,0.15)' : 'none',
                      transition: 'all 0.2s ease'
                    }} />
                  </div>

                  {/* Activity card */}
                  <div
                    onClick={() => openDetail(act)}
                    style={{
                      flex: 1,
                      padding: '0.875rem 1rem',
                      borderRadius: '8px',
                      backgroundColor: isInProgress
                        ? 'rgba(159,183,70,0.08)'
                        : 'var(--r1-bg-secondary)',
                      border: isInProgress
                        ? '1px solid rgba(159,183,70,0.35)'
                        : `1px solid var(--r1-border-subtle)`,
                      borderLeft: `4px solid ${isInProgress ? '#9fb746' : isPast ? 'var(--r1-border-strong)' : color}`,
                      opacity: isPast ? 0.65 : 1,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)'; e.currentTarget.style.transform = 'translateX(2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateX(0)'; }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                        <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--r1-text-primary)' }}>
                          {displayName}
                        </span>
                        {isInProgress && (
                          <span style={{ fontSize: '0.6875rem', fontWeight: 600, padding: '0.125rem 0.5rem', borderRadius: '10px', backgroundColor: 'rgba(159,183,70,0.2)', color: '#9fb746' }}>
                            U toku
                          </span>
                        )}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {act.activityType && (
                          <span style={{
                            fontSize: '0.6875rem', fontWeight: 500,
                            padding: '0.125rem 0.5rem', borderRadius: '10px',
                            backgroundColor: `${color}22`, color,
                            border: `1px solid ${color}44`
                          }}>
                            {translateActivityType(act.activityType)}
                          </span>
                        )}
                        <span style={{
                          fontSize: '0.6875rem', fontWeight: 500,
                          padding: '0.125rem 0.5rem', borderRadius: '10px',
                          backgroundColor: statusStyle.bg, color: statusStyle.color
                        }}>
                          {statusStyle.label}
                        </span>
                        {isMeal && act.mealDietType && (
                          <span style={{ fontSize: '0.75rem', color: 'var(--r1-text-secondary)' }}>
                            {act.mealDietType}
                          </span>
                        )}
                      </div>

                      {act.notes && (
                        <p style={{ margin: '0.375rem 0 0', fontSize: '0.8125rem', color: 'var(--r1-text-secondary)', lineHeight: 1.4 }}>
                          {act.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Detail side panel overlay */}
      {isDetailOpen && (
        <div className="side-panel-overlay" onClick={() => setIsDetailOpen(false)} />
      )}
      <div className={`menu-side-panel ${isDetailOpen ? 'open' : ''}`}>
        <div className="side-panel-header">
          <h2>Detalji aktivnosti</h2>
          <Button kind="ghost" size="sm" renderIcon={Close} hasIconOnly onClick={() => setIsDetailOpen(false)} />
        </div>
        {selectedActivity && (
          <div className="side-panel-content" style={{ padding: '1.5rem' }}>
            {detailLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
                <div className="residents1-spinner" style={{ margin: '0 auto' }} />
              </div>
            ) : (
              <>
                {mealImgUrl && (
                  <div style={{
                    width: '100%', height: '200px', borderRadius: '8px',
                    backgroundImage: `url(${mealImgUrl})`,
                    backgroundSize: 'cover', backgroundPosition: 'center',
                    marginBottom: '1.5rem',
                    border: '1px solid var(--cds-border-subtle-01)'
                  }} />
                )}

                <h3 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--cds-text-primary)' }}>
                  {selectedActivity.mealName || selectedActivity.name}
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--cds-text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Vreme
                    </label>
                    <div style={{ fontSize: '1rem', color: '#9fb746', fontWeight: 600 }}>
                      {formatTime(selectedActivity.startTime)}{selectedActivity.endTime ? ` - ${formatTime(selectedActivity.endTime)}` : ''}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--cds-text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Datum
                    </label>
                    <div style={{ fontSize: '1rem' }}>
                      {new Date(selectedActivity.date || selectedActivity.startTime).toLocaleDateString('sr-Latn-RS', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>
                  </div>

                  {selectedActivity.mealDietType && (
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--cds-text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Tip dijete
                      </label>
                      <div style={{ fontSize: '1rem' }}>{selectedActivity.mealDietType}</div>
                    </div>
                  )}

                  {(selectedActivity.activityType || selectedActivity.status) && (
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--cds-text-secondary)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Tip aktivnosti / Status
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        {selectedActivity.activityType && (() => { const c = getTypeColor(selectedActivity.activityType); return (
                          <span style={{ fontSize: '0.8125rem', fontWeight: 500, padding: '0.25rem 0.75rem', borderRadius: '10px', backgroundColor: `${c}22`, color: c, border: `1px solid ${c}44` }}>
                            {translateActivityType(selectedActivity.activityType)}
                          </span>
                        ); })()}
                        {selectedActivity.status && (() => { const s = getStatusStyle(selectedActivity.status); return (
                          <span style={{ fontSize: '0.8125rem', fontWeight: 500, padding: '0.25rem 0.75rem', borderRadius: '10px', backgroundColor: s.bg, color: s.color }}>
                            {s.label}
                          </span>
                        ); })()}
                      </div>
                    </div>
                  )}

                  {selectedActivity.notes && (
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--cds-text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Napomena
                      </label>
                      <div style={{ fontSize: '0.9375rem' }}>{selectedActivity.notes}</div>
                    </div>
                  )}
                </div>

                <Button kind="primary" size="md" style={{ backgroundColor: '#9fb746', borderColor: '#9fb746' }} onClick={() => setIsDetailOpen(false)}>
                  Zatvori
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Personal Info Tab
const PersonalInfoTab = ({ resident, onEditIdentity, onEditAdministrative, onTransferClick, roomData, setNotification, canTransfer = false, canUpdate = false }) => {
  const [showNationalId, setShowNationalId] = useState(false);

  // Groups state
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState(null);
  const [isGroupDetailOpen, setIsGroupDetailOpen] = useState(false);
  const [detailGroup, setDetailGroup] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [groupResidents, setGroupResidents] = useState([]);
  const [groupResidentsLoading, setGroupResidentsLoading] = useState(false);
  const [groupResidentsError, setGroupResidentsError] = useState(null);
  const [residentsPage, setResidentsPage] = useState(1);
  const [residentsPageSize] = useState(5);
  const [residentsPagination, setResidentsPagination] = useState({
    totalCount: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false
  });

  // Remove resident from group state
  const [residentToRemove, setResidentToRemove] = useState(null);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [removeResidentLoading, setRemoveResidentLoading] = useState(false);
  const [removeResidentError, setRemoveResidentError] = useState(null);

  // Helper to get initials for avatar fallback
  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0) : '';
    const last = lastName ? lastName.charAt(0) : '';
    return (first + last).toUpperCase() || '?';
  };

  // Fetch groups for this resident
  const fetchGroups = useCallback(async () => {
    if (!resident?.id) return;
    setGroupsLoading(true);
    setGroupsError(null);
    try {
      const response = await axiosInstance.get(`/api/Group/resident/${resident.id}`);
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data && typeof response.data === 'object') {
        if (Array.isArray(response.data.data)) {
          data = response.data.data;
        } else if (Array.isArray(response.data.$values)) {
          data = response.data.$values;
        } else if (Array.isArray(response.data.value)) {
          data = response.data.value;
        } else if (Array.isArray(response.data.items)) {
          data = response.data.items;
        }
      }
      setGroups(data);
    } catch (err) {
      console.error('Greška pri učitavanju grupa:', err);
      setGroupsError('Greška pri učitavanju grupa');
    } finally {
      setGroupsLoading(false);
    }
  }, [resident?.id]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Open group detail side panel
  const openGroupDetail = async (group) => {
    setDetailGroup(null);
    setDetailError(null);
    setDetailLoading(true);
    setIsGroupDetailOpen(true);

    try {
      const response = await axiosInstance.get(`/api/Group/${group.id}`);
      if (response.data.success && response.data.data) {
        setDetailGroup(response.data.data);
      } else {
        setDetailError('Neispravan format podataka');
      }
    } catch (error) {
      console.error('Error fetching group detail:', error);
      setDetailError(error.response?.data?.message || 'Greška pri učitavanju grupe');
    } finally {
      setDetailLoading(false);
    }

    // Fetch residents in this group
    setResidentsPage(1);
    fetchGroupResidents(group.id, 1);
  };

  const closeGroupDetail = () => {
    setIsGroupDetailOpen(false);
    setDetailGroup(null);
    setDetailError(null);
    setGroupResidents([]);
    setGroupResidentsError(null);
    setResidentsPage(1);
  };

  const fetchGroupResidents = async (groupId, page = residentsPage) => {
    try {
      setGroupResidentsLoading(true);
      setGroupResidentsError(null);
      const params = new URLSearchParams();
      params.append('pageNumber', String(page));
      params.append('pageSize', String(residentsPageSize));
      const response = await axiosInstance.get(`/api/Group/${groupId}/residents?${params.toString()}`);
      console.log('Group residents API Response:', response);

      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data && typeof response.data === 'object') {
        if (Array.isArray(response.data.data)) {
          data = response.data.data;
        } else if (Array.isArray(response.data.$values)) {
          data = response.data.$values;
        } else if (Array.isArray(response.data.value)) {
          data = response.data.value;
        } else if (Array.isArray(response.data.items)) {
          data = response.data.items;
        }
      }
      setGroupResidents(data);

      // Pagination metadata
      const pagination = response.data?.pagination;
      if (pagination) {
        setResidentsPagination({
          totalCount: pagination.totalCount || 0,
          totalPages: pagination.totalPages || 1,
          hasNextPage: pagination.hasNextPage || false,
          hasPreviousPage: pagination.hasPreviousPage || false
        });
      } else {
        setResidentsPagination({
          totalCount: data.length,
          totalPages: Math.ceil(data.length / residentsPageSize) || 1,
          hasNextPage: page < Math.ceil(data.length / residentsPageSize),
          hasPreviousPage: page > 1
        });
      }
    } catch (error) {
      console.error('Error fetching group residents:', error);
      setGroupResidentsError(error.response?.data?.message || 'Greška pri učitavanju korisnika grupe');
    } finally {
      setGroupResidentsLoading(false);
    }
  };

  const nextResidentsPage = () => {
    if (residentsPagination.hasNextPage) {
      const next = residentsPage + 1;
      setResidentsPage(next);
      fetchGroupResidents(detailGroup.id, next);
    }
  };

  const prevResidentsPage = () => {
    if (residentsPagination.hasPreviousPage) {
      const prev = residentsPage - 1;
      setResidentsPage(prev);
      fetchGroupResidents(detailGroup.id, prev);
    }
  };

  const openRemoveResidentModal = (resident) => {
    setResidentToRemove(resident);
    setRemoveResidentError(null);
    setIsRemoveModalOpen(true);
  };

  const handleRemoveResident = async () => {
    if (!residentToRemove || !detailGroup) return;

    setRemoveResidentLoading(true);
    setRemoveResidentError(null);

    try {
      const residentId = residentToRemove.residentId || residentToRemove.id;
      await axiosInstance.delete(`/api/Group/${detailGroup.id}/residents/${residentId}`);

      setNotification({
        kind: 'success',
        title: 'Uspešno!',
        subtitle: 'Korisnik je uspešno uklonjen iz grupe'
      });
      setIsRemoveModalOpen(false);
      setResidentToRemove(null);
      fetchGroupResidents(detailGroup.id, residentsPage);
      fetchGroups();

      setTimeout(() => {
        setNotification(null);
      }, 5000);
    } catch (error) {
      console.error('Error removing resident from group:', error);
      setRemoveResidentError(error.response?.data?.message || 'Greška pri uklanjanju korisnika iz grupe');
    } finally {
      setRemoveResidentLoading(false);
    }
  };

  // Helper to get gender from isWoman field
  const getGender = () => {
    if (resident.gender) return resident.gender;
    if (resident.isWoman === true) return 'Ženski';
    if (resident.isWoman === false) return 'Muški';
    return '-';
  };
  
  // Helper to get language
  const getLanguage = () => {
    const lang = resident.primaryLang || resident.language;
    if (!lang) return { text: '-', flag: null };
    
    const langLower = lang.toLowerCase();
    const capitalizedLang = lang.charAt(0).toUpperCase() + lang.slice(1);
    
    let flag = null;
    if (langLower === 'srpski') {
      flag = <FlagIcons.Serbian />;
    } else if (langLower === 'madjarski' || langLower === 'mađarski') {
      flag = <FlagIcons.Hungarian />;
    } else if (langLower === 'nemacki' || langLower === 'nemački') {
      flag = <FlagIcons.German />;
    } else if (langLower === 'engleski') {
      flag = <FlagIcons.English />;
    }
    
    return { text: capitalizedLang, flag };
  };
  
  // Helper to get national ID (JMBG)
  const getNationalId = () => resident.jmbg || resident.nationalId || '-';
  
  // Helper to get birth date
  const getBirthDate = () => resident.birthDate || resident.dateOfBirth;
  
  // Helper to get admission date
  const getAdmissionDate = () => resident.createdAt || resident.admissionDate;
  
  return (
    <div className="residents1-tab-content">
      <div className="residents1-cards-grid">
        <Card title="Identitet" icon={Icons.User} onEdit={canUpdate ? onEditIdentity : undefined}>
          <div className="residents1-data-list">
            <DataItem label="Ime" value={resident.firstName || '-'} />
            <DataItem label="Prezime" value={resident.lastName || '-'} />
            <DataItem label="Ime roditelja" value={resident.parentName || '-'} />
            <DataItem label="Pol" value={getGender()} />
            <DataItem label="Datum rođenja" value={formatDate(getBirthDate())} />
            <DataItem label="Godine" value={resident.age || '-'} />
            <DataItem label="Primarni jezik" value={getLanguage()} />
            <DataItem label="Državljanstvo" value={resident.citizenship || '-'} />
            <DataItem label="Mesto rođenja" value={resident.placeOfBirth || '-'} />
          </div>
        </Card>
        
        <Card title="Administrativni podaci" icon={Icons.Shield} onEdit={canUpdate ? onEditAdministrative : undefined}>
          <div className="residents1-data-list">
            <DataItem 
              label="Matični broj (JMBG)" 
              value={showNationalId ? getNationalId() : maskNationalId(getNationalId())}
              masked={!showNationalId}
              onReveal={() => setShowNationalId(!showNationalId)}
              isRevealed={showNationalId}
              copyable={true}
              onCopy={() => {
                navigator.clipboard.writeText(getNationalId());
              }}
            />
            <DataItem label="Broj lične karte" value={resident.identityCardNumber || '-'} />
            <DataItem label="Identifikacioni broj" value={resident.identificationNumber || '-'} />
            <div className="residents1-data-item">
              <span className="residents1-data-label">Nivo nege</span>
              <CareLevelTag level={resident.careLevel} />
            </div>
            <DataItem label="Datum prijema" value={formatDate(getAdmissionDate())} />
            <div className="residents1-data-item">
              <span className="residents1-data-label">Status</span>
              <StatusBadge status={resident.isActive ? 'active' : 'inactive'} />
            </div>
          </div>
        </Card>
        
        <Card title="Grupe" icon={Icons.Users}>
          <div className="residents1-groups-list">
            {groupsLoading && (
              <div className="residents1-groups-loading">
                <Loading description="Učitavanje grupa..." withOverlay={false} small />
              </div>
            )}

            {groupsError && !groupsLoading && (
              <InlineNotification
                kind="error"
                title="Greška"
                subtitle={groupsError}
                onCloseButtonClick={() => setGroupsError(null)}
                hideCloseButton={false}
                lowContrast
              />
            )}

            {!groupsLoading && !groupsError && groups.length > 0 && (
              groups.map((group) => (
                <div
                  key={group.id}
                  className="residents1-group-item"
                  onClick={() => openGroupDetail(group)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openGroupDetail(group);
                    }
                  }}
                >
                  <div className="residents1-group-item-icon">
                    <Group size={20} />
                  </div>
                  <div className="residents1-group-item-info">
                    <span className="residents1-group-item-name">{group.name}</span>
                    {group.description && (
                      <span className="residents1-group-item-desc">{group.description}</span>
                    )}
                  </div>
                  {group.isDefault && (
                    <Tag type="blue" size="sm">
                      Podrazumevana
                    </Tag>
                  )}
                  <ArrowRight size={16} className="residents1-group-item-arrow" />
                </div>
              ))
            )}

            {!groupsLoading && !groupsError && groups.length === 0 && (
              <div className="residents1-groups-empty">
                <User size={24} />
                <span>Korisnik nije član nijedne grupe</span>
              </div>
            )}
          </div>
        </Card>
        
        <Card 
          title="Informacije o sobi" 
          icon={Icons.Home}
          actions={
            canTransfer ? (
              <HeaderButton variant="ghost" icon={Icons.ArrowRight} onClick={onTransferClick} title="Premesti">
                Premesti
              </HeaderButton>
            ) : undefined
          }
        >
          <div className="residents1-room-showcase">
            <div className="residents1-room-number">
              <Icons.Home />
              <span className="residents1-room-number-text">{roomData?.name || resident.room?.name || '-'}</span>
            </div>
            <div className="residents1-room-meta">
              <span className="residents1-room-meta-item">
                <Icons.Users />
                {roomData?.residents?.length || resident.room?.roommatesCount || 0} {(roomData?.residents?.length || resident.room?.roommatesCount) === 1 ? 'stanar' : 'stanara'}
              </span>
              {roomData?.area && (
                <span className="residents1-room-meta-item">
                  <Icons.MapPin />
                  {roomData.area}
                </span>
              )}
            </div>
          </div>
          
          {roomData?.residents && roomData.residents.filter(r => r.id !== resident.id).length > 0 && (
            <div style={{ marginTop: 'var(--r1-spacing-05)', paddingTop: 'var(--r1-spacing-05)', borderTop: '1px solid var(--r1-border-subtle)' }}>
              <h4 style={{ fontSize: 'var(--r1-font-size-sm)', fontWeight: 600, marginBottom: 'var(--r1-spacing-04)', color: 'var(--r1-text-secondary)' }}>
                CIMERI
              </h4>
              <div className="residents1-roommates-list">
                {roomData.residents.filter(r => r.id !== resident.id).map((roommate) => (
                  <Link 
                    key={roommate.id} 
                    to={`/residents/${roommate.id}`}
                    className="residents1-roommate-card"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'auto' })}
                  >
                    <div className="residents1-roommate-avatar">
                      {roommate.imgSrc ? (
                        <img src={roommate.imgSrc} alt={roommate.fullName} />
                      ) : (
                        <Icons.User />
                      )}
                    </div>
                    <div className="residents1-roommate-info">
                      <h4 className="residents1-roommate-name">
                        {roommate.fullName || `${roommate.firstName} ${roommate.lastName}`}
                      </h4>
                      <p className="residents1-roommate-meta">
                        {roommate.age && (
                          <>
                            <Icons.Calendar />
                            {roommate.age} godina
                          </>
                        )}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>
      
      {(resident.timeline?.length > 0) && (
        <>
          <h3 style={{ marginTop: '32px', marginBottom: '16px', fontSize: '1.125rem', fontWeight: 600 }}>
            Istorija promena
          </h3>
          <div className="residents1-timeline">
            {resident.timeline.map(item => (
              <TimelineItem key={item.id} item={item} />
            ))}
          </div>
        </>
      )}

      {/* Group Detail Side Panel */}
      <div className={`residents1-group-side-panel ${isGroupDetailOpen ? 'open' : ''}`}>
        <div className="side-panel-header">
          <div className="group-detail-header-title">
            <h2>Detalji grupe</h2>
          </div>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Close}
            hasIconOnly
            onClick={closeGroupDetail}
          />
        </div>
        <div className="side-panel-content">
          {detailLoading && (
            <div className="group-detail-loading">
              <Loading description="Učitavanje grupe..." withOverlay={false} small />
            </div>
          )}

          {detailError && !detailLoading && (
            <InlineNotification
              kind="error"
              title="Greška"
              subtitle={detailError}
              onCloseButtonClick={() => setDetailError(null)}
              hideCloseButton={false}
              lowContrast
            />
          )}

          {detailGroup && !detailLoading && (
            <div className="group-detail">
              {/* Header */}
              <div className="group-detail-header">
                <div className="group-detail-icon">
                  <Group size={32} />
                </div>
                <div className="group-detail-title">
                  <h3>{detailGroup.name}</h3>
                  {detailGroup.isDefault && (
                    <span className="group-detail-default">
                      <CheckmarkFilled size={14} />
                      Podrazumevana grupa
                    </span>
                  )}
                </div>
              </div>

              {/* Info rows */}
              <div className="group-detail-info">
                <div className="group-detail-row">
                  <span className="detail-label">Opis:</span>
                  <span className="detail-value">{detailGroup.description || '-'}</span>
                </div>
              </div>

              {/* Residents */}
              <div className="group-detail-residents">
                <div className="group-detail-section-title group-residents-header">
                  <h4>Korisnici ({groupResidents.length})</h4>
                  <div className="group-residents-pagination">
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={ChevronLeft}
                      onClick={prevResidentsPage}
                      hasIconOnly
                      disabled={!residentsPagination.hasPreviousPage}
                      iconDescription="Prethodna stranica"
                    />
                    <span className="group-residents-page">
                      {residentsPage}/{residentsPagination.totalPages}
                    </span>
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={ChevronRight}
                      onClick={nextResidentsPage}
                      hasIconOnly
                      disabled={!residentsPagination.hasNextPage}
                      iconDescription="Sledeća stranica"
                    />
                  </div>
                </div>

                {groupResidentsLoading && (
                  <div className="group-residents-loading">
                    <Loading description="Učitavanje korisnika..." withOverlay={false} small />
                  </div>
                )}

                {groupResidentsError && !groupResidentsLoading && (
                  <InlineNotification
                    kind="error"
                    title="Greška"
                    subtitle={groupResidentsError}
                    onCloseButtonClick={() => setGroupResidentsError(null)}
                    hideCloseButton={false}
                    lowContrast
                  />
                )}

                {!groupResidentsLoading && !groupResidentsError && groupResidents.length > 0 && (
                  <div className="group-residents-list">
                    {groupResidents.map((resident) => {
                      const initials = getInitials(resident.firstName, resident.lastName);
                      const hasImage = resident.imgSrc && resident.imgSrc !== '';
                      return (
                        <div
                          key={resident.residentId || resident.id}
                          className="group-resident-item"
                        >
                          <div className="group-resident-avatar">
                            {hasImage ? (
                              <>
                                <img
                                  src={resident.imgSrc}
                                  alt={resident.fullName}
                                  className="group-resident-image"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextElementSibling.style.display = 'flex';
                                  }}
                                />
                                <div
                                  className="group-resident-initials"
                                  style={{ display: 'none' }}
                                >
                                  {initials || '?'}
                                </div>
                              </>
                            ) : (
                              <div className="group-resident-initials">
                                {initials || '?'}
                              </div>
                            )}
                          </div>
                          <div className="group-resident-info">
                            <span className="group-resident-name">{resident.fullName}</span>
                            <span className="group-resident-meta">
                              {resident.age !== undefined && resident.age !== null
                                ? `${resident.age} god.`
                                : ''}
                              {resident.roomName ? ` • Soba ${resident.roomName}` : ''}
                            </span>
                          </div>
                          <div className="group-resident-actions">
                            {!detailGroup.isDefault && (
                              <Button
                                kind="ghost"
                                size="sm"
                                renderIcon={TrashCan}
                                hasIconOnly
                                iconDescription="Ukloni iz grupe"
                                className="group-resident-remove-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openRemoveResidentModal(resident);
                                }}
                              />
                            )}
                            <ArrowRight size={16} className="group-resident-arrow" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {!groupResidentsLoading && !groupResidentsError && groupResidents.length === 0 && (
                  <div className="group-residents-empty">
                    <User size={24} />
                    <span>Nema korisnika u ovoj grupi</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Group Detail Panel Overlay */}
      {isGroupDetailOpen && (
        <div
          className="side-panel-overlay"
          onClick={closeGroupDetail}
        />
      )}

      {/* Remove Resident Confirmation Modal */}
      <Modal
        open={isRemoveModalOpen}
        onRequestClose={() => setIsRemoveModalOpen(false)}
        modalHeading="Ukloni korisnika iz grupe"
        primaryButtonText={removeResidentLoading ? 'Uklanjanje...' : 'Ukloni'}
        secondaryButtonText="Otkaži"
        primaryButtonDisabled={removeResidentLoading}
        onRequestSubmit={handleRemoveResident}
        danger
      >
        <p>
          Da li ste sigurni da želite da uklonite korisnika{' '}
          <strong>{residentToRemove?.fullName}</strong> iz grupe{' '}
          <strong>{detailGroup?.name}</strong>?
        </p>
        {removeResidentError && (
          <div style={{ marginTop: '0.75rem', color: '#da1e28', fontSize: '0.8125rem' }}>
            {removeResidentError}
          </div>
        )}
      </Modal>
    </div>
  );
};

// Documents Tab
const getDocumentFileType = (name) => {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (['pdf'].includes(ext)) return 'pdf';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return 'image';
  if (['doc', 'docx'].includes(ext)) return 'word';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'excel';
  if (['txt'].includes(ext)) return 'text';
  return 'other';
};

const getDocumentTypeLabel = (type) => {
  switch (type) {
    case 'pdf': return 'PDF';
    case 'image': return 'Slika';
    case 'word': return 'Word';
    case 'excel': return 'Excel';
    case 'text': return 'Tekst';
    default: return 'Dokument';
  }
};

const DocumentsTab = ({
  documents,
  loading,
  error,
  pagination,
  onPageChange,
  onUpload,
  onDownload,
  onDelete,
  onPreview,
  currentUserId,
  isAdmin,
  canUpload = true,
  canView = true
}) => {
  if (loading) {
    return (
      <div className="residents1-tab-content">
        {/* Toolbar skeleton */}
        <div className="residents1-documents-toolbar">
          <div className="residents1-documents-toolbar__info">
            <div className="r1-skel-line r1-skel-line--doc-count" />
          </div>
          <div className="r1-skel-line r1-skel-line--doc-btn" />
        </div>

        {/* Documents grid skeleton */}
        <div className="residents1-documents-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div className="r1-skel-doc-card" key={i}>
              <div className="r1-skel-doc-icon" />
              <div className="r1-skel-line r1-skel-line--doc-type" />
              <div className="r1-skel-line r1-skel-line--doc-name" />
              <div className="r1-skel-line r1-skel-line--doc-meta" />
              <div className="r1-skel-doc-footer">
                <div className="r1-skel-line r1-skel-line--doc-btn-sm" />
                <div className="r1-skel-line r1-skel-line--doc-btn-sm" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="residents1-tab-content">
        <div className="residents1-empty-state">
          <div className="residents1-empty-icon"><Icons.AlertCircle /></div>
          <h3 className="residents1-empty-title">Greška pri učitavanju</h3>
          <p className="residents1-empty-description">{error}</p>
          <Button kind="primary" onClick={() => onPageChange(pagination?.pageNumber || 1)} style={{ marginTop: '1rem' }}>
            <Icons.Refresh />
            <span style={{ marginLeft: '0.5rem' }}>Pokušaj ponovo</span>
          </Button>
        </div>
      </div>
    );
  }

  // Bez dozvole `documents.view` se GET endpoint ne poziva, pa ovaj tab
  // prikazuje jasnu poruku umesto prazne liste.
  if (!canView) {
    return (
      <div className="residents1-tab-content">
        <div className="residents1-empty-state">
          <div className="residents1-empty-icon"><Icons.FileText /></div>
          <h3 className="residents1-empty-title">Nemate dozvolu za pregled dokumenata</h3>
          <p className="residents1-empty-description">
            Za pregled dokumenata korisnika potrebna je dozvola „Pregled dokumenata".
          </p>
        </div>
      </div>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <div className="residents1-tab-content">
        <div className="residents1-empty-state">
          <div className="residents1-empty-icon"><Icons.FileText /></div>
          <h3 className="residents1-empty-title">Nema dokumenata</h3>
          <p className="residents1-empty-description">
            Trenutno nema evidentiranih dokumenata za ovog stanara.
          </p>
          {canUpload && (
            <Button kind="primary" onClick={onUpload} style={{ marginTop: '1rem' }}>
              <Icons.Upload />
              <span style={{ marginLeft: '0.5rem' }}>Dodaj dokument</span>
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="residents1-tab-content">
      <div className="residents1-documents-toolbar">
        <div className="residents1-documents-toolbar__info">
          {pagination?.totalCount != null && (
            <span className="residents1-documents-count">
              {pagination.totalCount} {pagination.totalCount === 1 ? 'dokument' : 'dokumenata'}
            </span>
          )}
        </div>
        {pagination && (
          <div className="residents1-documents-pagination">
            <button
              className="residents1-notes-pagination__btn"
              onClick={() => onPageChange(pagination.pageNumber - 1)}
              disabled={!pagination.hasPreviousPage || loading}
              aria-label="Prethodna strana"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="residents1-notes-pagination__info">{pagination.pageNumber} / {pagination.totalPages}</span>
            <button
              className="residents1-notes-pagination__btn"
              onClick={() => onPageChange(pagination.pageNumber + 1)}
              disabled={!pagination.hasNextPage || loading}
              aria-label="Sledeća strana"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
        {canUpload && (
          <Button kind="primary" onClick={onUpload}>
            <Icons.Upload />
            <span style={{ marginLeft: '0.5rem' }}>Dodaj dokument</span>
          </Button>
        )}
      </div>

      <div className="residents1-documents-grid">
        {documents.map(doc => {
          const fileType = getDocumentFileType(doc.documentName);
          // Only administrators may delete documents uploaded by other users.
          // Everyone else may delete only the documents they uploaded themselves.
          const isOwnDocument = currentUserId && doc.uploadedByUserId &&
            String(doc.uploadedByUserId).toLowerCase() === String(currentUserId).toLowerCase();
          const canDelete = isAdmin || isOwnDocument;
          return (
            <div
              key={doc.id}
              className="residents1-document-card"
              tabIndex={0}
              onClick={() => onPreview(doc)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPreview(doc); } }}
              role="button"
              aria-label={`Pregledaj ${doc.documentName}`}
            >
              {canDelete && (
                <div className="residents1-document-card__actions">
                  <button
                    className="residents1-document-action-btn residents1-document-action-btn--danger"
                    onClick={(e) => { e.stopPropagation(); onDelete(doc); }}
                    aria-label={`Obriši ${doc.documentName}`}
                    title="Obriši dokument"
                  >
                    <TrashCan />
                  </button>
                </div>
              )}
              <div className={`residents1-document-icon residents1-document-icon--${fileType}`}>
                <Icons.FileText />
              </div>
              <span className={`residents1-document-type residents1-document-type--${fileType}`}>
                {getDocumentTypeLabel(fileType)}
              </span>
              <h4 className="residents1-document-name" title={doc.documentName}>{doc.documentName}</h4>
              <span className="residents1-document-meta">{formatDate(doc.createdAt)}</span>
              <div className="residents1-document-card__footer">
                <button
                  className="residents1-document-download"
                  onClick={(e) => { e.stopPropagation(); onDownload(doc); }}
                  aria-label={`Preuzmi ${doc.documentName}`}
                  title="Preuzmi dokument"
                >
                  <Icons.Download />
                  <span>Preuzmi</span>
                </button>
                <button
                  className="residents1-document-preview-btn"
                  onClick={(e) => { e.stopPropagation(); onPreview(doc); }}
                  aria-label={`Pregledaj ${doc.documentName}`}
                  title="Pregledaj dokument"
                >
                  <Icons.Eye />
                  <span>Pregledaj</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
};

const getActivityTypeColor = (type) => {
  switch ((type || '').toLowerCase()) {
    case 'meal': case 'ishrana': case 'nutrition': return '#0f62fe';
    case 'therapy': case 'terapija': case 'medical': return '#da1e28';
    case 'exercise': case 'vezbe': case 'sport': case 'physical': return '#9fb746';
    case 'social': case 'socijalne': case 'entertainment': return '#8a3ffc';
    case 'visit': case 'poseta': return '#0f62fe';
    default: return '#6f6f6f';
  }
};

// Translate activity type values to Serbian for display
const translateActivityType = (type) => {
  switch ((type || '').toLowerCase()) {
    case 'nutrition': case 'meal': case 'ishrana': return 'Ishrana';
    case 'physical': case 'exercise': case 'vezbe': case 'sport': return 'Fizička aktivnost';
    case 'medical': case 'medicinska': return 'Medicinska aktivnost';
    case 'nursing': case 'nega': return 'Nega';
    case 'visit': case 'poseta': return 'Poseta';
    case 'entertainment': case 'zabava': return 'Zabava';
    default: return type;
  }
};

const AddActivitySidebar = ({ isOpen, onClose, resident, initialDate, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [stepTransition, setStepTransition] = useState('entering');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const stepTransitionTimeout = useRef(null);

  // Step 1 - Activity selection (search + paging)
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(6);
  const [pageFading, setPageFading] = useState(false);

  // Step 2 - Supply selection (optional, only for medical activities)
  const [supplies, setSupplies] = useState([]);
  const [suppliesLoading, setSuppliesLoading] = useState(false);
  const [selectedSupply, setSelectedSupply] = useState(null);

  // Step 3 - Supply quantity consumed by the activity
  const [supplyQuantity, setSupplyQuantity] = useState(1);

  // Step 4 - Summary / details
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [notes, setNotes] = useState('');
  const [repeatFrequency, setRepeatFrequency] = useState('none');
  const [repeatDays, setRepeatDays] = useState([]);
  const [taskCreation, setTaskCreation] = useState(false);
  const [severity, setSeverity] = useState(3);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const isMedicalType = (type) => {
    const t = (type || '').toLowerCase();
    return t === 'medical' || t === 'nursing' || t === 'therapy';
  };

  const formatDateForInput = (d) => {
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  };

  // Weekday helpers (payload uses lowercase English day names)
  const WEEKDAYS = [
    { value: 'monday', label: 'Ponedeljak' },
    { value: 'tuesday', label: 'Utorak' },
    { value: 'wednesday', label: 'Sreda' },
    { value: 'thursday', label: 'Četvrtak' },
    { value: 'friday', label: 'Petak' },
    { value: 'saturday', label: 'Subota' },
    { value: 'sunday', label: 'Nedelja' },
  ];

  // Returns the lowercase English weekday name for a yyyy-MM-dd date string
  const getWeekdayFromDate = (dateStr) => {
    if (!dateStr) return null;
    const [yr, mo, dy] = dateStr.split('-').map(Number);
    const dt = new Date(yr, mo - 1, dy);
    return WEEKDAYS[dt.getDay() === 0 ? 6 : dt.getDay() - 1].value;
  };

  const handleDateChange = (value) => {
    setDate(value);
    const weekday = getWeekdayFromDate(value);
    if (weekday) {
      // Auto-check the weekday matching the selected date; it cannot be unchecked
      setRepeatDays(prev => (prev.includes(weekday) ? prev : [...prev, weekday]));
    }
  };

  const toggleRepeatDay = (dayValue) => {
    const weekday = getWeekdayFromDate(date);
    // The day matching the selected date is locked and cannot be unchecked
    if (dayValue === weekday) return;
    setRepeatDays(prev =>
      prev.includes(dayValue) ? prev.filter(d => d !== dayValue) : [...prev, dayValue]
    );
  };

  const fetchActivities = async () => {
    setActivitiesLoading(true);
    try {
      const res = await axiosInstance.get('/api/Activity');
      setActivities(res.data?.data || []);
    } catch {
      setActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const fetchSupplies = async () => {
    if (!resident?.id) return;
    setSuppliesLoading(true);
    try {
      const res = await axiosInstance.get(`/api/ResidentSupply/resident/${resident.id}?pageNumber=1&pageSize=50`);
      setSupplies(res.data?.data || []);
    } catch {
      setSupplies([]);
    } finally {
      setSuppliesLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setStepTransition('entering');
      setSelectedActivity(null);
      setSelectedSupply(null);
      setSupplyQuantity(1);
      setSearchTerm('');
      setPage(1);
      setDate(formatDateForInput(initialDate || new Date()));
      setStartTime('09:00');
      setEndTime('10:00');
      setNotes('');
      setRepeatFrequency('none');
      setRepeatDays([]);
      setTaskCreation(false);
      setSeverity(3);
      setError(null);
      fetchActivities();
    }
  }, [isOpen, initialDate]);

  const goToStep = (step) => {
    setStepTransition('exiting');
    setTimeout(() => {
      setCurrentStep(step);
      setStepTransition('entering');
    }, 150);
  };

  const handleStepClick = (targetStep) => {
    // Allow going back
    if (targetStep < currentStep) {
      goToStep(targetStep);
      return;
    }
    if (targetStep === currentStep) return;

    // Going forward
    if (targetStep > currentStep + 1) {
      setError('Morate završiti trenutni korak pre nego što nastavite dalje.');
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Step 1 requires a selected activity
    if (targetStep === 1 && !selectedActivity) {
      setError('Prvo morate izabrati aktivnost.');
      setTimeout(() => setError(null), 3000);
      return;
    }
    // Step 2 requires a selected activity (medical supply step)
    if (targetStep === 2 && !selectedActivity) {
      setError('Prvo morate izabrati aktivnost.');
      setTimeout(() => setError(null), 3000);
      return;
    }
    // Step 3 requires a selected activity
    if (targetStep === 3 && !selectedActivity) {
      setError('Prvo morate izabrati aktivnost.');
      setTimeout(() => setError(null), 3000);
      return;
    }
    // Step 4 requires a selected activity and a date
    if (targetStep === 4 && (!selectedActivity || !date)) {
      setError('Prvo morate izabrati aktivnost i datum.');
      setTimeout(() => setError(null), 3000);
      return;
    }

    goToStep(targetStep);
  };

  const handleActivitySelect = (activity) => {
    if (isTransitioning) return;
    const isDeselecting = selectedActivity?.id === activity.id;
    setSelectedActivity(isDeselecting ? null : activity);
    if (isDeselecting) {
      if (stepTransitionTimeout.current) {
        clearTimeout(stepTransitionTimeout.current);
        stepTransitionTimeout.current = null;
      }
      return;
    }
    if (activity.duration) {
      const [h, m] = startTime.split(':').map(Number);
      const totalMins = h * 60 + m + activity.duration;
      setEndTime(`${String(Math.floor(totalMins / 60) % 24).padStart(2, '0')}:${String(totalMins % 60).padStart(2, '0')}`);
    }
    // Load supplies when a medical activity is selected
    if (isMedicalType(activity.activityType)) {
      fetchSupplies();
    }
    // Auto-advance to the next step after a short delay
    stepTransitionTimeout.current = setTimeout(() => {
      setIsTransitioning(true);
      setStepTransition('exiting');
      setTimeout(() => {
        setCurrentStep(1);
        setStepTransition('entering');
        setIsTransitioning(false);
      }, 150);
    }, 1000);
  };

  const handleSupplySelect = (supply) => {
    if (isTransitioning) return;
    const isDeselecting = selectedSupply?.id === supply.id;
    setSelectedSupply(isDeselecting ? null : supply);
    if (isDeselecting) {
      if (stepTransitionTimeout.current) {
        clearTimeout(stepTransitionTimeout.current);
        stepTransitionTimeout.current = null;
      }
      return;
    }
    stepTransitionTimeout.current = setTimeout(() => {
      setIsTransitioning(true);
      setStepTransition('exiting');
      setTimeout(() => {
        setCurrentStep(2);
        setStepTransition('entering');
        setIsTransitioning(false);
      }, 150);
    }, 1000);
  };

  const nextPage = () => {
    const totalPages = Math.ceil(filteredActivities.length / pageSize);
    if (page < totalPages) {
      setPageFading(true);
      setTimeout(() => {
        setPage(prev => prev + 1);
        setPageFading(false);
      }, 300);
    }
  };

  const prevPage = () => {
    if (page > 1) {
      setPageFading(true);
      setTimeout(() => {
        setPage(prev => prev - 1);
        setPageFading(false);
      }, 300);
    }
  };

  const handleSubmit = async () => {
    if (!selectedActivity || !date) return;
    setIsSaving(true);
    setError(null);
    try {
      const [yr, mo, dy] = date.split('-').map(Number);
      const [sh, sm] = startTime.split(':').map(Number);
      const [eh, em] = endTime.split(':').map(Number);
      const startDT = new Date(yr, mo - 1, dy, sh, sm, 0, 0);
      const endDT = new Date(yr, mo - 1, dy, eh, em, 0, 0);
      // Build repeat payload: weekly sends selected days, daily overrides with empty list
      let repeatDaysPayload = [];
      if (repeatFrequency === 'weekly') {
        repeatDaysPayload = repeatDays;
      } else if (repeatFrequency === 'daily') {
        repeatDaysPayload = [];
      }

      await axiosInstance.post('/api/ResidentActivity', {
        residentId: resident.id,
        name: selectedActivity.name,
        date, // plain yyyy-MM-dd (DateOnly) — no timezone shift
        startTime: startDT.toISOString(),
        endTime: endDT.toISOString(),
        activityType: selectedActivity.activityType,
        notes,
        status: 'planned',
        activityId: selectedActivity.id,
        residentSupplyId: selectedSupply?.id || null,
        quantityConsumed: isMedicalType(selectedActivity.activityType) && selectedSupply ? Number(supplyQuantity) : 0,
        description: notes || null,
        repeatFrequency,
        repeatDays: repeatDaysPayload,
        taskCreation,
        severity
      });
      onSuccess();
      onClose();
    } catch {
      setError('Greška pri dodavanju aktivnosti. Pokušajte ponovo.');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredActivities = activities.filter(act =>
    !searchTerm || act.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.ceil(filteredActivities.length / pageSize);
  const start = (page - 1) * pageSize;
  const paginatedActivities = filteredActivities.slice(start, start + pageSize);

  const inputStyle = {
    width: '100%',
    height: '2.5rem',
    padding: '0 1rem',
    border: '1px solid var(--cds-border-strong-01)',
    backgroundColor: 'var(--cds-field-01)',
    color: 'var(--cds-text-primary)',
    fontSize: '0.875rem',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const steps = [
    { label: 'Aktivnost', description: selectedActivity ? selectedActivity.name : '' },
    { label: 'Zaliha', description: selectedSupply ? selectedSupply.itemName : '' },
    { label: 'Količina', description: selectedSupply ? `${supplyQuantity}` : '' },
    { label: 'Detalji', description: '' },
    { label: 'Potvrda', description: '' }
  ];

  return (
    <>
      {isOpen && <div className="edit-sidebar-overlay" onClick={onClose} />}
      <div className={`edit-sidebar add-activity-sidebar ${isOpen ? 'open' : ''}`} style={{ width: '440px' }}>
        <div className="edit-sidebar__header">
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500' }}>Dodaj aktivnost</h3>
          <Button kind="ghost" size="sm" renderIcon={Close} hasIconOnly iconDescription="Zatvori" onClick={onClose} />
        </div>

        {/* Progress Indicator */}
        <div style={{
          padding: '1rem 1.5rem 0.75rem',
          borderBottom: '1px solid var(--cds-border-subtle)',
          backgroundColor: 'var(--cds-layer-01)'
        }}>
          <div className="custom-progress-bar">
            <div className="progress-steps">
              {steps.map((step, idx) => (
                <React.Fragment key={idx}>
                  <div
                    className={`progress-step ${currentStep >= idx ? 'completed' : ''} ${currentStep === idx ? 'active' : ''}`}
                    onClick={() => handleStepClick(idx)}
                    style={{ cursor: idx <= currentStep ? 'pointer' : 'default' }}
                  >
                    <div className="step-circle">
                      {currentStep > idx ? <Checkmark size={16} /> : idx + 1}
                    </div>
                    <div className="step-label">
                      <div className="step-title">{step.label}</div>
                      {currentStep > idx && step.description && (
                        <div className="step-description">{step.description}</div>
                      )}
                    </div>
                  </div>
                  {idx < steps.length - 1 && (
                    <div className={`progress-line ${currentStep > idx ? 'completed' : ''}`}></div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <div className="edit-sidebar__content">
          {error && (
            <div style={{ color: '#da1e28', fontSize: '0.8125rem', padding: '0.5rem 0.75rem', backgroundColor: 'rgba(218,30,40,0.08)', borderRadius: '4px', borderLeft: '3px solid #da1e28', marginBottom: '0.5rem' }}>
              {error}
            </div>
          )}

          {/* Step 0 - Activity selection */}
          {currentStep === 0 && (
            <div className={`step-content ${stepTransition}`}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Search
                  size="md"
                  placeholder="Pretraži aktivnosti..."
                  labelText="Pretraga"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                  onClear={() => { setSearchTerm(''); setPage(1); }}
                  style={{ flex: 1 }}
                />
                {filteredActivities.length > pageSize && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Button
                      kind="ghost"
                      size="md"
                      renderIcon={ChevronLeft}
                      onClick={prevPage}
                      hasIconOnly
                      disabled={page === 1}
                    />
                    <span style={{ fontSize: '0.875rem', opacity: 0.8, minWidth: '60px', textAlign: 'center', fontWeight: '500' }}>
                      {page}/{totalPages}
                    </span>
                    <Button
                      kind="ghost"
                      size="md"
                      renderIcon={ChevronRight}
                      onClick={nextPage}
                      hasIconOnly
                      disabled={page === totalPages}
                    />
                  </div>
                )}
              </div>

              {activitiesLoading ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                  {[...Array(6)].map((_, idx) => (
                    <SkeletonPlaceholder key={idx} style={{ height: '80px', borderRadius: '6px' }} />
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '0.5rem',
                    opacity: pageFading ? 0 : 1,
                    transition: 'opacity 0.3s ease'
                  }}
                >
                  {filteredActivities.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem 1rem', color: 'var(--cds-text-secondary)' }}>
                      {searchTerm ? 'Nema aktivnosti koje odgovaraju pretrazi' : 'Nema dostupnih aktivnosti'}
                    </div>
                  ) : (
                    paginatedActivities.map(act => {
                      const isSelected = selectedActivity?.id === act.id;
                      const typeColor = getActivityTypeColor(act.activityType);
                      return (
                        <div
                          key={act.id}
                          onClick={() => handleActivitySelect(act)}
                          style={{
                            padding: '0.625rem',
                            borderRadius: '6px',
                            border: `2px solid ${isSelected ? '#9fb746' : 'var(--cds-border-subtle-00, rgba(0,0,0,0.08))'}`,
                            borderLeft: `4px solid ${typeColor}`,
                            backgroundColor: isSelected ? 'rgba(159,183,70,0.1)' : 'var(--cds-layer-02)',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            position: 'relative'
                          }}
                        >
                          <div style={{ fontSize: '0.8125rem', fontWeight: '600', marginBottom: '0.25rem', paddingRight: isSelected ? '1.25rem' : 0 }}>
                            {act.name}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.65rem', color: typeColor, fontWeight: '500' }}>{translateActivityType(act.activityType)}</span>
                            {act.duration && (
                              <span style={{ fontSize: '0.65rem', color: 'var(--cds-text-secondary)' }}>{act.duration} min</span>
                            )}
                          </div>
                          {isSelected && (
                            <span style={{ position: 'absolute', top: '4px', right: '6px', color: '#9fb746', fontWeight: '700', fontSize: '0.875rem' }}>✓</span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <Button kind="secondary" onClick={onClose} style={{ flex: 1 }}>Otkaži</Button>
                <Button kind="primary" onClick={() => handleStepClick(1)} style={{ flex: 1 }} disabled={!selectedActivity}>Dalje</Button>
              </div>
            </div>
          )}

          {/* Step 1 - Supply selection (optional, medical only) */}
          {currentStep === 1 && (
            <div className={`step-content ${stepTransition}`}>
              {isMedicalType(selectedActivity?.activityType) ? (
                <>
                  <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)', margin: 0 }}>
                    Opciono izaberite zalihu koja se troši ovom aktivnošću.
                  </p>
                  {suppliesLoading ? (
                    <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--cds-text-secondary)' }}>Učitavanje zaliha...</div>
                  ) : supplies.length === 0 ? (
                    <div style={{ padding: '1.5rem 1rem', textAlign: 'center', color: 'var(--cds-text-secondary)', backgroundColor: 'var(--cds-layer-02)', borderRadius: '6px' }}>
                      Nema dostupnih zaliha za ovog korisnika.
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', maxHeight: '300px', overflowY: 'auto', paddingRight: '2px' }}>
                      {supplies.map(supply => {
                        const isSelected = selectedSupply?.id === supply.id;
                        return (
                          <div
                            key={supply.id}
                            onClick={() => handleSupplySelect(supply)}
                            style={{
                              padding: '0.625rem',
                              borderRadius: '6px',
                              border: `2px solid ${isSelected ? '#9fb746' : 'var(--cds-border-subtle-00, rgba(0,0,0,0.08))'}`,
                              backgroundColor: isSelected ? 'rgba(159,183,70,0.1)' : 'var(--cds-layer-02)',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              position: 'relative'
                            }}
                          >
                            <div style={{ fontSize: '0.8125rem', fontWeight: '600', marginBottom: '0.25rem', paddingRight: isSelected ? '1.25rem' : 0 }}>
                              {supply.itemName}
                            </div>
                            <div style={{ fontSize: '0.65rem', color: 'var(--cds-text-secondary)' }}>
                              Količina: {supply.quantity ?? '-'}
                            </div>
                            {isSelected && (
                              <span style={{ position: 'absolute', top: '4px', right: '6px', color: '#9fb746', fontWeight: '700', fontSize: '0.875rem' }}>✓</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ padding: '1.5rem 1rem', textAlign: 'center', color: 'var(--cds-text-secondary)', backgroundColor: 'var(--cds-layer-02)', borderRadius: '6px' }}>
                  Ova aktivnost ne zahteva izbor zalihe.
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <Button kind="secondary" onClick={() => goToStep(0)} style={{ flex: 1 }}>Nazad</Button>
                <Button kind="primary" onClick={() => goToStep(2)} style={{ flex: 1 }}>Dalje</Button>
              </div>
            </div>
          )}

          {/* Step 2 - Supply quantity (medical only) */}
          {currentStep === 2 && (
            <div className={`step-content ${stepTransition}`}>
              {isMedicalType(selectedActivity?.activityType) ? (
                <>
                  <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)', margin: 0 }}>
                    Unesite količinu zalihe koja se troši ovom aktivnošću.
                  </p>
                  {selectedSupply ? (
                    <div style={{ padding: '1rem', backgroundColor: 'var(--cds-layer-02)', borderRadius: '8px', marginBottom: '0.5rem' }}>
                      <p style={{ margin: 0, fontSize: '0.875rem' }}>
                        <strong>Zaliha:</strong> {selectedSupply.itemName}
                      </p>
                      <p style={{ margin: '0.25rem 0 0', fontSize: '0.8125rem', color: 'var(--cds-text-secondary)' }}>
                        Dostupna količina: {selectedSupply.quantity ?? '-'}
                      </p>
                    </div>
                  ) : (
                    <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--cds-text-secondary)', backgroundColor: 'var(--cds-layer-02)', borderRadius: '6px' }}>
                      Nije izabrana zaliha.
                    </div>
                  )}

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: '400', color: 'var(--cds-text-secondary, #525252)' }}>
                      Količina koja se troši *
                    </label>
                    <input
                      type="number"
                      min="1"
                      max={selectedSupply?.quantity ?? undefined}
                      value={supplyQuantity}
                      onChange={e => setSupplyQuantity(e.target.value)}
                      style={inputStyle}
                      disabled={!selectedSupply || isSaving}
                    />
                  </div>
                </>
              ) : (
                <div style={{ padding: '1.5rem 1rem', textAlign: 'center', color: 'var(--cds-text-secondary)', backgroundColor: 'var(--cds-layer-02)', borderRadius: '6px' }}>
                  Ova aktivnost ne zahteva količinu zalihe.
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <Button kind="secondary" onClick={() => goToStep(1)} style={{ flex: 1 }}>Nazad</Button>
                <Button kind="primary" onClick={() => goToStep(3)} style={{ flex: 1 }}>Dalje</Button>
              </div>
            </div>
          )}

          {/* Step 3 - Details (date, time, repeat, notes) */}
          {currentStep === 3 && (
            <div className={`step-content ${stepTransition}`}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Detalji aktivnosti</h3>

              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: '400', color: 'var(--cds-text-secondary, #525252)' }}>
                  Datum *
                </label>
                <input type="date" value={date} onChange={e => handleDateChange(e.target.value)} style={inputStyle} required />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: '400', color: 'var(--cds-text-secondary, #525252)' }}>
                    Vreme početka *
                  </label>
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    <Select
                      id="start-hour"
                      labelText=""
                      hideLabel
                      value={startTime.split(':')[0]}
                      onChange={e => {
                        const newHour = e.target.value;
                        setStartTime(`${newHour}:${startTime.split(':')[1]}`);
                        // Automatically move the end hour to match the new start hour
                        setEndTime(`${newHour}:${endTime.split(':')[1]}`);
                      }}
                      disabled={isSaving}
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i).padStart(2, '0')} text={String(i).padStart(2, '0')} />
                      ))}
                    </Select>
                    <Select
                      id="start-minute"
                      labelText=""
                      hideLabel
                      value={startTime.split(':')[1]}
                      onChange={e => setStartTime(`${startTime.split(':')[0]}:${e.target.value}`)}
                      disabled={isSaving}
                    >
                      {['00','05','10','15','20','25','30','35','40','45','50','55'].map(m => (
                        <SelectItem key={m} value={m} text={m} />
                      ))}
                    </Select>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: '400', color: 'var(--cds-text-secondary, #525252)' }}>
                    Vreme kraja
                  </label>
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    <Select
                      id="end-hour"
                      labelText=""
                      hideLabel
                      value={endTime.split(':')[0]}
                      onChange={e => setEndTime(`${e.target.value}:${endTime.split(':')[1]}`)}
                      disabled={isSaving}
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i).padStart(2, '0')} text={String(i).padStart(2, '0')} />
                      ))}
                    </Select>
                    <Select
                      id="end-minute"
                      labelText=""
                      hideLabel
                      value={endTime.split(':')[1]}
                      onChange={e => setEndTime(`${endTime.split(':')[0]}:${e.target.value}`)}
                      disabled={isSaving}
                    >
                      {['00','05','10','15','20','25','30','35','40','45','50','55'].map(m => (
                        <SelectItem key={m} value={m} text={m} />
                      ))}
                    </Select>
                  </div>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: '400', color: 'var(--cds-text-secondary, #525252)' }}>
                  Ponavljanje
                </label>
                <Select
                  id="act-repeat-frequency"
                  labelText=""
                  hideLabel
                  value={repeatFrequency}
                  onChange={e => setRepeatFrequency(e.target.value)}
                  disabled={isSaving}
                >
                  <SelectItem value="none" text="Ne ponavlja se" />
                  <SelectItem value="daily" text="Svakodnevno" />
                  <SelectItem value="everyOtherDay" text="Svaki drugi dan" />
                  <SelectItem value="everyThirdDay" text="Svaki treći dan" />
                  <SelectItem value="weekly" text="Nedeljno" />
                  <SelectItem value="everyOtherWeek" text="Svake druge nedelje" />
                </Select>
              </div>

              <div>
                <Toggle
                  id="act-task-creation"
                  labelText="Kreiraj Zadatak"
                  labelA="Ne"
                  labelB="Da"
                  toggled={taskCreation}
                  onToggle={(checked) => setTaskCreation(checked)}
                  disabled={isSaving}
                />
                {taskCreation && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: '400', color: 'var(--cds-text-secondary, #525252)' }}>
                      Nivo ozbiljnosti (severity)
                    </label>
                    <Select
                      id="act-severity"
                      labelText=""
                      hideLabel
                      value={severity}
                      onChange={e => setSeverity(Number(e.target.value))}
                      disabled={isSaving}
                    >
                      <SelectItem value={1} text="1 - Veoma važno" />
                      <SelectItem value={2} text="2 - Važno" />
                      <SelectItem value={3} text="3 - Normalno" />
                    </Select>
                  </div>
                )}
              </div>

              {repeatFrequency === 'weekly' && (
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.75rem', fontWeight: '400', color: 'var(--cds-text-secondary, #525252)' }}>
                    Dani u nedelji
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {WEEKDAYS.map(day => {
                      const isLocked = day.value === getWeekdayFromDate(date);
                      const isChecked = repeatDays.includes(day.value);
                      return (
                        <Checkbox
                          key={day.value}
                          id={`repeat-day-${day.value}`}
                          labelText={day.label}
                          checked={isChecked}
                          disabled={isLocked}
                          onChange={() => toggleRepeatDay(day.value)}
                        />
                      );
                    })}
                  </div>
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: 'var(--cds-text-secondary, #525252)' }}>
                    Dan koji odgovara izabranom datumu je automatski označen i ne može se isključiti dok ne promenite datum.
                  </p>
                </div>
              )}

              <TextArea
                id="act-notes"
                labelText="Napomena (opciono)"
                placeholder="Unesite napomenu..."
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={3}
                disabled={isSaving}
              />

              <div style={{ display: 'flex', gap: '1rem' }}>
                <Button kind="secondary" onClick={() => goToStep(2)} style={{ flex: 1 }} disabled={isSaving}>Nazad</Button>
                <Button kind="primary" onClick={() => goToStep(4)} style={{ flex: 1 }} disabled={isSaving || !date}>Dalje</Button>
              </div>
            </div>
          )}

          {/* Step 4 - Confirmation */}
          {currentStep === 4 && (
            <div className={`step-content ${stepTransition}`}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Potvrdi dodavanje</h3>
              <div style={{ padding: '1rem', backgroundColor: 'var(--cds-layer-02)', borderRadius: '8px' }}>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Aktivnost:</strong> {selectedActivity?.name}
                </p>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Tip aktivnosti:</strong> {selectedActivity ? translateActivityType(selectedActivity.activityType) : '-'}
                </p>
                {isMedicalType(selectedActivity?.activityType) && (
                  <p style={{ marginBottom: '0.5rem' }}>
                    <strong>Zaliha:</strong> {selectedSupply ? `${selectedSupply.itemName} (${selectedSupply.quantity})` : 'Nije izabrana'}
                  </p>
                )}
                {isMedicalType(selectedActivity?.activityType) && selectedSupply && (
                  <p style={{ marginBottom: '0.5rem' }}>
                    <strong>Količina koja se troši:</strong> {supplyQuantity}
                  </p>
                )}
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Datum:</strong> {date}
                </p>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Vreme:</strong> {startTime} - {endTime}
                </p>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Ponavljanje:</strong> {repeatFrequency === 'none' ? 'Ne ponavlja se' : repeatFrequency}
                </p>
                {repeatFrequency === 'weekly' && (
                  <p style={{ marginBottom: '0.5rem' }}>
                    <strong>Dani:</strong> {repeatDays.length > 0
                      ? repeatDays.map(d => WEEKDAYS.find(w => w.value === d)?.label).filter(Boolean).join(', ')
                      : '-'}
                  </p>
                )}
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Kreiraj Zadatak:</strong> {taskCreation ? 'Da' : 'Ne'}
                </p>
                {taskCreation && (
                  <p style={{ marginBottom: '0.5rem' }}>
                    <strong>Nivo ozbiljnosti:</strong> {severity}
                  </p>
                )}
                {notes && (
                  <p style={{ marginBottom: 0 }}>
                    <strong>Napomena:</strong> {notes}
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <Button kind="secondary" onClick={() => goToStep(3)} style={{ flex: 1 }} disabled={isSaving}>Nazad</Button>
                <Button kind="primary" onClick={handleSubmit} style={{ flex: 1 }} disabled={isSaving || !selectedActivity || !date}>
                  {isSaving ? 'Čuvanje...' : 'Potvrdi'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// Activities Tab
const ActivitiesTab = ({ resident, setNotification, canManageActivities = false }) => {
  const [viewMode, setViewMode] = useState('week');
  const [weekOffset, setWeekOffset] = useState(0);
  const [dayViewDate, setDayViewDate] = useState(new Date());
  const [weekActivities, setWeekActivities] = useState({});
  const [dayActivities, setDayActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isViewTransitioning, setIsViewTransitioning] = useState(false);
  const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
  const [isActivityDetailOpen, setIsActivityDetailOpen] = useState(false);
  const [viewingActivity, setViewingActivity] = useState(null);
  const [activityMealImgUrl, setActivityMealImgUrl] = useState(null);
  const [activityDetailLoading, setActivityDetailLoading] = useState(false);
  const [activityTypes, setActivityTypes] = useState([]);
  const [selectedActivityType, setSelectedActivityType] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load available activity types for the filter dropdown
  useEffect(() => {
    axiosInstance.get('/api/Activity/activity-types')
      .then(res => {
        const types = res.data?.data?.activityTypes || [];
        setActivityTypes(types);
      })
      .catch(() => setActivityTypes([]));
  }, []);

  const openActivityDetail = async (act) => {
    setViewingActivity(act);
    setActivityMealImgUrl(null);
    setIsActivityDetailOpen(true);
    setActivityDetailLoading(true);
    try {
      const res = await axiosInstance.get(`/api/ResidentActivity/${act.id}`);
      const full = res.data?.data || act;
      setViewingActivity(full);
      if (full.mealId) {
        axiosInstance.get(`/api/Meal/${full.mealId}`)
          .then(r => setActivityMealImgUrl(r.data?.data?.imgUrl || null))
          .catch(() => {});
      }
    } catch {
      if (act.mealId) {
        axiosInstance.get(`/api/Meal/${act.mealId}`)
          .then(r => setActivityMealImgUrl(r.data?.data?.imgUrl || null))
          .catch(() => {});
      }
    } finally {
      setActivityDetailLoading(false);
    }
  };

  // Check if an activity is in the past (already passed its end time)
  const isActivityPast = (act) => {
    const end = act.endTime ? new Date(act.endTime) : new Date(act.startTime);
    return end < new Date();
  };

  const handleDeleteActivity = (act, e) => {
    if (e) e.stopPropagation();
    // Brisanje aktivnosti rezidenta zahteva dozvolu `resident-activities.manage`.
    if (!canManageActivities) return;
    setActivityToDelete(act);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteActivity = async () => {
    // Bez dozvole se DELETE /api/ResidentActivity/{id} ne poziva.
    if (!canManageActivities || !activityToDelete) return;
    setIsDeleting(true);
    try {
      await axiosInstance.delete(`/api/ResidentActivity/${activityToDelete.id}`);
      setIsDeleteModalOpen(false);
      setActivityToDelete(null);
      setIsActivityDetailOpen(false);
      setViewingActivity(null);
      if (viewMode === 'week') fetchWeekActivities(); else fetchDayActivities(dayViewDate);
      setNotification({ kind: 'success', title: 'Uspešno!', subtitle: 'Aktivnost je uspešno obrisana.' });
      setTimeout(() => setNotification(null), 3000);
    } catch {
      setNotification({ kind: 'error', title: 'Greška!', subtitle: 'Nije moguće obrisati aktivnost. Pokušajte ponovo.' });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsDeleting(false);
    }
  };

  const getWeekStart = (offset) => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - now.getDay() + offset * 7);
    start.setHours(0, 0, 0, 0);
    return start;
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('sr-Latn-RS', { hour: '2-digit', minute: '2-digit' });
  };

  // Send the intended calendar date as UTC midnight (ISO timestamp). The backend
  // parses this format and takes .Date (UTC), so the local UTC offset no longer
  // shifts the requested day.
  const toDateParam = (d) => {
    const dt = new Date(d);
    return new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate())).toISOString();
  };

  const getStatusStyle = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'completed' || s === 'završeno') return { label: 'Završeno', bg: 'rgba(159,183,70,0.15)', color: '#9fb746' };
    if (s === 'cancelled' || s === 'otkazano') return { label: 'Otkazano', bg: 'rgba(218,30,40,0.15)', color: '#da1e28' };
    return { label: 'Planirano', bg: 'rgba(255,138,0,0.15)', color: '#ff8a00' };
  };

  const fetchWeekActivities = useCallback(async () => {
    if (!resident?.id) return;
    setLoading(true);
    const startOfWeek = getWeekStart(weekOffset);
    const dates = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      return d;
    });
    const results = await Promise.all(
      dates.map(date =>
        axiosInstance.get(`/api/ResidentActivity/resident/${resident.id}`, {
          params: {
            date: toDateParam(date),
            ...(selectedActivityType ? { activityType: selectedActivityType } : {})
          }
        }).catch(() => ({ data: { data: { activities: [] } } }))
      )
    );
    const grouped = {};
    dates.forEach((date, i) => {
      grouped[date.toDateString()] = results[i]?.data?.data?.activities || [];
    });
    setWeekActivities(grouped);
    setLoading(false);
  }, [resident?.id, weekOffset, selectedActivityType]);

  const fetchDayActivities = useCallback(async (date) => {
    if (!resident?.id || !date) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/api/ResidentActivity/resident/${resident.id}`, {
        params: {
          date: toDateParam(date),
          ...(selectedActivityType ? { activityType: selectedActivityType } : {})
        }
      });
      setDayActivities(res.data?.data?.activities || []);
    } catch {
      setDayActivities([]);
    } finally {
      setLoading(false);
    }
  }, [resident?.id, selectedActivityType]);

  useEffect(() => {
    if (viewMode === 'week') fetchWeekActivities();
  }, [viewMode, weekOffset, selectedActivityType, fetchWeekActivities]);

  useEffect(() => {
    if (viewMode === 'day') fetchDayActivities(dayViewDate);
  }, [viewMode, dayViewDate, selectedActivityType, fetchDayActivities]);

  const weekStart = getWeekStart(weekOffset);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  const dayNamesArr = ['Ned', 'Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub'];

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + i);
    return {
      date,
      dayName: dayNamesArr[date.getDay()],
      isToday: date.toDateString() === new Date().toDateString(),
      activities: weekActivities[date.toDateString()] || []
    };
  });

  const formatWeekLabel = () =>
    `${weekStart.getDate()}.${weekStart.getMonth() + 1}. - ${weekEnd.getDate()}.${weekEnd.getMonth() + 1}.${weekEnd.getFullYear()}`;

  const formatDayLabel = () => {
    const days = ['Nedelja', 'Ponedeljak', 'Utorak', 'Sreda', 'Četvrtak', 'Petak', 'Subota'];
    const months = ['januar', 'februar', 'mart', 'april', 'maj', 'jun', 'jul', 'avgust', 'septembar', 'oktobar', 'novembar', 'decembar'];
    return `${days[dayViewDate.getDay()]}, ${dayViewDate.getDate()}. ${months[dayViewDate.getMonth()]} ${dayViewDate.getFullYear()}`;
  };

  return (
    <div className="residents1-tab-content">
      {/* Toolbar */}
      <div className="residents1-toolbar">
        <div className="residents1-toolbar-left">
          <div className="residents1-nav-group">
            <Button
              kind="ghost"
              size="md"
              renderIcon={ChevronLeft}
              hasIconOnly
              iconDescription={viewMode === 'day' ? 'Prethodni dan' : 'Prethodna nedelja'}
              onClick={() => {
                if (viewMode === 'day') {
                  const prev = new Date(dayViewDate);
                  prev.setDate(prev.getDate() - 1);
                  setDayViewDate(prev);
                } else {
                  setWeekOffset(w => w - 1);
                }
              }}
            />
            <Button
              kind="secondary"
              size="md"
              className="residents1-today-btn"
              style={{ width: '150px' }}
              onClick={() => {
                if (viewMode === 'day') setDayViewDate(new Date());
                else setWeekOffset(0);
              }}
              disabled={viewMode === 'day'
                ? dayViewDate.toDateString() === new Date().toDateString()
                : weekOffset === 0}
            >
              Danas
            </Button>
            <Button
              kind="ghost"
              size="md"
              renderIcon={ChevronRight}
              hasIconOnly
              iconDescription={viewMode === 'day' ? 'Sledeći dan' : 'Sledeća nedelja'}
              onClick={() => {
                if (viewMode === 'day') {
                  const next = new Date(dayViewDate);
                  next.setDate(next.getDate() + 1);
                  setDayViewDate(next);
                } else {
                  setWeekOffset(w => w + 1);
                }
              }}
            />
            <div className="residents1-filter-wrap" style={{ position: 'relative' }}>
              <Button
                kind={selectedActivityType ? 'primary' : 'ghost'}
                size="md"
                renderIcon={Filter}
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                hasIconOnly
                iconDescription="Filtriraj po tipu aktivnosti"
                style={selectedActivityType ? { backgroundColor: '#9fb746', borderColor: '#9fb746' } : {}}
              />
              {isFilterOpen && (
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  right: 0,
                  zIndex: 100,
                  minWidth: '220px',
                  backgroundColor: 'var(--cds-layer-01)',
                  border: '1px solid var(--cds-border-subtle-01)',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  padding: '0.5rem'
                }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--cds-text-secondary)', padding: '0.25rem 0.5rem 0.5rem' }}>
                    Filtriraj po tipu aktivnosti
                  </div>
                  <div
                    onClick={() => { setSelectedActivityType(''); setIsFilterOpen(false); }}
                    style={{
                      padding: '0.5rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.8125rem',
                      backgroundColor: !selectedActivityType ? 'rgba(159,183,70,0.15)' : 'transparent',
                      color: 'var(--cds-text-primary)'
                    }}
                  >
                    Sve aktivnosti
                  </div>
                  {activityTypes.map(type => (
                    <div
                      key={type.value}
                      onClick={() => { setSelectedActivityType(type.value); setIsFilterOpen(false); }}
                      style={{
                        padding: '0.5rem',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8125rem',
                        backgroundColor: selectedActivityType === type.value ? 'rgba(159,183,70,0.15)' : 'transparent',
                        color: 'var(--cds-text-primary)'
                      }}
                    >
                      {type.displayName}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="residents1-view-row">
            <div className="residents1-view-toggle" style={{ display: 'flex', backgroundColor: 'var(--cds-layer-02)', borderRadius: '4px', padding: '2px', gap: '2px' }}>
              {['week', 'day'].map(mode => (
                <button
                  key={mode}
                  onClick={() => {
                    if (mode === 'day' && !dayViewDate) setDayViewDate(new Date());
                    setViewMode(mode);
                  }}
                  style={{
                    padding: '0.25rem 0.75rem',
                    fontSize: '0.8125rem',
                    fontWeight: '500',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    backgroundColor: viewMode === mode ? '#9fb746' : 'transparent',
                    color: viewMode === mode ? '#fff' : 'inherit',
                    transition: 'all 0.15s'
                  }}
                >
                  {mode === 'week' ? 'Nedeljni' : 'Dnevni'}
                </button>
              ))}
            </div>
            <span className="residents1-date-label" style={{
              fontSize: '0.8125rem',
              color: 'var(--cds-text-secondary)',
              padding: '0.25rem 0.75rem',
              backgroundColor: 'var(--cds-layer-accent-01)',
              borderRadius: '12px'
            }}>
              {viewMode === 'week' ? formatWeekLabel() : formatDayLabel()}
            </span>
          </div>
        </div>

        <div className="residents1-toolbar-right">
          {canManageActivities && (
            <Button
              kind="primary"
              size="md"
              renderIcon={Add}
              onClick={() => setIsAddPanelOpen(true)}
              disabled={resident?.isActive === false}
              style={{ backgroundColor: '#9fb746', borderColor: '#9fb746', width: 'max-content' }}
            >
              Dodaj aktivnost
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        viewMode === 'week' ? (
          <div className="residents1-week-calendar">
            {/* Day names skeleton row */}
            <div className="residents1-week-grid residents1-week-day-names" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '0.5rem',
              marginBottom: '0.5rem',
              paddingBottom: '0.5rem',
              borderBottom: '1px solid var(--cds-border-subtle-01, rgba(255,255,255,0.1))'
            }}>
              {[...Array(7)].map((_, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <SkeletonText width="60%" style={{ margin: '0 auto' }} />
                </div>
              ))}
            </div>
            {/* Week day cells skeleton */}
            <div className="residents1-week-grid residents1-week-day-cells" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.5rem', minHeight: '300px', alignItems: 'stretch' }}>
              {[...Array(7)].map((_, i) => (
                <div key={i} className="residents1-week-day-cell" style={{
                  padding: '0.5rem',
                  borderRadius: '8px',
                  minWidth: 0,
                  overflow: 'hidden',
                  backgroundColor: 'var(--cds-layer-01, rgba(255,255,255,0.05))',
                  border: '1px solid var(--cds-border-subtle-00, rgba(255,255,255,0.08))',
                  height: '100%',
                  minHeight: '200px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.375rem'
                }}>
                  {/* Mobile day header skeleton - shown only on mobile */}
                  <div className="residents1-mobile-week-day-header">
                    <SkeletonText width="40%" />
                    <SkeletonText width="25%" />
                  </div>
                  {/* Day number skeleton - hidden on mobile */}
                  <div className="residents1-week-day-number">
                    <SkeletonText width="30%" style={{ margin: '0 auto' }} />
                  </div>
                  {/* Activity card skeletons - simple blocks matching real card size */}
                  {[...Array(4)].map((_, ai) => (
                    <SkeletonPlaceholder key={ai} style={{ height: '44px', borderRadius: '4px' }} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {[...Array(4)].map((_, i) => (
              <div key={i} style={{
                padding: '1rem',
                borderRadius: '8px',
                backgroundColor: 'var(--cds-layer-01, rgba(255,255,255,0.05))',
                border: '1px solid var(--cds-border-subtle-00, rgba(255,255,255,0.08))',
                borderLeft: '4px solid var(--cds-border-subtle-01)',
                display: 'flex',
                gap: '1rem',
                alignItems: 'flex-start'
              }}>
                {/* Time column */}
                <div style={{ minWidth: '70px', textAlign: 'center', flexShrink: 0 }}>
                  <SkeletonText width="50px" style={{ margin: '0 auto' }} />
                  <SkeletonText width="40px" style={{ margin: '0 auto' }} />
                </div>
                <div style={{ width: '1px', backgroundColor: 'var(--cds-border-subtle-01)', alignSelf: 'stretch', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Name row with type + status chips */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                    <SkeletonText heading width="35%" />
                    <SkeletonPlaceholder style={{ width: '70px', height: '18px', borderRadius: '10px' }} />
                    <SkeletonPlaceholder style={{ width: '60px', height: '18px', borderRadius: '10px' }} />
                  </div>
                  {/* Diet type */}
                  <SkeletonText width="45%" />
                  {/* Notes */}
                  <SkeletonText width="80%" />
                </div>
              </div>
            ))}
          </div>
        )
      ) : viewMode === 'week' ? (
        <div className="residents1-week-calendar">
          <div className="residents1-week-grid residents1-week-day-names" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '0.5rem',
            marginBottom: '0.5rem',
            paddingBottom: '0.5rem',
            borderBottom: '1px solid var(--cds-border-subtle-01, rgba(255,255,255,0.1))'
          }}>
            {weekDays.map((day, i) => (
              <div key={i} style={{ textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: '#9fb746', letterSpacing: '0.5px', minWidth: 0, overflow: 'hidden' }}>
                {day.dayName}
              </div>
            ))}
          </div>
          <div className="residents1-week-grid residents1-week-day-cells" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.5rem', minHeight: '300px', alignItems: 'stretch' }}>
            {weekDays.map((day, i) => {
              const hasActivities = day.activities.length > 0;
              const today = new Date(); today.setHours(0, 0, 0, 0);
              const cellDate = new Date(day.date); cellDate.setHours(0, 0, 0, 0);

              return (
                <div
                  key={i}
                  className="residents1-week-day-cell"
                  onClick={() => {
                    if (resident?.isActive === false) return;
                    setIsViewTransitioning(true);
                    setTimeout(() => {
                      setDayViewDate(day.date);
                      setViewMode('day');
                      setTimeout(() => setIsViewTransitioning(false), 50);
                    }, 200);
                  }}
                  style={{
                    padding: '0.5rem',
                    borderRadius: '8px',
                    minWidth: 0,
                    overflow: 'hidden',
                    backgroundColor: day.isToday
                      ? 'var(--cds-layer-selected-01, rgba(159,183,70,0.15))'
                      : hasActivities
                        ? 'var(--cds-layer-accent-01, rgba(159,183,70,0.08))'
                        : 'var(--cds-layer-01, rgba(255,255,255,0.05))',
                    border: day.isToday
                      ? '2px solid #9fb746'
                      : hasActivities
                        ? '1px solid rgba(159,183,70,0.25)'
                        : '1px solid var(--cds-border-subtle-00, rgba(255,255,255,0.08))',
                    height: '100%',
                    minHeight: '200px',
                    cursor: resident?.isActive === false ? 'default' : 'pointer',
                    opacity: resident?.isActive === false ? 0.6 : 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.375rem',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={e => {
                    if (resident?.isActive === false) return;
                    e.currentTarget.style.backgroundColor = 'var(--cds-layer-hover-01, rgba(159,183,70,0.12))';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = day.isToday
                      ? 'var(--cds-layer-selected-01, rgba(159,183,70,0.15))'
                      : hasActivities
                        ? 'var(--cds-layer-accent-01, rgba(159,183,70,0.08))'
                        : 'var(--cds-layer-01, rgba(255,255,255,0.05))';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* Mobile day header - shown only on mobile */}
                  <div className="residents1-mobile-week-day-header">
                    <span className="residents1-mobile-week-day-name">{day.dayName}</span>
                    <span className="residents1-mobile-week-day-date">
                      {day.date.getDate()}.{day.date.getMonth() + 1}.
                    </span>
                  </div>
                  <div className="residents1-week-day-number" style={{
                    fontSize: '1.75rem',
                    fontWeight: day.isToday ? '500' : hasActivities ? '600' : '400',
                    color: day.isToday ? '#0f62fe' : hasActivities ? '#9fb746' : 'inherit',
                    lineHeight: 1,
                    textAlign: 'center'
                  }}>
                    {day.date.getDate()}
                  </div>
                  {day.activities.length === 0 ? (
                    <div style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', textAlign: 'center', fontStyle: 'italic', opacity: 0.6 }}>
                      Nema aktivnosti
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', overflowY: 'auto', flex: 1, minHeight: 0 }}>
                      {day.activities.map((act, ai) => {
                        const isMeal = act.source === 'Meal' || act.activityType === 'Nutrition';
                        return (
                          <div key={ai}
                            onClick={(e) => {
                              if (resident?.isActive === false) return;
                              e.stopPropagation();
                              openActivityDetail(act);
                            }}
                            style={{
                              padding: '0.25rem 0.375rem',
                              borderRadius: '4px',
                              borderLeft: `3px solid ${getActivityTypeColor(act.activityType)}`,
                              backgroundColor: 'var(--cds-layer-02, rgba(255,255,255,0.05))',
                              fontSize: '0.7rem',
                              lineHeight: '1.3',
                              cursor: resident?.isActive === false ? 'default' : 'pointer'
                            }}
                          >
                            <div style={{ fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {isMeal && act.mealName ? act.mealName : act.name}
                            </div>
                            {isMeal && act.mealDietType && (
                              <div style={{ color: '#9fb746', fontSize: '0.6rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {act.mealDietType}
                              </div>
                            )}
                            <div style={{ color: 'var(--cds-text-secondary)', fontSize: '0.65rem' }}>
                              {formatTime(act.startTime)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div style={{ opacity: isViewTransitioning ? 0 : 1, transition: 'opacity 0.3s ease' }}>
          {dayActivities.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
              <div style={{
                textAlign: 'center',
                padding: '3rem 2rem',
                borderRadius: '12px',
                backgroundColor: 'var(--cds-layer-01, rgba(255,255,255,0.05))',
                border: '1px solid var(--cds-border-subtle-00, rgba(255,255,255,0.08))'
              }}>
                <div style={{ fontSize: '1rem', color: 'var(--cds-text-secondary)', fontStyle: 'italic', marginBottom: '1.5rem' }}>
                  Nema aktivnosti za ovaj dan
                </div>
                {canManageActivities && (
                  <Button
                    kind="tertiary"
                    size="md"
                    renderIcon={Add}
                    onClick={() => setIsAddPanelOpen(true)}
                    disabled={resident?.isActive === false}
                    style={{ borderColor: '#9fb746', color: '#9fb746' }}
                  >
                    Dodaj aktivnost
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {[...dayActivities]
                .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
                .map((act, i) => {
                  const statusStyle = getStatusStyle(act.status);
                  const isMeal = act.source === 'Meal' || act.activityType === 'Nutrition';
                  return (
                    <div key={i}
                      onClick={() => {
                        if (resident?.isActive === false) return;
                        openActivityDetail(act);
                      }}
                      style={{
                        padding: '1rem',
                        borderRadius: '8px',
                        backgroundColor: 'var(--cds-layer-01, rgba(255,255,255,0.05))',
                        border: '1px solid var(--cds-border-subtle-00, rgba(255,255,255,0.08))',
                        borderLeft: `4px solid ${getActivityTypeColor(act.activityType)}`,
                        display: 'flex',
                        gap: '1rem',
                        alignItems: 'flex-start',
                        cursor: resident?.isActive === false ? 'default' : 'pointer',
                        opacity: resident?.isActive === false ? 0.6 : 1,
                        transition: 'box-shadow 0.2s ease'
                      }}
                      onMouseEnter={(e) => { if (resident?.isActive === false) return; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <div style={{ minWidth: '70px', textAlign: 'center', flexShrink: 0 }}>
                        <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#9fb746' }}>
                          {formatTime(act.startTime)}
                        </div>
                        {act.endTime && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)' }}>
                            {formatTime(act.endTime)}
                          </div>
                        )}
                      </div>
                      <div style={{ width: '1px', backgroundColor: 'var(--cds-border-subtle-01)', alignSelf: 'stretch', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                          <h4 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: '600' }}>
                            {isMeal && act.mealName ? act.mealName : act.name}
                          </h4>
                          {act.activityType && (
                            <span style={{
                              fontSize: '0.6875rem',
                              fontWeight: '500',
                              padding: '0.125rem 0.5rem',
                              borderRadius: '10px',
                              backgroundColor: `${getActivityTypeColor(act.activityType)}22`,
                              color: getActivityTypeColor(act.activityType),
                              border: `1px solid ${getActivityTypeColor(act.activityType)}44`
                            }}>
                              {translateActivityType(act.activityType)}
                            </span>
                          )}
                          <span style={{
                            fontSize: '0.6875rem',
                            fontWeight: '500',
                            padding: '0.125rem 0.5rem',
                            borderRadius: '10px',
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.color
                          }}>
                            {statusStyle.label}
                          </span>
                        </div>
                        {isMeal && act.mealDietType && (
                          <div style={{ fontSize: '0.8125rem', color: 'var(--cds-text-secondary)', marginBottom: '0.25rem' }}>
                            {act.mealDietType}
                          </div>
                        )}
                        {act.notes && (
                          <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--cds-text-secondary)', lineHeight: '1.4' }}>
                            {act.notes}
                          </p>
                        )}
                        {!isMeal && act.source && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--cds-text-disabled)', marginTop: '0.25rem' }}>
                            Izvor: {act.source}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              }
            </div>
          )}
        </div>
      )}
      <AddActivitySidebar
        isOpen={canManageActivities && isAddPanelOpen}
        onClose={() => setIsAddPanelOpen(false)}
        resident={resident}
        initialDate={viewMode === 'day' ? dayViewDate : new Date()}
        onSuccess={() => {
          if (viewMode === 'week') fetchWeekActivities(); else fetchDayActivities(dayViewDate);
          setNotification({ kind: 'success', title: 'Uspešno!', subtitle: 'Aktivnost je uspešno dodata.' });
          setTimeout(() => setNotification(null), 3000);
        }}
      />

      {/* Activity Detail Side Panel */}
      {isActivityDetailOpen && (
        <div className="side-panel-overlay" onClick={() => setIsActivityDetailOpen(false)} />
      )}
      <div className={`menu-side-panel ${isActivityDetailOpen ? 'open' : ''}`}>
        <div className="side-panel-header">
          <h2>Detalji aktivnosti</h2>
          <Button kind="ghost" size="sm" renderIcon={Close} hasIconOnly onClick={() => setIsActivityDetailOpen(false)} />
        </div>
        {viewingActivity && (
          <div className="side-panel-content" style={{ padding: '1.5rem' }}>
            {activityDetailLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px', color: 'var(--cds-text-secondary)' }}>
                <div className="residents1-spinner" style={{ margin: '0 auto' }} />
              </div>
            ) : (
              <>
            {activityMealImgUrl && (
              <div style={{
                width: '100%',
                height: '200px',
                borderRadius: '8px',
                backgroundImage: `url(${activityMealImgUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                marginBottom: '1.5rem',
                border: '1px solid var(--cds-border-subtle-01)'
              }} />
            )}

            <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--cds-text-primary)' }}>
              {viewingActivity.mealName || viewingActivity.name}
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: 'var(--cds-text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Vreme
                </label>
                <div style={{ fontSize: '1rem', color: '#9fb746', fontWeight: '600' }}>
                  {formatTime(viewingActivity.startTime)}{viewingActivity.endTime ? ` - ${formatTime(viewingActivity.endTime)}` : ''}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: 'var(--cds-text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Datum
                </label>
                <div style={{ fontSize: '1rem' }}>
                  {new Date(viewingActivity.date || viewingActivity.startTime).toLocaleDateString('sr-Latn-RS', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
              </div>

              {viewingActivity.mealDietType && (
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: 'var(--cds-text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Tip dijete
                  </label>
                  <div style={{ fontSize: '1rem' }}>{viewingActivity.mealDietType}</div>
                </div>
              )}

              {(viewingActivity.activityType || viewingActivity.status) && (
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: 'var(--cds-text-secondary)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Tip aktivnosti / Status
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    {viewingActivity.activityType && (
                      <span style={{
                        fontSize: '0.8125rem',
                        fontWeight: '500',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '10px',
                        backgroundColor: `${getActivityTypeColor(viewingActivity.activityType)}22`,
                        color: getActivityTypeColor(viewingActivity.activityType),
                        border: `1px solid ${getActivityTypeColor(viewingActivity.activityType)}44`
                      }}>
                        {translateActivityType(viewingActivity.activityType)}
                      </span>
                    )}
                    {viewingActivity.status && (() => { const s = getStatusStyle(viewingActivity.status); return (
                      <span style={{ fontSize: '0.8125rem', fontWeight: '500', padding: '0.25rem 0.75rem', borderRadius: '10px', backgroundColor: s.bg, color: s.color }}>
                        {s.label}
                      </span>
                    ); })()}
                  </div>
                </div>
              )}

              {viewingActivity.notes && (
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: 'var(--cds-text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Napomena
                  </label>
                  <div style={{ fontSize: '0.9375rem' }}>{viewingActivity.notes}</div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {canManageActivities && !isActivityPast(viewingActivity) && resident?.isActive !== false && (
                <Button
                  kind="danger--ghost"
                  size="md"
                  renderIcon={TrashCan}
                  onClick={(e) => handleDeleteActivity(viewingActivity, e)}
                >
                  Obriši
                </Button>
              )}
              <Button kind="primary" size="md" style={{ backgroundColor: '#9fb746', borderColor: '#9fb746' }} onClick={() => setIsActivityDetailOpen(false)}>
                Zatvori
              </Button>
            </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      <Modal
        open={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        modalHeading="Brisanje aktivnosti"
        primaryButtonText="Obriši"
        secondaryButtonText="Otkaži"
        onRequestSubmit={confirmDeleteActivity}
        danger
        size="sm"
      >
        <p>
          Da li ste sigurni da želite da obrišete aktivnost{' '}
          <strong>{activityToDelete?.name || activityToDelete?.mealName}</strong>?
        </p>
        <p style={{ marginTop: '1rem', fontSize: '0.875rem', opacity: 0.7 }}>
          Ova akcija se ne može poništiti.
        </p>
      </Modal>
    </div>
  );
};

// Health Tab
const HealthTab = ({ resident, healthData, healthLoading, onAddHealthRecord, onEditHealth, canAddHealthRecord = true }) => {
  // Helper function to get status color based on ability level
  const getAbilityStatusColor = (value, type) => {
    // Mapping for different ability types
    const statusMapping = {
      mobility: {
        'Mobile': 'green',
        'ImpairedMobility': 'orange',
        'Immobile': 'red'
      },
      selfService: {
        'Capable': 'green',
        'PartiallyCapable': 'orange',
        'NotCapable': 'red'
      },
      hygiene: {
        'Independent': 'green',
        'PartiallyIndependent': 'orange',
        'NotIndependent': 'red'
      },
      communication: {
        'Preserved': 'green',
        'ImpairedCommunication': 'orange',
        'CommunicationCannotBeEstablished': 'red'
      },
      mentalState: {
        'MentallyPreserved': 'green',
        'MildDementia': 'orange',
        'ModerateDementia': 'orange',
        'SevereDementia': 'red',
        'Psychosis': 'red',
        'NeurologicalDisorder': 'orange',
        'PersonalityDisorder': 'orange',
        'AddictionDisorders': 'red'
      }
    };

    const mapping = statusMapping[type] || {};
    return mapping[value] || 'green';
  };

  // Helper to get Serbian display label from English enum value
  const getAbilityLabel = (value, type) => {
    const labelMapping = {
      mobility: {
        'Mobile': 'Pokretan',
        'ImpairedMobility': 'Otežano pokretan',
        'Immobile': 'Nepokretan'
      },
      selfService: {
        'Capable': 'Sposoban',
        'PartiallyCapable': 'Delimično sposoban',
        'NotCapable': 'Nije sposoban'
      },
      hygiene: {
        'Independent': 'Samostalan',
        'PartiallyIndependent': 'Delimično samostalan',
        'NotIndependent': 'Nije samostalan'
      },
      communication: {
        'Preserved': 'Očuvana',
        'ImpairedCommunication': 'Otežana komunikacija',
        'CommunicationCannotBeEstablished': 'Komunikacija se ne uspostavlja'
      },
      mentalState: {
        'MentallyPreserved': 'Psihički očuvan',
        'MildDementia': 'Demencija - blaga',
        'ModerateDementia': 'Demencija - umerena',
        'SevereDementia': 'Demencija - teška',
        'Psychosis': 'Psihoza',
        'NeurologicalDisorder': 'Neuronski poremećaj',
        'PersonalityDisorder': 'Poremećaj ličnosti',
        'AddictionDisorders': 'Bolesti zavisnosti'
      }
    };

    const mapping = labelMapping[type] || {};
    return mapping[value] || value;
  };

  // Helper to get color styles for the entire ability item
  const getColorStyle = (color) => {
    const colors = {
      green: {
        borderColor: 'var(--r1-status-success)',
        iconColor: 'var(--r1-status-success)',
        backgroundColor: 'var(--r1-status-success-bg)'
      },
      orange: {
        borderColor: 'var(--r1-status-warning)',
        iconColor: 'var(--r1-status-warning)',
        backgroundColor: 'var(--r1-status-warning-bg)'
      },
      red: {
        borderColor: 'var(--r1-status-error)',
        iconColor: 'var(--r1-status-error)',
        backgroundColor: 'var(--r1-status-error-bg)'
      }
    };
    return colors[color] || colors.green;
  };

  // Render ability item with colored div
  const AbilityItem = ({ icon: Icon, label, value, type }) => {
    const statusColor = getAbilityStatusColor(value, type);
    const colorStyle = getColorStyle(statusColor);
    const displayValue = getAbilityLabel(value, type);
    
    return (
      <div 
        className={`residents1-health-ability-item residents1-health-ability-item--${statusColor}`}
      >
        <div 
          className="residents1-health-ability-icon"
        >
          <Icon />
        </div>
        <div className="residents1-health-ability-content">
          <span className="residents1-health-ability-label">{label}</span>
          <span 
            className="residents1-health-ability-value"
          >
            {displayValue}
          </span>
        </div>
      </div>
    );
  };

  if (healthLoading) {
    return (
      <div className="residents1-tab-content">
        {/* Functional Abilities Grid skeleton */}
        <div className="residents1-cards-grid">
          <div className="r1-skel-card">
            <div className="r1-skel-card-head">
              <div className="r1-skel-icon" />
              <div className="r1-skel-line r1-skel-line--card-title" />
            </div>
            <div className="r1-skel-health-abilities">
              <div className="r1-skel-ability">
                <div className="r1-skel-ability-icon" />
                <div className="r1-skel-ability-text">
                  <div className="r1-skel-line r1-skel-line--ability-label" />
                  <div className="r1-skel-line r1-skel-line--ability-value" />
                </div>
              </div>
              <div className="r1-skel-ability">
                <div className="r1-skel-ability-icon" />
                <div className="r1-skel-ability-text">
                  <div className="r1-skel-line r1-skel-line--ability-label" />
                  <div className="r1-skel-line r1-skel-line--ability-value" />
                </div>
              </div>
              <div className="r1-skel-ability">
                <div className="r1-skel-ability-icon" />
                <div className="r1-skel-ability-text">
                  <div className="r1-skel-line r1-skel-line--ability-label" />
                  <div className="r1-skel-line r1-skel-line--ability-value" />
                </div>
              </div>
            </div>
          </div>

          <div className="r1-skel-card">
            <div className="r1-skel-card-head">
              <div className="r1-skel-icon" />
              <div className="r1-skel-line r1-skel-line--card-title" />
            </div>
            <div className="r1-skel-health-abilities">
              <div className="r1-skel-ability">
                <div className="r1-skel-ability-icon" />
                <div className="r1-skel-ability-text">
                  <div className="r1-skel-line r1-skel-line--ability-label" />
                  <div className="r1-skel-line r1-skel-line--ability-value" />
                </div>
              </div>
              <div className="r1-skel-ability">
                <div className="r1-skel-ability-icon" />
                <div className="r1-skel-ability-text">
                  <div className="r1-skel-line r1-skel-line--ability-label" />
                  <div className="r1-skel-line r1-skel-line--ability-value" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Special Aids skeleton */}
        <div className="r1-skel-card" style={{ marginTop: '24px' }}>
          <div className="r1-skel-card-head">
            <div className="r1-skel-icon" />
            <div className="r1-skel-line r1-skel-line--card-title" />
          </div>
          <div className="r1-skel-data-list">
            <div className="r1-skel-line r1-skel-line--data-row" />
            <div className="r1-skel-line r1-skel-line--data-row" />
          </div>
        </div>

        {/* Diseases skeleton */}
        <div className="r1-skel-card" style={{ marginTop: '24px' }}>
          <div className="r1-skel-card-head">
            <div className="r1-skel-icon" />
            <div className="r1-skel-line r1-skel-line--card-title" />
          </div>
          <div className="r1-skel-data-list">
            <div className="r1-skel-line r1-skel-line--data-row" />
            <div className="r1-skel-line r1-skel-line--data-row" />
          </div>
        </div>

        {/* Metadata skeleton */}
        <div className="r1-skel-card" style={{ marginTop: '24px' }}>
          <div className="r1-skel-card-head">
            <div className="r1-skel-icon" />
            <div className="r1-skel-line r1-skel-line--card-title" />
          </div>
          <div className="r1-skel-data-list">
            <div className="r1-skel-line r1-skel-line--data-row" />
            <div className="r1-skel-line r1-skel-line--data-row" />
          </div>
        </div>
      </div>
    );
  }

  if (!healthData) {
    return (
      <div className="residents1-tab-content">
        <div className="residents1-empty-state">
          <div className="residents1-empty-icon"><Icons.Heart /></div>
          <h3 className="residents1-empty-title">Zdravstveni podaci nisu dostupni</h3>
          <p className="residents1-empty-description">
            Trenutno nema evidentiranih zdravstvenih podataka za ovog stanara.
          </p>
          {canAddHealthRecord && (
            <Button
              kind="primary"
              onClick={onAddHealthRecord}
              disabled={resident?.isActive === false}
              style={{ marginTop: '1rem' }}
            >
              <Icons.Plus />
              <span style={{ marginLeft: '0.5rem' }}>Dodaj zdravstveni karton</span>
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="residents1-tab-content">
      {/* Functional Abilities Grid */}
      <div className="residents1-cards-grid">
        <Card title="Pokretljivost i nezavisnost" icon={Icons.Activity} onEdit={() => onEditHealth('mobility')} disabled={resident?.isActive === false}>
          <div className="residents1-health-abilities">
            <AbilityItem 
              icon={Icons.Activity} 
              label="Pokretljivost" 
              value={healthData.mobility} 
              type="mobility" 
            />
            <AbilityItem 
              icon={Icons.User} 
              label="Samoposluživanje" 
              value={healthData.selfServiceAbility} 
              type="selfService" 
            />
            <AbilityItem 
              icon={Icons.Heart} 
              label="Lična higijena" 
              value={healthData.personalHygieneAbility} 
              type="hygiene" 
            />
          </div>
        </Card>
        
        <Card title="Komunikacija i mentalno stanje" icon={Icons.User} onEdit={() => onEditHealth('communication')} disabled={resident?.isActive === false}>
          <div className="residents1-health-abilities">
            <AbilityItem 
              icon={Icons.User} 
              label="Sposobnost komunikacije" 
              value={healthData.communicationAbility} 
              type="communication" 
            />
            <AbilityItem 
              icon={Icons.Activity} 
              label="Mentalno stanje" 
              value={healthData.mentalState} 
              type="mentalState" 
            />
          </div>
        </Card>
      </div>

      {/* Special Aids Section */}
      <Card title="Specijalna pomagala" icon={Icons.Heart} onEdit={() => onEditHealth('aids')} disabled={resident?.isActive === false} style={{ marginTop: '24px' }}>
        <div className="residents1-data-list">
          <div className="residents1-data-item">
            <span className="residents1-data-label">Koristi specijalna pomagala</span>
            <StatusBadge status={healthData.usesSpecialAids ? 'active' : 'inactive'} 
                         customLabel={healthData.usesSpecialAids ? 'Da' : 'Ne'} />
          </div>
          {healthData.usesSpecialAids && healthData.specialAidsDescription && (
            <div className="residents1-data-item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
              <span className="residents1-data-label">Opis pomagala</span>
              <p className="residents1-data-value" style={{ 
                padding: '12px', 
                backgroundColor: 'var(--r1-bg-secondary)', 
                borderRadius: 'var(--r1-radius-sm)',
                lineHeight: '1.5',
                margin: 0
              }}>
                {healthData.specialAidsDescription}
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Diseases Section */}
      <Card title="Bolesti i dijagnoze" icon={Icons.Hospital} onEdit={() => onEditHealth('diseases')} disabled={resident?.isActive === false} style={{ marginTop: '24px' }}>
        <div className="residents1-data-list">
          {healthData.diseases && (
            <DataItem label="Bolesti" value={healthData.diseases} />
          )}
          {healthData.diseasesDescription && (
            <div className="residents1-data-item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
              <span className="residents1-data-label">Detaljni opis</span>
              <p className="residents1-data-value" style={{ 
                padding: '12px', 
                backgroundColor: 'var(--r1-bg-secondary)', 
                borderRadius: 'var(--r1-radius-sm)',
                lineHeight: '1.5',
                margin: 0
              }}>
                {healthData.diseasesDescription}
              </p>
            </div>
          )}
          {!healthData.diseases && !healthData.diseasesDescription && (
            <div className="residents1-data-item">
              <span className="residents1-data-value" style={{ color: 'var(--r1-text-secondary)' }}>
                Nema evidentiranih bolesti
              </span>
            </div>
          )}
        </div>
      </Card>

    </div>
  );
};

const DietPlanSidebar = ({ isOpen, onClose, resident, setNotification, onSuccess, existingDiet }) => {
  const [dietPlans, setDietPlans] = useState([]);
  const [dietPlansLoading, setDietPlansLoading] = useState(false);
  const [selectedDietPlanId, setSelectedDietPlanId] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedDietPlanId(existingDiet?.dietPlanId || '');
      const fetchDietPlans = async () => {
        setDietPlansLoading(true);
        try {
          const response = await axiosInstance.get('/api/DietPlan');
          if (response.data.success) setDietPlans(response.data.data || []);
        } catch (err) {
          console.error('Greška pri učitavanju planova ishrane:', err);
        } finally {
          setDietPlansLoading(false);
        }
      };
      fetchDietPlans();
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!selectedDietPlanId) {
      setNotification({ kind: 'warning', title: 'Upozorenje', subtitle: 'Molimo izaberite plan ishrane.' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }
    setIsSaving(true);
    try {
      let response;
      if (existingDiet) {
        response = await axiosInstance.put(`/api/ResidentDiet/${existingDiet.id}`, { dietPlanId: selectedDietPlanId });
      } else {
        response = await axiosInstance.post('/api/ResidentDiet', { residentId: resident.id, dietPlanId: selectedDietPlanId });
      }
      if (response.data.success) {
        const planName = dietPlans.find(p => p.id === selectedDietPlanId)?.name || '';
        setNotification({ kind: 'success', title: 'Uspešno sačuvano', subtitle: `Plan ishrane "${planName}" je dodeljen stanaru.` });
        setTimeout(() => setNotification(null), 3000);
        onClose();
        if (onSuccess) onSuccess({ ...response.data.data, dietPlanName: planName });
      } else {
        setNotification({ kind: 'error', title: 'Greška', subtitle: response.data.message || 'Došlo je do greške.' });
        setTimeout(() => setNotification(null), 4000);
      }
    } catch (err) {
      console.error('Greška pri čuvanju plana ishrane:', err);
      setNotification({ kind: 'error', title: 'Greška', subtitle: err.response?.data?.message || 'Došlo je do greške pri čuvanju plana ishrane.' });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {isOpen && <div className="edit-sidebar-overlay" onClick={onClose} />}
      <div className={`edit-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="edit-sidebar__header">
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
            {existingDiet ? 'Izmeni plan ishrane' : 'Dodaj plan ishrane'}
          </h3>
          <Button kind="ghost" size="sm" renderIcon={Close} iconDescription="Zatvori" hasIconOnly onClick={onClose} />
        </div>
        <div className="edit-sidebar__content">
          <form className="edit-sidebar__form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <Select
              id="dietPlanId"
              labelText="Plan ishrane *"
              value={selectedDietPlanId}
              onChange={(e) => setSelectedDietPlanId(e.target.value)}
              disabled={isSaving || dietPlansLoading}
            >
              <SelectItem value="" text={dietPlansLoading ? 'Učitavanje...' : 'Izaberite plan ishrane...'} />
              {dietPlans.map(plan => (
                <SelectItem key={plan.id} value={plan.id} text={plan.name} />
              ))}
            </Select>
            <div className="edit-sidebar__actions">
              <Button kind="secondary" onClick={onClose} disabled={isSaving}>Otkaži</Button>
              <Button kind="primary" type="submit" disabled={isSaving || !selectedDietPlanId}>
                {isSaving ? 'Čuvanje...' : 'Sačuvaj'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

// Nutrition Tab
const NutritionTab = ({ resident, dietData, dietLoading, onAddDietPlan, canEditDiet = true }) => {
  const [weekOffset, setWeekOffset] = useState(0);
  const [menuData, setMenuData] = useState(null);
  const [menuLoading, setMenuLoading] = useState(false);
  const [viewingMeal, setViewingMeal] = useState(null);
  const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);
  const [mealImageModalOpen, setMealImageModalOpen] = useState(false);
  const [dietPlanExpanded, setDietPlanExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const openMealDetails = (menu) => {
    setViewingMeal(menu);
    setIsViewPanelOpen(true);
  };

  const closeMealDetails = () => {
    setIsViewPanelOpen(false);
    setViewingMeal(null);
    setMealImageModalOpen(false);
  };

  useEffect(() => {
    if (!dietData?.dietPlanId) return;
    const fetch = async () => {
      setMenuLoading(true);
      try {
        const res = await axiosInstance.get(
          `/api/DailyMenu?dietPlanId=${dietData.dietPlanId}&weekOffset=${weekOffset}`
        );
        if (res.data.success) setMenuData(res.data.data);
      } catch (err) {
        console.error('Greška pri učitavanju jelovnika:', err);
      } finally {
        setMenuLoading(false);
      }
    };
    fetch();
  }, [weekOffset, dietData?.dietPlanId]);

  const groupByDay = (menus) => {
    const grouped = {};
    (menus || []).forEach(m => {
      const key = new Date(m.startTime).toDateString();
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(m);
    });
    return grouped;
  };

  const fmt = (iso) => new Date(iso).toLocaleTimeString('sr-Latn-RS', { hour: '2-digit', minute: '2-digit' });

  const dayNames = ['Ned', 'Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub'];

  const getWeekLabel = () => {
    if (!menuData) return '';
    const s = new Date(menuData.weekStartDate);
    const e = new Date(menuData.weekEndDate);
    return `${s.getDate()}.${s.getMonth()+1}.${s.getFullYear()} - ${e.getDate()}.${e.getMonth()+1}.${e.getFullYear()}`;
  };

  const renderWeekNav = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <button
          onClick={() => setWeekOffset(w => w - 1)}
          style={{ background: 'none', border: '1px solid var(--cds-border-subtle)', borderRadius: '4px', cursor: 'pointer', padding: '0.25rem 0.5rem', color: 'var(--cds-text-primary)' }}
        >‹</button>
        <button
          onClick={() => setWeekOffset(0)}
          disabled={weekOffset === 0}
          style={{ background: 'none', border: '1px solid var(--cds-border-subtle)', borderRadius: '4px', cursor: weekOffset === 0 ? 'default' : 'pointer', padding: '0.25rem 0.75rem', color: 'var(--cds-text-primary)', opacity: weekOffset === 0 ? 0.4 : 1 }}
        >Ova nedelja</button>
        <button
          onClick={() => setWeekOffset(w => w + 1)}
          style={{ background: 'none', border: '1px solid var(--cds-border-subtle)', borderRadius: '4px', cursor: 'pointer', padding: '0.25rem 0.5rem', color: 'var(--cds-text-primary)' }}
        >›</button>
      </div>
      <span style={{ fontSize: '0.8125rem', color: 'var(--cds-text-secondary)', padding: '0.2rem 0.6rem', background: 'var(--cds-layer-accent-01)', borderRadius: '10px' }}>
        {getWeekLabel()}
      </span>
    </div>
  );

  const renderMealCard = (menu) => (
    <div
      key={menu.id || menu.mealName}
      className="residents1-meal-card"
      onClick={() => openMealDetails(menu)}
      style={{
        borderRadius: '5px',
        backgroundImage: menu.mealImgUrl
          ? `linear-gradient(to bottom,rgba(0,0,0,0.3) 0%,rgba(0,0,0,0.7) 100%),url(${menu.mealImgUrl})`
          : 'linear-gradient(135deg,#667eea 0%,#764ba2 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        padding: '0.4rem 0.5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ fontSize: '0.7rem', fontWeight: '600', color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.8)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{menu.mealName}</div>
      <div style={{ fontSize: '0.625rem', color: '#9fb746', fontWeight: '600', backgroundColor: 'rgba(0,0,0,0.4)', padding: '1px 4px', borderRadius: '3px', display: 'inline-block', alignSelf: 'flex-start' }}>{fmt(menu.startTime)}</div>
    </div>
  );

  const renderWeekGrid = () => {
    const grouped = groupByDay(menuData?.menus);
    const startDate = new Date(menuData?.weekStartDate || new Date());
    const weekDays = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      return {
        date,
        isToday: date.toDateString() === new Date().toDateString(),
        menus: grouped[date.toDateString()] || []
      };
    });

    // Mobile: dedicated vertical layout — the desktop 7-column table is never
    // rendered, so it can never flash in before the mobile styles apply.
    if (isMobile) {
      return (
        <div className="residents1-week-calendar" style={{ marginTop: '1.5rem' }}>
          {renderWeekNav()}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {weekDays.map((day, i) => (
              <div
                key={i}
                className="residents1-week-day-cell"
                style={{
                  padding: '0.75rem',
                  borderRadius: '8px',
                  backgroundColor: day.isToday ? 'rgba(159,183,70,0.15)' : day.menus.length > 0 ? 'rgba(159,183,70,0.06)' : 'var(--cds-layer-01, rgba(255,255,255,0.04))',
                  border: day.isToday ? '2px solid #9fb746' : day.menus.length > 0 ? '1px solid rgba(159,183,70,0.25)' : '1px solid var(--cds-border-subtle-00)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem'
                }}
              >
                <div className="residents1-mobile-week-day-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', margin: '-0.75rem -0.75rem 0 -0.75rem', backgroundColor: 'rgba(159, 183, 70, 0.15)', borderRadius: '8px 8px 0 0', borderLeft: '4px solid #9fb746' }}>
                  <span className="residents1-mobile-week-day-name" style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--cds-text-primary)', textTransform: 'uppercase' }}>
                    {dayNames[day.date.getDay()]}
                  </span>
                  <span className="residents1-mobile-week-day-date" style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--cds-text-secondary)' }}>
                    {day.date.getDate()}.{day.date.getMonth() + 1}.
                  </span>
                </div>
                {day.menus.length === 0 ? (
                  <div style={{ fontSize: '0.7rem', color: 'var(--cds-text-secondary)', textAlign: 'center', fontStyle: 'italic', opacity: 0.6 }}>Nema</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {day.menus.sort((a,b) => new Date(a.startTime)-new Date(b.startTime)).map((menu) => renderMealCard(menu))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="residents1-week-calendar" style={{ marginTop: '1.5rem', overflowX: 'auto' }}>
        {/* Week navigation */}
        <div className="residents1-week-minwidth-wrap" style={{ minWidth: '560px' }}>
        {renderWeekNav()}

        {/* Day header */}
        <div className="residents1-week-grid residents1-week-day-names" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(80px, 1fr))', gap: '0.5rem', marginBottom: '0.5rem', paddingBottom: '0.5rem', borderBottom: '1px solid var(--cds-border-subtle-01)' }}>
          {weekDays.map((day, i) => (
            <div key={i} style={{ textAlign: 'center', fontSize: '0.75rem', fontWeight: '600', color: '#9fb746', letterSpacing: '0.5px' }}>
              {dayNames[day.date.getDay()]}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="residents1-week-grid residents1-week-day-cells" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(80px, 1fr))', gap: '0.5rem' }}>
          {weekDays.map((day, i) => (
            <div
              key={i}
              className="residents1-week-day-cell"
              style={{
                padding: '0.5rem',
                borderRadius: '8px',
                backgroundColor: day.isToday ? 'rgba(159,183,70,0.15)' : day.menus.length > 0 ? 'rgba(159,183,70,0.06)' : 'var(--cds-layer-01, rgba(255,255,255,0.04))',
                border: day.isToday ? '2px solid #9fb746' : day.menus.length > 0 ? '1px solid rgba(159,183,70,0.25)' : '1px solid var(--cds-border-subtle-00)',
                minHeight: '120px',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.375rem'
              }}
            >
              {/* Mobile day header - shown only on mobile */}
              <div className="residents1-mobile-week-day-header">
                <span className="residents1-mobile-week-day-name">{dayNames[day.date.getDay()]}</span>
                <span className="residents1-mobile-week-day-date">
                  {day.date.getDate()}.{day.date.getMonth() + 1}.
                </span>
              </div>
              <div className="residents1-week-day-number" style={{ fontSize: '1.75rem', fontWeight: day.isToday ? '500' : day.menus.length > 0 ? '600' : '400', lineHeight: 1, color: day.isToday ? '#0f62fe' : day.menus.length > 0 ? '#9fb746' : 'inherit', textAlign: 'center' }}>
                {day.date.getDate()}
              </div>
              {day.menus.length === 0 ? (
                <div style={{ fontSize: '0.7rem', color: 'var(--cds-text-secondary)', textAlign: 'center', fontStyle: 'italic', opacity: 0.6 }}>Nema</div>
              ) : (
                day.menus.sort((a,b) => new Date(a.startTime)-new Date(b.startTime)).map((menu) => renderMealCard(menu))
              )}
            </div>
          ))}
        </div>
        </div>{/* end minWidth wrapper */}
      </div>
    );
  };

  if (dietLoading) {
    return (
      <div className="residents1-tab-content">
        {/* Diet plan card skeleton - collapsed mode (same wrappers as real card) */}
        <div className="residents1-cards-grid">
          <div className="residents1-card residents1-diet-plan-card">
            <div className="residents1-card__header">
              <h3 className="residents1-card__title">
                <span className="residents1-card__title-icon"><Icons.Utensils /></span>
                Plan ishrane
              </h3>
              <div className="r1-skel-chevron" />
            </div>
            <div className="residents1-card__body residents1-diet-plan-body collapsed">
              <div className="r1-skel-line r1-skel-line--title" />
            </div>
          </div>
        </div>

        {/* Week calendar skeleton - same wrappers & inline styles as real calendar */}
        {isMobile ? (
          <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {Array.from({ length: 7 }).map((_, i) => (
              <div
                key={i}
                style={{
                  padding: '0.75rem',
                  borderRadius: '8px',
                  backgroundColor: 'var(--cds-layer-01, rgba(255,255,255,0.04))',
                  border: '1px solid var(--cds-border-subtle-00)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem'
                }}
              >
                <div className="r1-skel-line r1-skel-line--dayname" style={{ margin: 0 }} />
                <div className="r1-skel-meal" />
                <div className="r1-skel-meal" />
                <div className="r1-skel-meal r1-skel-meal--short" />
              </div>
            ))}
          </div>
        ) : (
          <div className="residents1-week-calendar" style={{ marginTop: '1.5rem', overflowX: 'auto' }}>
            <div className="residents1-week-minwidth-wrap" style={{ minWidth: '560px' }}>
              {/* Week navigation */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div className="r1-skel-line r1-skel-line--nav" />
                  <div className="r1-skel-line r1-skel-line--nav" />
                  <div className="r1-skel-line r1-skel-line--nav" />
                </div>
                <div className="r1-skel-line r1-skel-line--label" />
              </div>

              {/* Day header */}
              <div className="residents1-week-grid residents1-week-day-names" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(80px, 1fr))', gap: '0.5rem', marginBottom: '0.5rem', paddingBottom: '0.5rem', borderBottom: '1px solid var(--cds-border-subtle-01)' }}>
                {Array.from({ length: 7 }).map((_, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <div className="r1-skel-line r1-skel-line--dayname" />
                  </div>
                ))}
              </div>

              {/* Day cells */}
              <div className="residents1-week-grid residents1-week-day-cells" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(80px, 1fr))', gap: '0.5rem' }}>
                {Array.from({ length: 7 }).map((_, i) => (
                  <div
                    key={i}
                    className="residents1-week-day-cell"
                    style={{
                      padding: '0.5rem',
                      borderRadius: '8px',
                      backgroundColor: 'var(--cds-layer-01, rgba(255,255,255,0.04))',
                      border: '1px solid var(--cds-border-subtle-00)',
                      minHeight: '120px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.375rem'
                    }}
                  >
                    {/* Mobile day header - shown only on mobile */}
                    <div className="residents1-mobile-week-day-header">
                      <span className="r1-skel-line r1-skel-line--dayname" />
                      <span className="r1-skel-line r1-skel-line--daydate" />
                    </div>
                    {/* Big day number - shown on desktop */}
                    <div className="residents1-week-day-number" style={{ textAlign: 'center' }}>
                      <div className="r1-skel-day-number" />
                    </div>
                    {/* Meal placeholders */}
                    <div className="r1-skel-meal" />
                    <div className="r1-skel-meal" />
                    <div className="r1-skel-meal r1-skel-meal--short" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!dietData) {
    return (
      <div className="residents1-tab-content">
        <div className="residents1-empty-state">
          <div className="residents1-empty-icon"><Icons.Utensils /></div>
          <h3 className="residents1-empty-title">Plan ishrane nije dodeljen</h3>
          <p className="residents1-empty-description">Trenutno nema dodeljenog plana ishrane za ovog stanara.</p>
          {canEditDiet && (
            <Button kind="primary" onClick={onAddDietPlan} disabled={resident?.isActive === false} style={{ marginTop: '1rem' }}>
              <Icons.Plus />
              <span style={{ marginLeft: '0.5rem' }}>Dodaj plan ishrane</span>
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="residents1-tab-content">
      <div className="residents1-cards-grid">
        <div className="residents1-card residents1-diet-plan-card">
          <div
            className="residents1-card__header"
            onClick={() => setDietPlanExpanded(!dietPlanExpanded)}
          >
            <h3 className="residents1-card__title">
              <span className="residents1-card__title-icon"><Icons.Utensils /></span>
              Plan ishrane
            </h3>
            <button
              className="residents1-diet-plan-toggle"
              onClick={(e) => {
                e.stopPropagation();
                setDietPlanExpanded(!dietPlanExpanded);
              }}
              aria-expanded={dietPlanExpanded}
              aria-label={dietPlanExpanded ? 'Skupi plan ishrane' : 'Proširi plan ishrane'}
            >
              <Icons.ChevronDown />
            </button>
          </div>
          <div className={`residents1-card__body residents1-diet-plan-body ${dietPlanExpanded ? 'expanded' : 'collapsed'}`}>
            <div className="residents1-data-list">
              <div className="residents1-data-item">
                <span className="residents1-data-label">Naziv plana</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span className="residents1-data-value">{dietData.dietPlanName}</span>
                  {canEditDiet && (
                    <button
                      onClick={onAddDietPlan}
                      disabled={resident?.isActive === false}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.25rem 0.625rem', borderRadius: '4px', border: 'none', backgroundColor: '#f47920', color: '#fff', fontSize: '0.75rem', fontWeight: '500', cursor: resident?.isActive === false ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: resident?.isActive === false ? 0.5 : 1 }}
                    >
                      <Icons.Edit />
                      Izmeni
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Weekly menu preview */}
      {menuLoading ? (
        isMobile ? (
          <div className="residents1-menu-loading-mobile" style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Week navigation skeleton (mirrors renderWeekNav) */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div className="r1-skel-line r1-skel-line--nav" />
                <div className="r1-skel-line r1-skel-line--nav" style={{ width: '5.5rem' }} />
                <div className="r1-skel-line r1-skel-line--nav" />
              </div>
              <div className="r1-skel-line r1-skel-line--label" />
            </div>
            {[...Array(7)].map((_, i) => (
              <div key={i} className="residents1-menu-loading-day">
                {/* Day header skeleton */}
                <div className="residents1-menu-loading-day-header">
                  <div className="r1-skel-line r1-skel-line--dayname" style={{ margin: 0 }} />
                  <div className="r1-skel-line r1-skel-line--daydate" />
                </div>
                {/* Meal card skeletons */}
                <div className="r1-skel-meal-card" />
                <div className="r1-skel-meal-card" />
                <div className="r1-skel-meal-card r1-skel-meal-card--short" />
              </div>
            ))}
          </div>
        ) : (
          <div className="residents1-menu-loading" style={{ marginTop: '1.5rem', display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.5rem' }}>
            {[...Array(7)].map((_, i) => (
              <div key={i} className="residents1-menu-loading__cell" style={{ height: '120px', borderRadius: '8px', backgroundColor: 'var(--cds-layer-01)', border: '1px solid var(--cds-border-subtle-00)', animation: 'pulse 1.5s infinite' }} />
            ))}
          </div>
        )
      ) : menuData ? renderWeekGrid() : null}

      {/* Meal Details Side Panel */}
      {isViewPanelOpen && viewingMeal && (
        <>
          <div className="pocketmoney-overlay" onClick={closeMealDetails} />
          <div className="pocketmoney-side-panel open">
            <div className="side-panel-header">
              <h2>Detalji obroka</h2>
              <Button
                kind="ghost"
                size="sm"
                renderIcon={Close}
                hasIconOnly
                iconDescription="Zatvori"
                onClick={closeMealDetails}
              />
            </div>

            <div className="side-panel-content">
              {/* Slika obroka */}
              {viewingMeal.mealImgUrl && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <img
                    src={viewingMeal.mealImgUrl}
                    alt={viewingMeal.mealName || 'Obrok'}
                    className="pocketmoney-receipt-image"
                    onClick={() => setMealImageModalOpen(true)}
                    style={{ cursor: 'zoom-in' }}
                  />
                </div>
              )}

              {/* Naziv obroka */}
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                marginBottom: '1rem',
                color: 'var(--cds-text-primary)'
              }}>
                {viewingMeal.mealName}
              </h3>

              {/* Osnovne informacije */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div>
                  <label className="pocketmoney-detail-label">Vreme</label>
                  <div className="pocketmoney-detail-value" style={{ color: '#9fb746', fontWeight: '600' }}>
                    {fmt(viewingMeal.startTime)} - {fmt(viewingMeal.endTime)}
                  </div>
                </div>

                <div>
                  <label className="pocketmoney-detail-label">Datum</label>
                  <div className="pocketmoney-detail-value">
                    {new Date(viewingMeal.startTime).toLocaleDateString('sr-Latn-RS', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                </div>

                {viewingMeal.menuName && (
                  <div>
                    <label className="pocketmoney-detail-label">Naziv jelovnika</label>
                    <div className="pocketmoney-detail-value">{viewingMeal.menuName}</div>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-start' }}>
                <Button
                  kind="secondary"
                  size="md"
                  style={{ width: 'max-content' }}
                  onClick={closeMealDetails}
                >
                  Zatvori
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Meal Image Modal */}
      {mealImageModalOpen && (
        <Modal
          open
          onRequestClose={() => setMealImageModalOpen(false)}
          onSecondarySubmit={() => setMealImageModalOpen(false)}
          modalHeading={viewingMeal?.mealName || 'Obrok'}
          size="sm"
          passiveModal
        >
          {viewingMeal?.mealImgUrl && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '0.5rem 0'
              }}
            >
              <img
                src={viewingMeal.mealImgUrl}
                alt={viewingMeal.mealName || 'Obrok'}
                style={{
                  maxWidth: '100%',
                  maxHeight: '60vh',
                  objectFit: 'contain',
                  borderRadius: '8px'
                }}
              />
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

// Inventory (Zalihe) Tab
const InventoryTab = ({ resident, setNotification, canManage = true }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 8;
  const [pageFading, setPageFading] = useState(false);
  const [isSupplySidebarOpen, setIsSupplySidebarOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDetailSidebarOpen, setIsDetailSidebarOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  // Fetch the resident's supply items
  useEffect(() => {
    if (!resident?.id) return;
    let cancelled = false;
    const fetchItems = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.append('pageNumber', pageNumber);
        params.append('pageSize', pageSize);
        if (searchTerm) {
          params.append('search', searchTerm);
        }
        const res = await axiosInstance.get(
          `/api/ResidentSupply/resident/${resident.id}?${params.toString()}`
        );
        if (!cancelled) {
          if (res.data?.success) {
            setItems(res.data.data || []);
            setPagination(res.data.pagination || null);
          } else {
            setItems([]);
            setPagination(null);
          }
        }
      } catch (err) {
        console.error('Greška pri učitavanju zaliha:', err);
        if (!cancelled) {
          setError('Nije moguće učitati zalihe.');
          setItems([]);
          setPagination(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchItems();
    return () => { cancelled = true; };
  }, [resident?.id, pageNumber, searchTerm]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('sr-Latn-RS', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPageNumber(1);
  };

  const nextPage = () => {
    if (pagination?.hasNextPage) {
      setPageFading(true);
      setTimeout(() => {
        setPageNumber(prev => prev + 1);
        setPageFading(false);
      }, 300);
    }
  };

  const prevPage = () => {
    if (pagination?.hasPreviousPage) {
      setPageFading(true);
      setTimeout(() => {
        setPageNumber(prev => prev - 1);
        setPageFading(false);
      }, 300);
    }
  };

  const handleSupplyAdded = (newItem) => {
    setItems(prev => [newItem, ...prev]);
    setPagination(prev => prev ? {
      ...prev,
      totalCount: (prev.totalCount || 0) + 1,
      totalPages: Math.ceil(((prev.totalCount || 0) + 1) / pageSize)
    } : prev);
  };

  const openDeleteModal = (item) => {
    setItemToDelete(item);
    setIsDeleteModalOpen(true);
    // Close the detail sidebar when deleting from it
    setIsDetailSidebarOpen(false);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      await axiosInstance.delete(`/api/ResidentSupply/${itemToDelete.id}`);
      setNotification({
        kind: 'success',
        title: 'Uspešno obrisano',
        subtitle: 'Zaliha je uspešno obrisana.'
      });
      setTimeout(() => setNotification(null), 3000);
      setItems(prev => prev.filter(i => i.id !== itemToDelete.id));
      setPagination(prev => prev ? {
        ...prev,
        totalCount: Math.max((prev.totalCount || 0) - 1, 0),
        totalPages: Math.max(Math.ceil(((prev.totalCount || 0) - 1) / pageSize), 1)
      } : prev);
    } catch (err) {
      console.error('Greška pri brisanju zalihe:', err);
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle: err.response?.data?.message || 'Nije moguće obrisati zalihu.'
      });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      setIsDeleting(false);
    }
  };

  const openSupplyDetail = async (item) => {
    setDetailItem(null);
    setDetailError(null);
    setDetailLoading(true);
    setIsDetailSidebarOpen(true);
    try {
      const res = await axiosInstance.get(`/api/ResidentSupply/${item.id}`);
      if (res.data?.success) {
        setDetailItem(res.data.data || item);
      } else {
        setDetailItem(item);
      }
    } catch (err) {
      console.error('Greška pri učitavanju detalja zalihe:', err);
      setDetailError(err.response?.data?.message || 'Nije moguće učitati detalje zalihe.');
      setDetailItem(item);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeSupplyDetail = () => {
    setIsDetailSidebarOpen(false);
    setDetailItem(null);
    setDetailError(null);
  };

  const handleSupplyImageUploaded = (updatedItem) => {
    setDetailItem(updatedItem);
    setItems(prev => prev.map(i => i.id === updatedItem.id ? updatedItem : i));
  };

  const handleSupplyQuantityIncreased = (updatedItem) => {
    setDetailItem(updatedItem);
    setItems(prev => prev.map(i => i.id === updatedItem.id ? updatedItem : i));
  };

  const handleSupplyUpdated = (updatedItem) => {
    setDetailItem(updatedItem);
    setItems(prev => prev.map(i => i.id === updatedItem.id ? updatedItem : i));
  };

  return (
    <div className="residents1-tab-content">
      {/* Toolbar */}
      <div className="inventory-toolbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
          <Search
            size="lg"
            placeholder="Pretražite zalihe..."
            value={searchTerm}
            onChange={handleSearchChange}
            onClear={() => {
              setSearchTerm('');
              setPageNumber(1);
            }}
            labelText=""
            closeButtonLabelText="Obriši pretragu"
            style={{ flex: '1 1 250px', maxWidth: '400px' }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Button
            kind="ghost"
            size="md"
            renderIcon={ChevronLeft}
            onClick={prevPage}
            hasIconOnly
            disabled={!pagination?.hasPreviousPage}
            iconDescription="Prethodna stranica"
          />
          <span style={{
            fontSize: '0.875rem',
            opacity: 0.8,
            minWidth: '60px',
            textAlign: 'center',
            fontWeight: '500'
          }}>
            {pageNumber}/{pagination?.totalPages || 1}
          </span>
          <Button
            kind="ghost"
            size="md"
            renderIcon={ChevronRight}
            onClick={nextPage}
            hasIconOnly
            disabled={!pagination?.hasNextPage}
            iconDescription="Sledeća stranica"
          />
        </div>
        {canManage && (
          <div className="inventory-toolbar__actions">
            <Button
              kind="primary"
              renderIcon={Add}
              onClick={() => setIsSupplySidebarOpen(true)}
              disabled={resident?.isActive === false}
              className="inventory-add-button"
            >
              Dodaj zalihu
            </Button>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="inventory-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <div className="r1-skel-inv-card" key={i}>
              <div className="r1-skel-inv-head">
                <div className="r1-skel-inv-name">
                  <div className="r1-skel-inv-icon" />
                  <div className="r1-skel-line r1-skel-line--inv-title" />
                </div>
                <div className="r1-skel-line r1-skel-line--inv-tag" />
              </div>
              <div className="r1-skel-inv-content">
                <div className="r1-skel-inv-detail">
                  <div className="r1-skel-line r1-skel-line--inv-label" />
                  <div className="r1-skel-inv-image" />
                </div>
                <div className="r1-skel-line r1-skel-line--inv-detail" />
                <div className="r1-skel-line r1-skel-line--inv-detail" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="inventory-empty">
          <div className="empty-icon"><Icons.Package /></div>
          <h3 className="empty-title">Greška</h3>
          <p className="empty-subtitle">{error}</p>
        </div>
      )}

      {/* Grid */}
      {!loading && !error && items.length > 0 && (
        <div
          className="inventory-grid"
          style={{
            opacity: pageFading ? 0 : 1,
            transition: 'opacity 0.3s ease',
            animation: 'inventoryFadeIn 0.4s ease-in'
          }}
        >
          {items.map((item) => (
            <div
              key={item.id}
              className="inventory-card inventory-card-clickable"
              onClick={() => openSupplyDetail(item)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openSupplyDetail(item);
                }
              }}
            >
              {/* Header */}
              <div className="inventory-card-header">
                <div className="inventory-card-name">
                  <Package size={24} className="inventory-card-icon" />
                  <span className="inventory-card-title">{truncateText(item.itemName || 'Zaliha', 18)}</span>
                </div>
                <Tag type="blue" size="sm">
                  {item.quantity ?? '-'}
                </Tag>
              </div>

              {/* Details */}
              <div className="inventory-card-content">
                <div className="inventory-card-detail">
                  <span className="detail-label">Slika</span>
                  {item.imgSrc && item.imgSrc.startsWith('https://') ? (
                    <img
                      src={item.imgSrc}
                      alt={item.itemName || 'Zaliha'}
                      className="inventory-card-image"
                    />
                  ) : (
                    <span className="inventory-card-image-placeholder">
                      <Package size={20} />
                    </span>
                  )}
                </div>
                <div className="inventory-card-detail">
                  <span className="detail-label">Količina:</span>
                  <span className="detail-value">{item.quantity ?? '-'}</span>
                </div>
                <div className="inventory-card-detail">
                  <span className="detail-label">Dodato:</span>
                  <span className="detail-value">{formatDate(item.createdAt)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && items.length === 0 && !searchTerm && (
        <div className="inventory-empty">
          <div className="empty-icon">
            <Package size={64} />
          </div>
          <h3 className="empty-title">Nema zaliha</h3>
          <p className="empty-subtitle">Dodajte prvu zalihu klikom na dugme iznad</p>
        </div>
      )}

      {/* No Results */}
      {!loading && !error && items.length === 0 && searchTerm && (
        <div className="inventory-empty">
          <div className="empty-icon">🔍</div>
          <h3 className="empty-title">Nema rezultata</h3>
          <p className="empty-subtitle">Pokušajte sa drugom pretragom</p>
        </div>
      )}

      {/* Add Supply Sidebar */}
      <SupplySidebar
        isOpen={isSupplySidebarOpen}
        onClose={() => setIsSupplySidebarOpen(false)}
        resident={resident}
        setNotification={setNotification}
        onSuccess={handleSupplyAdded}
      />

      {/* Delete Supply Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        modalHeading="Brisanje zalihe"
        primaryButtonText={isDeleting ? 'Brisanje...' : 'Obriši'}
        secondaryButtonText="Otkaži"
        onRequestSubmit={confirmDelete}
        danger
        size="sm"
      >
        <p>
          Da li ste sigurni da želite da obrišete zalihu{' '}
          <strong>{itemToDelete?.itemName}</strong>?
        </p>
        <p style={{ marginTop: '1rem', fontSize: '0.875rem', opacity: 0.7 }}>
          Ova akcija se ne može poništiti.
        </p>
      </Modal>

      {/* Supply Detail Sidebar */}
      <SupplyDetailSidebar
        isOpen={isDetailSidebarOpen}
        onClose={closeSupplyDetail}
        item={detailItem}
        loading={detailLoading}
        error={detailError}
        setNotification={setNotification}
        onImageUploaded={handleSupplyImageUploaded}
        onQuantityIncreased={handleSupplyQuantityIncreased}
        onUpdated={handleSupplyUpdated}
        onDelete={() => openDeleteModal(detailItem)}
        canManage={canManage}
      />
    </div>
  );
};

// Supply Sidebar (Add supply item with optional image upload)
const SupplySidebar = ({ isOpen, onClose, resident, setNotification, onSuccess }) => {
  const [itemName, setItemName] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Reset state when sidebar opens
  useEffect(() => {
    if (isOpen) {
      setItemName('');
      setQuantity(1);
      setSelectedFile(null);
      setIsSaving(false);
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleSubmit = async () => {
    if (!itemName.trim()) {
      setNotification({ kind: 'warning', title: 'Upozorenje', subtitle: 'Molimo unesite naziv zalihe.' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    if (itemName.trim().length > 30) {
      setNotification({ kind: 'warning', title: 'Upozorenje', subtitle: 'Naziv zalihe može imati najviše 30 karaktera.' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    const qty = Number(quantity);
    if (!qty || qty <= 0) {
      setNotification({ kind: 'warning', title: 'Upozorenje', subtitle: 'Molimo unesite ispravnu količinu.' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    setIsSaving(true);
    try {
      const response = await axiosInstance.post('/api/ResidentSupply', {
        residentId: resident.id,
        itemName: itemName.trim(),
        quantity: qty
      });

      if (response.data.success) {
        const newItem = response.data.data;

        // If an image was selected, upload it to the newly created supply item
        if (selectedFile && newItem?.id) {
          try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            await axiosInstance.post(`/api/ResidentSupply/${newItem.id}/upload-image`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch (uploadErr) {
            console.error('Greška pri dodavanju slike zalihe:', uploadErr);
          }
        }

        setNotification({
          kind: 'success',
          title: 'Uspešno dodato',
          subtitle: 'Zaliha je uspešno dodata.'
        });
        setTimeout(() => setNotification(null), 3000);
        onClose();
        if (onSuccess) onSuccess(newItem);
      } else {
        setNotification({ kind: 'error', title: 'Greška', subtitle: response.data.message || 'Došlo je do greške pri dodavanju zalihe.' });
        setTimeout(() => setNotification(null), 4000);
      }
    } catch (err) {
      console.error('Greška pri dodavanju zalihe:', err);
      setNotification({ kind: 'error', title: 'Greška', subtitle: err.response?.data?.message || 'Došlo je do greške pri dodavanju zalihe.' });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {isOpen && <div className="edit-sidebar-overlay" onClick={onClose} />}
      <div className={`edit-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="edit-sidebar__header">
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
            Dodaj zalihu
          </h3>
          <Button kind="ghost" size="sm" renderIcon={Close} iconDescription="Zatvori" hasIconOnly onClick={onClose} />
        </div>
        <div className="edit-sidebar__content">
          <form className="edit-sidebar__form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <TextInput
              id="supplyItemName"
              labelText="Naziv zalihe *"
              placeholder="Unesite naziv zalihe"
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              maxLength={30}
              disabled={isSaving}
            />
            <NumberInput
              id="supplyQuantity"
              label="Količina *"
              value={quantity}
              min={1}
              max={2147483647}
              onChange={(e, { value }) => setQuantity(value)}
              disabled={isSaving}
            />
            <FileUploader
              labelTitle=""
              labelDescription="Slika zalihe (opciono)"
              buttonLabel="Izaberi sliku"
              filenameStatus="edit"
              accept={['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp']}
              multiple={false}
              disabled={isSaving}
              onChange={handleFileChange}
            />
            {selectedFile && (
              <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginTop: '0.5rem' }}>
                Izabran fajl: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
              </p>
            )}
            {isSaving && (
              <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginTop: '0.5rem' }}>
                Dodavanje zalihe u toku...
              </p>
            )}
            <div className="edit-sidebar__actions">
              <Button kind="secondary" onClick={onClose} disabled={isSaving}>Otkaži</Button>
              <Button kind="primary" type="submit" disabled={isSaving}>
                {isSaving ? 'Dodavanje...' : 'Dodaj zalihu'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

// Supply Detail Sidebar (shows all details of a supply item)
const SupplyDetailSidebar = ({ isOpen, onClose, item, loading, error, setNotification, onImageUploaded, onQuantityIncreased, onUpdated, onDelete, canManage = true }) => {
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [increaseAmount, setIncreaseAmount] = useState(1);
  const [isIncreasing, setIsIncreasing] = useState(false);
  const [view, setView] = useState('detail'); // 'detail' | 'increase' | 'edit'
  const [editName, setEditName] = useState('');
  const [editQuantity, setEditQuantity] = useState(1);
  const [isEditing, setIsEditing] = useState(false);
  const fileInputRef = useRef(null);

  // Reset state when sidebar opens
  useEffect(() => {
    if (isOpen) {
      setSelectedFile(null);
      setIsUploading(false);
      setIncreaseAmount(1);
      setIsIncreasing(false);
      setView('detail');
      setEditName(item?.itemName || '');
      setEditQuantity(item?.quantity ?? 1);
      setIsEditing(false);
    }
  }, [isOpen, item]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString('sr-Latn-RS', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const hasImage = item?.imgSrc && item.imgSrc.startsWith('https://');

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !item?.id) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await axiosInstance.post(`/api/ResidentSupply/${item.id}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data?.success) {
        setNotification({
          kind: 'success',
          title: 'Uspešno dodato',
          subtitle: 'Slika je uspešno dodata.'
        });
        setTimeout(() => setNotification(null), 3000);
        setSelectedFile(null);
        if (onImageUploaded) onImageUploaded(res.data.data || { ...item, imgSrc: res.data.data?.imgSrc });
        // Refresh the page after a short delay so the new image is shown
        setTimeout(() => {
          window.location.reload();
        }, 2500);
      } else {
        setNotification({ kind: 'error', title: 'Greška', subtitle: res.data?.message || 'Došlo je do greške pri dodavanju slike.' });
        setTimeout(() => setNotification(null), 4000);
      }
    } catch (err) {
      console.error('Greška pri dodavanju slike zalihe:', err);
      setNotification({ kind: 'error', title: 'Greška', subtitle: err.response?.data?.message || 'Došlo je do greške pri dodavanju slike.' });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsUploading(false);
    }
  };

  const handleIncreaseQuantity = async () => {
    const amount = Number(increaseAmount);
    if (!amount || amount <= 0) {
      setNotification({ kind: 'warning', title: 'Upozorenje', subtitle: 'Molimo unesite ispravnu količinu.' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }
    if (!item?.id) return;
    setIsIncreasing(true);
    try {
      const res = await axiosInstance.patch(`/api/ResidentSupply/${item.id}/increase-quantity`, { amount });
      if (res.data?.success) {
        setNotification({
          kind: 'success',
          title: 'Uspešno dodato',
          subtitle: 'Količina je uspešno povećana.'
        });
        setTimeout(() => setNotification(null), 3000);
        setIncreaseAmount(1);
        if (onQuantityIncreased) onQuantityIncreased(res.data.data || { ...item, quantity: (item.quantity || 0) + amount });
        // Return to the detail view
        setView('detail');
      } else {
        setNotification({ kind: 'error', title: 'Greška', subtitle: res.data?.message || 'Došlo je do greške pri povećanju količine.' });
        setTimeout(() => setNotification(null), 4000);
      }
    } catch (err) {
      console.error('Greška pri povećanju količine zalihe:', err);
      setNotification({ kind: 'error', title: 'Greška', subtitle: err.response?.data?.message || 'Došlo je do greške pri povećanju količine.' });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsIncreasing(false);
    }
  };

  const handleUpdate = async () => {
    if (!editName.trim()) {
      setNotification({ kind: 'warning', title: 'Upozorenje', subtitle: 'Molimo unesite naziv zalihe.' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }
    if (editName.trim().length > 30) {
      setNotification({ kind: 'warning', title: 'Upozorenje', subtitle: 'Naziv zalihe može imati najviše 30 karaktera.' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }
    const qty = Number(editQuantity);
    if (!qty || qty <= 0) {
      setNotification({ kind: 'warning', title: 'Upozorenje', subtitle: 'Molimo unesite ispravnu količinu.' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }
    if (!item?.id) return;
    setIsEditing(true);
    try {
      const res = await axiosInstance.put(`/api/ResidentSupply/${item.id}`, {
        itemName: editName.trim(),
        quantity: qty
      });
      if (res.data?.success) {
        setNotification({
          kind: 'success',
          title: 'Uspešno sačuvano',
          subtitle: 'Zaliha je uspešno ažurirana.'
        });
        setTimeout(() => setNotification(null), 3000);
        if (onUpdated) onUpdated(res.data.data || { ...item, itemName: editName.trim(), quantity: qty });
        setView('detail');
      } else {
        setNotification({ kind: 'error', title: 'Greška', subtitle: res.data?.message || 'Došlo je do greške pri ažuriranju zalihe.' });
        setTimeout(() => setNotification(null), 4000);
      }
    } catch (err) {
      console.error('Greška pri ažuriranju zalihe:', err);
      setNotification({ kind: 'error', title: 'Greška', subtitle: err.response?.data?.message || 'Došlo je do greške pri ažuriranju zalihe.' });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsEditing(false);
    }
  };

  return (
    <>
      {isOpen && <div className="edit-sidebar-overlay" onClick={onClose} />}
      <div className={`edit-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="edit-sidebar__header">
          <div className="edit-sidebar__header-title">
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
              {view === 'increase' ? 'Dodaj količinu' : view === 'edit' ? 'Izmeni zalihu' : 'Detalji zalihe'}
            </h3>
            {view === 'detail' && canManage && (
              <button
                type="button"
                className="residents1-card__edit-btn"
                onClick={() => {
                  setEditName(item?.itemName || '');
                  setEditQuantity(item?.quantity ?? 1);
                  setView('edit');
                }}
                aria-label="Izmeni zalihu"
                title="Izmeni"
              >
                <Icons.Pencil />
              </button>
            )}
          </div>
          <Button kind="ghost" size="sm" renderIcon={Close} iconDescription="Zatvori" hasIconOnly onClick={onClose} />
        </div>
        <div className="edit-sidebar__content">
          {loading && (
            <div className="residents1-loading">
              <div className="residents1-spinner" />
              <p className="residents1-loading-text">Učitavanje detalja zalihe...</p>
            </div>
          )}

          {error && !loading && (
            <div className="residents1-empty-state">
              <div className="residents1-empty-icon"><Icons.AlertCircle /></div>
              <h3 className="residents1-empty-title">Greška pri učitavanju</h3>
              <p className="residents1-empty-description">{error}</p>
            </div>
          )}

          {item && !loading && view === 'detail' && (
            <div className="inventory-detail">
              {/* Header */}
              <div className="inventory-detail-header">
                <div className="inventory-detail-title">
                  <h3>{item.itemName || 'Zaliha'}</h3>
                  <span className="inventory-detail-qty">
                    Količina: {item.quantity ?? '-'}
                  </span>
                </div>
              </div>

              {/* Image */}
              <div className="inventory-detail-image">
                {hasImage ? (
                  <button
                    type="button"
                    className="inventory-detail-image__btn"
                    onClick={() => setIsImageModalOpen(true)}
                    aria-label="Uvećaj sliku"
                    title="Klikni za uvećanje"
                  >
                    <img
                      src={item.imgSrc}
                      alt={item.itemName || 'Zaliha'}
                      className="inventory-detail-image__img"
                    />
                  </button>
                ) : (
                  <span className="inventory-detail-image__placeholder">
                    <Package size={48} />
                  </span>
                )}
              </div>

              {/* Image upload */}
              {canManage && (
                <div className="inventory-detail-upload">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,.webp,.bmp"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                  <button
                    type="button"
                    className="inventory-detail-upload__btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    title={hasImage ? 'Zameni sliku' : 'Dodaj sliku'}
                    aria-label={hasImage ? 'Zameni sliku' : 'Dodaj sliku'}
                  >
                    <Icons.Image size={16} />
                    <span>{hasImage ? 'Zameni sliku' : 'Dodaj sliku'}</span>
                  </button>
                  {selectedFile && (
                    <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginTop: '0.5rem' }}>
                      Izabran fajl: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                    </p>
                  )}
                  {selectedFile && (
                    <div className="edit-sidebar__actions" style={{ marginTop: '0.5rem' }}>
                      <Button kind="secondary" size="sm" onClick={() => setSelectedFile(null)} disabled={isUploading}>Otkaži</Button>
                      <Button kind="primary" size="sm" onClick={handleUpload} disabled={isUploading}>
                        {isUploading ? 'Dodavanje...' : 'Dodaj sliku'}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Action buttons */}
              {canManage && (
                <div className="inventory-detail-actions">
                  <Button
                    kind="tertiary"
                    size="sm"
                    className="inventory-detail-actions__add"
                    onClick={() => setView('increase')}
                  >
                    Dodaj količinu
                  </Button>
                  <Button
                    kind="danger--ghost"
                    size="sm"
                    renderIcon={TrashCan}
                    onClick={onDelete}
                    iconDescription="Obriši zalihu"
                    title="Obriši zalihu"
                  >
                    Obriši
                  </Button>
                </div>
              )}

              {/* Info rows */}
              <div className="inventory-detail-info">
                <div className="inventory-detail-row">
                  <span className="detail-label">Naziv:</span>
                  <span className="detail-value">{item.itemName || '-'}</span>
                </div>
                <div className="inventory-detail-row">
                  <span className="detail-label">Količina:</span>
                  <span className="detail-value">{item.quantity ?? '-'}</span>
                </div>
                <div className="inventory-detail-row">
                  <span className="detail-label">Datum kreiranja:</span>
                  <span className="detail-value">{formatDate(item.createdAt)}</span>
                </div>
                <div className="inventory-detail-row">
                  <span className="detail-label">Poslednja izmena:</span>
                  <span className="detail-value">{formatDate(item.updatedAt)}</span>
                </div>
              </div>

              <div className="edit-sidebar__actions" style={{ marginTop: '1rem' }}>
                <Button kind="secondary" size="sm" onClick={onClose}>Zatvori</Button>
              </div>
            </div>
          )}

          {item && !loading && view === 'increase' && (
            <div className="inventory-detail">
              {/* Back button */}
              <button
                type="button"
                className="inventory-detail-back"
                onClick={() => setView('detail')}
              >
                <ChevronLeft size={16} />
                <span>Nazad na zalihu</span>
              </button>

              {/* Current quantity */}
              <div className="inventory-detail-increase">
                <label className="inventory-detail-increase__label">
                  Trenutna količina
                </label>
                <div className="inventory-detail-increase__current">
                  {item.quantity ?? '-'}
                </div>
              </div>

              {/* Amount to add */}
              <div className="inventory-detail-increase">
                <label className="inventory-detail-increase__label">
                  Količina za dodavanje
                </label>
                <NumberInput
                  id="supplyIncreaseAmount"
                  label=""
                  value={increaseAmount}
                  min={1}
                  max={2147483647}
                  onChange={(e, { value }) => setIncreaseAmount(value)}
                  disabled={isIncreasing}
                />
              </div>

              <div className="edit-sidebar__actions" style={{ marginTop: '1rem' }}>
                <Button kind="secondary" size="md" onClick={() => setView('detail')} disabled={isIncreasing}>Nazad</Button>
                <Button kind="primary" size="md" onClick={handleIncreaseQuantity} disabled={isIncreasing}>
                  {isIncreasing ? 'Dodavanje...' : 'Dodaj količinu'}
                </Button>
              </div>
            </div>
          )}

          {item && !loading && view === 'edit' && (
            <div className="inventory-detail">
              {/* Back button */}
              <button
                type="button"
                className="inventory-detail-back"
                onClick={() => setView('detail')}
              >
                <ChevronLeft size={16} />
                <span>Nazad na zalihu</span>
              </button>

              <form className="edit-sidebar__form" onSubmit={(e) => { e.preventDefault(); handleUpdate(); }}>
                <TextInput
                  id="supplyEditName"
                  labelText="Naziv zalihe *"
                  placeholder="Unesite naziv zalihe"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  maxLength={30}
                  disabled={isEditing}
                />
                <NumberInput
                  id="supplyEditQuantity"
                  label="Količina *"
                  value={editQuantity}
                  min={1}
                  max={2147483647}
                  onChange={(e, { value }) => setEditQuantity(value)}
                  disabled={isEditing}
                />
                <div className="edit-sidebar__actions">
                  <Button kind="secondary" onClick={() => setView('detail')} disabled={isEditing}>Otkaži</Button>
                  <Button kind="primary" type="submit" disabled={isEditing}>
                    {isEditing ? 'Čuvanje...' : 'Sačuvaj'}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Image Modal */}
      <ImageModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        imageSrc={item?.imgSrc}
        altText={`Fotografija: ${item?.itemName || 'Zaliha'}`}
      />
    </>
  );
};

// Family Sidebar (Add/Edit relative)
// Document Upload Sidebar
const DocumentUploadSidebar = ({ isOpen, onClose, resident, setNotification, onSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Reset state when sidebar opens
  useEffect(() => {
    if (isOpen) {
      setSelectedFile(null);
      setIsUploading(false);
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setNotification({ kind: 'warning', title: 'Upozorenje', subtitle: 'Molimo izaberite fajl koji želite da dodate.' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('residentId', resident.id);
      formData.append('file', selectedFile);

      const response = await axiosInstance.post('/api/ResidentDocument/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setNotification({
          kind: 'success',
          title: 'Uspešno dodato',
          subtitle: 'Dokument je uspešno dodat.'
        });
        setTimeout(() => setNotification(null), 3000);
        onClose();
        if (onSuccess) onSuccess(response.data.data);
      } else {
        setNotification({ kind: 'error', title: 'Greška', subtitle: response.data.message || 'Došlo je do greške pri dodavanju dokumenta.' });
        setTimeout(() => setNotification(null), 4000);
      }
    } catch (err) {
      console.error('Greška pri dodavanju dokumenta:', err);
      setNotification({ kind: 'error', title: 'Greška', subtitle: err.response?.data?.message || 'Došlo je do greške pri dodavanju dokumenta.' });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      {isOpen && <div className="edit-sidebar-overlay" onClick={onClose} />}
      <div className={`edit-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="edit-sidebar__header">
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
            Dodaj dokument
          </h3>
          <Button kind="ghost" size="sm" renderIcon={Close} iconDescription="Zatvori" hasIconOnly onClick={onClose} />
        </div>
        <div className="edit-sidebar__content">
          <form className="edit-sidebar__form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <FileUploader
              labelTitle=""
              labelDescription="PDF, slike, Word, Excel, tekst (maks 10MB)"
              buttonLabel="Izaberi fajl"
              filenameStatus="edit"
              accept={['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt']}
              multiple={false}
              disabled={isUploading}
              onChange={handleFileChange}
            />
            {selectedFile && (
              <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginTop: '0.5rem' }}>
                Izabran fajl: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
              </p>
            )}
            {isUploading && (
              <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginTop: '0.5rem' }}>
                Dodavanje dokumenta u toku...
              </p>
            )}
            <div className="edit-sidebar__actions">
              <Button kind="secondary" onClick={onClose} disabled={isUploading}>Otkaži</Button>
              <Button kind="primary" type="submit" disabled={isUploading}>
                {isUploading ? 'Dodavanje...' : 'Dodaj dokument'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

// Document Preview Sidebar
const DocumentPreviewSidebar = ({
  isOpen,
  onClose,
  document,
  url,
  loading,
  error,
  onDownload
}) => {
  const fileType = getDocumentFileType(document?.documentName);

  return (
    <>
      {isOpen && <div className="edit-sidebar-overlay" onClick={onClose} />}
      <div className={`edit-sidebar edit-sidebar--wide ${isOpen ? 'open' : ''}`}>
        <div className="edit-sidebar__header">
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {document?.documentName || 'Pregled dokumenta'}
          </h3>
          <Button kind="ghost" size="sm" renderIcon={Close} iconDescription="Zatvori" hasIconOnly onClick={onClose} />
        </div>
        <div className="edit-sidebar__content">
          <div className="residents1-document-preview">
            {loading && (
              <div className="residents1-loading">
                <div className="residents1-spinner" />
                <p className="residents1-loading-text">Učitavanje dokumenta...</p>
              </div>
            )}
            {error && (
              <div className="residents1-empty-state">
                <div className="residents1-empty-icon"><Icons.AlertCircle /></div>
                <h3 className="residents1-empty-title">Greška pri učitavanju</h3>
                <p className="residents1-empty-description">{error}</p>
              </div>
            )}
            {!loading && !error && url && (
              <div className="residents1-document-preview__content">
                {fileType === 'image' ? (
                  <img
                    src={url}
                    alt={document?.documentName}
                    className="residents1-document-preview__image"
                  />
                ) : fileType === 'pdf' ? (
                  <iframe
                    src={url}
                    title={document?.documentName}
                    className="residents1-document-preview__iframe"
                  />
                ) : (
                  <div className="residents1-document-preview__unsupported">
                    <div className="residents1-empty-icon"><Icons.FileText /></div>
                    <h3 className="residents1-empty-title">Pregled nije dostupan</h3>
                    <p className="residents1-empty-description">
                      Ovaj tip dokumenta se ne može prikazati u pregledu. Preuzmite fajl da biste ga otvorili.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="edit-sidebar__actions" style={{ marginTop: '1rem' }}>
            <Button kind="secondary" size="md" onClick={onClose}>Zatvori</Button>
            <Button kind="primary" size="md" onClick={() => onDownload(document)} disabled={!document}>
              <Icons.Download />
              <span style={{ marginLeft: '0.5rem' }}>Preuzmi</span>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

const FamilySidebar = ({ isOpen, onClose, resident, setNotification, onSuccess, existingMember }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contactPhone: '',
    contactEmail: ''
  });

  // Initialize form with existing member data when sidebar opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: existingMember?.name || '',
        contactPhone: existingMember?.contactPhone || '',
        contactEmail: existingMember?.contactEmail || ''
      });
    }
  }, [isOpen, existingMember]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setNotification({ kind: 'warning', title: 'Upozorenje', subtitle: 'Molimo unesite ime i prezime srodnika.' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    const phone = formData.contactPhone.trim();
    if (!phone) {
      setNotification({ kind: 'warning', title: 'Upozorenje', subtitle: 'Molimo unesite broj telefona srodnika.' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }
    // Phone may contain digits, spaces, +, -, parentheses
    if (!/^[0-9+\s\-()]+$/.test(phone)) {
      setNotification({ kind: 'error', title: 'Neispravan broj telefona', subtitle: 'Broj telefona može sadržati samo cifre, znak +, razmake, crtice i zagrade.' });
      setTimeout(() => setNotification(null), 4000);
      return;
    }

    setIsSaving(true);
    try {
      let response;
      if (existingMember) {
        response = await axiosInstance.put(`/api/ResidentFamily/${existingMember.id}`, {
          name: formData.name.trim(),
          contactPhone: formData.contactPhone.trim(),
          contactEmail: formData.contactEmail.trim()
        });
      } else {
        response = await axiosInstance.post('/api/ResidentFamily', {
          residentId: resident.id,
          name: formData.name.trim(),
          contactPhone: formData.contactPhone.trim(),
          contactEmail: formData.contactEmail.trim()
        });
      }
      if (response.data.success) {
        setNotification({
          kind: 'success',
          title: 'Uspešno sačuvano',
          subtitle: existingMember ? 'Podaci o srodniku su uspešno ažurirani.' : 'Srodnik je uspešno dodat.'
        });
        setTimeout(() => setNotification(null), 3000);
        onClose();
        if (onSuccess) onSuccess(response.data.data);
      } else {
        setNotification({ kind: 'error', title: 'Greška', subtitle: response.data.message || 'Došlo je do greške.' });
        setTimeout(() => setNotification(null), 4000);
      }
    } catch (err) {
      console.error('Greška pri čuvanju srodnika:', err);
      setNotification({ kind: 'error', title: 'Greška', subtitle: err.response?.data?.message || 'Došlo je do greške pri čuvanju srodnika.' });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {isOpen && <div className="edit-sidebar-overlay" onClick={onClose} />}
      <div className={`edit-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="edit-sidebar__header">
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
            {existingMember ? 'Izmeni srodnika' : 'Dodaj srodnika'}
          </h3>
          <Button kind="ghost" size="sm" renderIcon={Close} iconDescription="Zatvori" hasIconOnly onClick={onClose} />
        </div>
        <div className="edit-sidebar__content">
          <form className="edit-sidebar__form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <TextInput
              id="familyName"
              labelText="Ime i prezime *"
              placeholder="Unesite ime i prezime srodnika"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              disabled={isSaving}
            />
            <TextInput
              id="familyPhone"
              labelText="Kontakt telefon"
              placeholder="+381 6x xxx xxxx"
              value={formData.contactPhone}
              onChange={(e) => handleInputChange('contactPhone', e.target.value)}
              disabled={isSaving}
            />
            <TextInput
              id="familyEmail"
              labelText="Kontakt e-pošta"
              placeholder="user@example.com"
              type="email"
              value={formData.contactEmail}
              onChange={(e) => handleInputChange('contactEmail', e.target.value)}
              disabled={isSaving}
            />
            <div className="edit-sidebar__actions">
              <Button kind="secondary" onClick={onClose} disabled={isSaving}>Otkaži</Button>
              <Button kind="primary" type="submit" disabled={isSaving}>
                {isSaving ? 'Čuvanje...' : 'Sačuvaj'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

// Relatives Tab
const MAX_FAMILY_MEMBERS = 4;

const RelativesTab = ({ resident, familyData, loading, onAdd, onEdit, onDelete, canManage = true }) => {
  if (loading) {
    return (
      <div className="residents1-tab-content">
        {/* Header skeleton */}
        <div className="residents1-relatives-header">
          <div className="residents1-relatives-header__info">
            <div className="r1-skel-line r1-skel-line--rel-title" />
            <div className="r1-skel-line r1-skel-line--rel-count" />
          </div>
          <div className="r1-skel-line r1-skel-line--rel-btn" />
        </div>

        {/* Relatives grid skeleton */}
        <div className="residents1-relatives-grid">
          {Array.from({ length: 4 }).map((_, i) => (
            <div className="r1-skel-rel-card" key={i}>
              <div className="r1-skel-rel-head">
                <div className="r1-skel-rel-avatar" />
                <div className="r1-skel-line r1-skel-line--rel-name" />
                <div className="r1-skel-rel-actions">
                  <div className="r1-skel-rel-action" />
                  <div className="r1-skel-rel-action" />
                </div>
              </div>
              <div className="r1-skel-rel-contact">
                <div className="r1-skel-line r1-skel-line--rel-contact" />
                <div className="r1-skel-line r1-skel-line--rel-contact" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const members = familyData || [];
  const atLimit = members.length >= MAX_FAMILY_MEMBERS;

  const getInitials = (name) => {
    if (!name) return '?';
    return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  };

  return (
    <div className="residents1-tab-content">
      {/* Header with counter and add button */}
      <div className="residents1-relatives-header">
        <div className="residents1-relatives-header__info">
          <h3 className="residents1-relatives-header__title">Srodnici</h3>
          <span className="residents1-relatives-header__count">
            {members.length} / {MAX_FAMILY_MEMBERS}
          </span>
        </div>
        {canManage && (
          <Button
            kind="primary"
            onClick={onAdd}
            disabled={atLimit}
            title={atLimit ? 'Maksimalan broj srodnika je 4' : 'Dodaj srodnika'}
          >
            <Icons.Plus />
            <span style={{ marginLeft: '0.5rem' }}>Dodaj srodnika</span>
          </Button>
        )}
      </div>

      {members.length === 0 ? (
        <div className="residents1-empty-state">
          <div className="residents1-empty-icon"><Icons.Users /></div>
          <h3 className="residents1-empty-title">Nema registrovanih srodnika</h3>
          <p className="residents1-empty-description">Trenutno nema registrovanih srodnika za ovog stanara.</p>
        </div>
      ) : (
        <div className="residents1-relatives-grid">
          {members.map(member => (
            <div key={member.id} className="residents1-relative-card">
              <div className="residents1-relative-header">
                <div className="residents1-relative-avatar">{getInitials(member.name)}</div>
                <div className="residents1-relative-info">
                  <h4>{member.name}</h4>
                </div>
                {canManage && (
                  <div className="residents1-relative-actions">
                    <button
                      className="residents1-relative-action-btn"
                      onClick={() => onEdit(member)}
                      disabled={resident?.isActive === false}
                      aria-label={`Izmeni ${member.name}`}
                      title="Izmeni"
                      style={resident?.isActive === false ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                    >
                      <Icons.Edit />
                    </button>
                    <button
                      className="residents1-relative-action-btn residents1-relative-action-btn--danger"
                      onClick={() => onDelete(member)}
                      disabled={resident?.isActive === false}
                      aria-label={`Obriši ${member.name}`}
                      title="Obriši"
                      style={resident?.isActive === false ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                    >
                      <TrashCan />
                    </button>
                  </div>
                )}
              </div>
              <div className="residents1-relative-contact">
                {member.contactPhone && (
                  <div className="residents1-relative-contact-item">
                    <Icons.Phone />
                    <span>{member.contactPhone}</span>
                  </div>
                )}
                {member.contactEmail && (
                  <div className="residents1-relative-contact-item">
                    <Icons.Mail />
                    <span>{member.contactEmail}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ===========================================
// Pocket Money (Džeparac) Tab
// ===========================================
const PocketMoneyTab = ({ resident, setNotification }) => {
  // Persist the active sub-tab (Stanje / Transakcije) so it survives a page refresh
  const subTabStorageKey = `pocket-money-subtab:${resident?.id || 'unknown'}`;
  const [activeSubTab, setActiveSubTab] = useState(() => {
    const saved = localStorage.getItem(subTabStorageKey);
    return saved === 'transactions' ? 'transactions' : 'balance';
  });

  // Save the active sub-tab whenever it changes
  useEffect(() => {
    localStorage.setItem(subTabStorageKey, activeSubTab);
  }, [activeSubTab, subTabStorageKey]);

  // Receipt upload sidebar state
  const [receiptSidebarOpen, setReceiptSidebarOpen] = useState(false);

  // Deposit sidebar state
  const [depositSidebarOpen, setDepositSidebarOpen] = useState(false);

  // Budget (balance) state
  const [budget, setBudget] = useState(null);
  const [budgetLoading, setBudgetLoading] = useState(true);
  const [budgetError, setBudgetError] = useState(null);

  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsError, setTransactionsError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 10;

  // Selected transaction for the details side panel
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Image zoom modal
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [zoomOrigin, setZoomOrigin] = useState('50% 50%');

  // Fetch the resident's budget (balance)
  useEffect(() => {
    if (!resident?.id) return;
    let cancelled = false;
    const fetchBudget = async () => {
      setBudgetLoading(true);
      setBudgetError(null);
      try {
        const res = await axiosInstance.get(`/api/ResidentBudget/resident/${resident.id}`);
        if (!cancelled) {
          if (res.data?.success) {
            setBudget(res.data.data || null);
          } else {
            setBudget(null);
          }
        }
      } catch (err) {
        console.error('Greška pri učitavanju džeparca:', err);
        if (!cancelled) {
          setBudgetError('Nije moguće učitati stanje džeparca.');
          setBudget(null);
        }
      } finally {
        if (!cancelled) setBudgetLoading(false);
      }
    };
    fetchBudget();
    return () => { cancelled = true; };
  }, [resident?.id]);

  // Fetch transactions once we have the budget id (and on page change)
  useEffect(() => {
    if (!budget?.id) return;
    let cancelled = false;
    const fetchTransactions = async () => {
      setTransactionsLoading(true);
      setTransactionsError(null);
      try {
        const res = await axiosInstance.get(
          `/api/ResidentBudgetTransaction/budget/${budget.id}?pageNumber=${pageNumber}&pageSize=${pageSize}`
        );
        if (!cancelled) {
          if (res.data?.success) {
            setTransactions(res.data.data || []);
            setPagination(res.data.pagination || null);
          } else {
            setTransactions([]);
            setPagination(null);
          }
        }
      } catch (err) {
        console.error('Greška pri učitavanju transakcija džeparca:', err);
        if (!cancelled) {
          setTransactionsError('Nije moguće učitati transakcije džeparca.');
          setTransactions([]);
          setPagination(null);
        }
      } finally {
        if (!cancelled) setTransactionsLoading(false);
      }
    };
    fetchTransactions();
    return () => { cancelled = true; };
  }, [budget?.id, pageNumber]);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('sr-RS').format(Number(amount) || 0) + ' RSD';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('sr-Latn-RS', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString('sr-Latn-RS', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Parse receiptDetails JSON string into a readable object.
  // Handles all the formats the backend may return:
  //   1. A valid JSON object directly: {"merchantName": "...", ...}
  //   2. A fragment with a "data" key: "data": { "merchantName": "...", ... }
  //   3. A wrapped object with a "data" key: {"data": { "merchantName": "...", ... }}
  const parseReceipt = (raw) => {
    if (!raw) return null;
    const tryParse = (str) => {
      try {
        return JSON.parse(str);
      } catch (e) {
        return null;
      }
    };

    // Try direct parse first
    let parsed = tryParse(raw.trim());
    // If that fails, try wrapping in braces (fragment like `"data": {...}`)
    if (!parsed) {
      parsed = tryParse(`{${raw.trim()}}`);
    }
    if (!parsed) return null;

    // If the parsed object has a "data" key, unwrap it
    if (parsed && typeof parsed === 'object' && 'data' in parsed) {
      const data = parsed.data;
      if (typeof data === 'string') {
        return tryParse(data) || null;
      }
      return data || null;
    }

    // Otherwise the object itself is the receipt data
    return parsed;
  };

  const subTabStyle = (active) => ({
    flex: 1,
    padding: '0.625rem 1rem',
    border: 'none',
    borderRadius: 'var(--r1-radius-sm)',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    backgroundColor: active ? 'var(--r1-interactive-primary)' : 'transparent',
    color: active ? 'var(--r1-text-inverse)' : 'var(--r1-text-secondary)',
    transition: 'background-color 0.15s ease, color 0.15s ease'
  });

  return (
    <div className="residents1-tab-content">
      {/* Sub-tabs: Stanje / Transakcije */}
      <div style={{
        display: 'flex',
        gap: '0.25rem',
        padding: '0.25rem',
        backgroundColor: 'var(--r1-bg-secondary)',
        borderRadius: 'var(--r1-radius-md)',
        border: '1px solid var(--r1-border-subtle)',
        marginBottom: '1.5rem'
      }}>
        <button style={subTabStyle(activeSubTab === 'balance')} onClick={() => setActiveSubTab('balance')}>
          Stanje
        </button>
        <button style={subTabStyle(activeSubTab === 'transactions')} onClick={() => setActiveSubTab('transactions')}>
          Transakcije
        </button>
      </div>

      {activeSubTab === 'balance' ? (
        /* ============ BALANCE TAB ============ */
        <div>
          {budgetLoading ? (
            <div className="residents1-cards-grid">
              {Array.from({ length: 3 }).map((_, i) => (
                <div className="r1-skel-card" key={i}>
                  <div className="r1-skel-card-head">
                    <div className="r1-skel-icon" />
                    <div className="r1-skel-line r1-skel-line--card-title" />
                  </div>
                  <div className="r1-skel-balance">
                    <div className="r1-skel-line r1-skel-line--balance-amount" />
                    <div className="r1-skel-line r1-skel-line--balance-label" />
                  </div>
                </div>
              ))}
            </div>
          ) : budgetError ? (
            <div className="residents1-empty-state">
              <div className="residents1-empty-icon"><Icons.Wallet /></div>
              <h3 className="residents1-empty-title">Nije moguće učitati podatke</h3>
              <p className="residents1-empty-description">{budgetError}</p>
            </div>
          ) : (
            <div className="residents1-cards-grid">
              <Card title="Trenutno stanje" icon={Icons.Wallet}>
                <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
                  <div style={{ fontSize: '2.25rem', fontWeight: '700', color: '#9fb746' }}>
                    {formatAmount(budget?.balance)}
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--r1-text-secondary)', marginTop: '0.25rem' }}>
                    Raspoloživi džeparac
                  </div>
                </div>
              </Card>

              <Card title="Ukupne uplate" icon={Icons.ArrowUpRight}>
                <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '600', color: 'var(--r1-status-success)' }}>
                    {formatAmount(
                      (transactions || [])
                        .filter(t => t.type === 'Priliv')
                        .reduce((sum, t) => sum + (Number(t.amount) || 0), 0)
                    )}
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--r1-text-secondary)', marginTop: '0.25rem' }}>
                    {(transactions || []).filter(t => t.type === 'Priliv').length} uplata
                  </div>
                </div>
              </Card>

              <Card title="Ukupne potrošnje" icon={Icons.ArrowDownRight}>
                <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '600', color: 'var(--r1-status-error)' }}>
                    {formatAmount(
                      (transactions || [])
                        .filter(t => t.type === 'Odliv')
                        .reduce((sum, t) => sum + (Number(t.amount) || 0), 0)
                    )}
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--r1-text-secondary)', marginTop: '0.25rem' }}>
                    {(transactions || []).filter(t => t.type === 'Odliv').length} potrošnji
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      ) : (
        /* ============ TRANSACTIONS TAB ============ */
        <div>
          <div className="residents1-relatives-header">
            <div className="residents1-relatives-header__info">
              <h3 className="residents1-relatives-header__title">Istorija transakcija</h3>
              <span className="residents1-relatives-header__count">
                {pagination?.totalCount ?? transactions.length}
              </span>
            </div>
            <div className="residents1-transactions-actions">
              <Button
                kind="secondary"
                size="sm"
                onClick={() => setReceiptSidebarOpen(true)}
                disabled={resident?.isActive === false}
              >
                + Trošak
              </Button>
              <Button
                kind="primary"
                size="sm"
                onClick={() => setDepositSidebarOpen(true)}
                disabled={resident?.isActive === false}
              >
                + Depozit
              </Button>
            </div>
          </div>

          {transactionsLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <div className="r1-skel-txn" key={i}>
                  <div className="r1-skel-txn-icon" />
                  <div className="r1-skel-txn-body">
                    <div className="r1-skel-line r1-skel-line--txn-title" />
                    <div className="r1-skel-line r1-skel-line--txn-meta" />
                  </div>
                  <div className="r1-skel-line r1-skel-line--txn-amount" />
                </div>
              ))}
            </div>
          ) : transactionsError ? (
            <div className="residents1-empty-state">
              <div className="residents1-empty-icon"><Icons.Wallet /></div>
              <h3 className="residents1-empty-title">Nije moguće učitati podatke</h3>
              <p className="residents1-empty-description">{transactionsError}</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="residents1-empty-state">
              <div className="residents1-empty-icon"><Icons.Wallet /></div>
              <h3 className="residents1-empty-title">Nema transakcija</h3>
              <p className="residents1-empty-description">Trenutno nema zabeleženih transakcija džeparca za ovog stanara.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {transactions.map((t) => {
                const isIncome = t.type === 'Priliv';
                const receipt = parseReceipt(t.receiptDetails);
                return (
                  <div
                    key={t.id}
                    onClick={() => setSelectedTransaction(t)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.75rem 1rem',
                      backgroundColor: 'var(--r1-bg-secondary)',
                      borderRadius: 'var(--r1-radius-md)',
                      border: '1px solid var(--r1-border-subtle)',
                      gap: '1rem',
                      flexWrap: 'wrap',
                      cursor: 'pointer',
                      transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--r1-interactive-primary)';
                      e.currentTarget.style.boxShadow = 'var(--r1-shadow-sm)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--r1-border-subtle)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: '1 1 auto', minWidth: '200px' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: 'var(--r1-radius-full)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        backgroundColor: isIncome ? 'var(--r1-status-success-bg)' : 'var(--r1-status-error-bg)',
                        color: isIncome ? 'var(--r1-status-success)' : 'var(--r1-status-error)'
                      }}>
                        {isIncome ? <Icons.ArrowUpRight /> : <Icons.ArrowDownRight />}
                      </div>
                      <div>
                        <div style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--r1-text-primary)' }}>
                          {isIncome
                            ? `Priliv${t.payerName ? ` · ${t.payerName}` : ''}`
                            : `Odliv${receipt?.merchantName ? ` - ${receipt.merchantName}` : ''}`}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--r1-text-secondary)' }}>
                          {formatDateTime(t.createdAt)}
                        </div>
                      </div>
                    </div>
                    <div style={{
                      fontSize: '0.9375rem',
                      fontWeight: '600',
                      color: isIncome ? 'var(--r1-status-success)' : 'var(--r1-status-error)',
                      whiteSpace: 'nowrap'
                    }}>
                      {isIncome ? '+' : '−'} {formatAmount(t.amount)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="residents1-notes-pagination">
              <button
                className="residents1-notes-pagination__btn"
                onClick={() => setPageNumber(pagination.pageNumber - 1)}
                disabled={!pagination.hasPreviousPage || transactionsLoading}
                aria-label="Prethodna strana"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="residents1-notes-pagination__info">
                {pagination.pageNumber} / {pagination.totalPages}
              </span>
              <button
                className="residents1-notes-pagination__btn"
                onClick={() => setPageNumber(pagination.pageNumber + 1)}
                disabled={!pagination.hasNextPage || transactionsLoading}
                aria-label="Sledeća strana"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Transaction Details Side Panel */}
      {selectedTransaction && (() => {
        const t = selectedTransaction;
        const receipt = parseReceipt(t.receiptDetails);
        return (
          <>
            <div className="pocketmoney-overlay open" onClick={() => setSelectedTransaction(null)} />
            <div className="pocketmoney-side-panel open">
              <div className="side-panel-header">
                <h2>Detalji transakcije</h2>
                <Button
                  kind="ghost"
                  size="sm"
                  renderIcon={Close}
                  hasIconOnly
                  iconDescription="Zatvori"
                  onClick={() => setSelectedTransaction(null)}
                />
              </div>

              <div className="side-panel-content">
                {/* Priliv / Odliv header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    backgroundColor: t.type === 'Priliv' ? 'var(--r1-status-success-bg)' : 'var(--r1-status-error-bg)',
                    color: t.type === 'Priliv' ? 'var(--r1-status-success)' : 'var(--r1-status-error)'
                  }}>
                    {t.type === 'Priliv' ? <Icons.ArrowUpRight /> : <Icons.ArrowDownRight />}
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.7, color: 'var(--r1-text-secondary)' }}>
                      {t.type === 'Priliv' ? 'Priliv' : 'Odliv'}
                    </div>
                    <div style={{
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      color: t.type === 'Priliv' ? 'var(--r1-status-success)' : 'var(--r1-status-error)'
                    }}>
                      {t.type === 'Priliv' ? '+' : '−'} {formatAmount(t.amount)}
                    </div>
                  </div>
                </div>

                {/* Detalji priliva - ko je, kada i koliko uplatio */}
                {t.type === 'Priliv' && (
                  <div style={{
                    padding: '1rem',
                    backgroundColor: 'var(--cds-layer-01)',
                    borderRadius: '8px',
                    border: '1px solid var(--cds-border-subtle-01)',
                    marginBottom: '1.5rem'
                  }}>
                    <h4 style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      margin: '0 0 0.75rem 0',
                      color: 'var(--cds-text-primary)'
                    }}>
                      Detalji uplate
                    </h4>

                    {t.payerName && (
                      <div className="pocketmoney-detail-row">
                        <span className="pocketmoney-detail-row__label">Uplatio</span>
                        <span className="pocketmoney-detail-row__value">{t.payerName}</span>
                      </div>
                    )}
                    {t.createdAt && (
                      <div className="pocketmoney-detail-row">
                        <span className="pocketmoney-detail-row__label">Datum uplate</span>
                        <span className="pocketmoney-detail-row__value">{formatDateTime(t.createdAt)}</span>
                      </div>
                    )}
                    {t.amount != null && (
                      <div className="pocketmoney-detail-row">
                        <span className="pocketmoney-detail-row__label">Iznos</span>
                        <span className="pocketmoney-detail-row__value" style={{ color: 'var(--r1-status-success)', fontWeight: '600' }}>
                          {formatAmount(t.amount)}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Slika računa */}
                {t.imgSrc && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <img
                      src={t.imgSrc}
                      alt="Račun"
                      className="pocketmoney-receipt-image"
                      onClick={() => setImageModalOpen(true)}
                      style={{ cursor: 'zoom-in' }}
                    />
                  </div>
                )}

                {/* Detalji računa */}
                {receipt && (
                  <div style={{
                    padding: '1rem',
                    backgroundColor: 'var(--cds-layer-01)',
                    borderRadius: '8px',
                    border: '1px solid var(--cds-border-subtle-01)',
                    marginBottom: '1.5rem'
                  }}>
                    <h4 style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      margin: '0 0 0.75rem 0',
                      color: 'var(--cds-text-primary)'
                    }}>
                      Detalji računa
                    </h4>

                    {receipt.merchantName && (
                      <div className="pocketmoney-detail-row">
                        <span className="pocketmoney-detail-row__label">Prodavnica</span>
                        <span className="pocketmoney-detail-row__value">{receipt.merchantName}</span>
                      </div>
                    )}
                    {receipt.transactionDate && (
                      <div className="pocketmoney-detail-row">
                        <span className="pocketmoney-detail-row__label">Datum kupovine</span>
                        <span className="pocketmoney-detail-row__value">{formatDate(receipt.transactionDate)}</span>
                      </div>
                    )}
                    {receipt.subtotal != null && (
                      <div className="pocketmoney-detail-row">
                        <span className="pocketmoney-detail-row__label">Međuzbir</span>
                        <span className="pocketmoney-detail-row__value">{formatAmount(receipt.subtotal)}</span>
                      </div>
                    )}
                    {receipt.tax != null && (
                      <div className="pocketmoney-detail-row">
                        <span className="pocketmoney-detail-row__label">PDV</span>
                        <span className="pocketmoney-detail-row__value">{formatAmount(receipt.tax)}</span>
                      </div>
                    )}
                    {receipt.total != null && (
                      <div className="pocketmoney-detail-row">
                        <span className="pocketmoney-detail-row__label">Ukupno</span>
                        <span className="pocketmoney-detail-row__value" style={{ color: '#9fb746', fontWeight: '600' }}>
                          {formatAmount(receipt.total)}
                        </span>
                      </div>
                    )}
                    {receipt.currency && (
                      <div className="pocketmoney-detail-row">
                        <span className="pocketmoney-detail-row__label">Valuta</span>
                        <span className="pocketmoney-detail-row__value">{receipt.currency}</span>
                      </div>
                    )}

                    {/* Stavke */}
                    {Array.isArray(receipt.items) && receipt.items.length > 0 && (
                      <div style={{ marginTop: '1rem' }}>
                        <h5 style={{
                          fontSize: '0.8125rem',
                          fontWeight: '600',
                          margin: '0 0 0.5rem 0',
                          color: 'var(--cds-text-secondary)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Stavke ({receipt.items.length})
                        </h5>
                        {receipt.items.map((item, idx) => (
                          <div key={idx} className="pocketmoney-item-row">
                            <div className="pocketmoney-item-desc">{item.description}</div>
                            <div className="pocketmoney-item-qty">×{item.quantity}</div>
                            <div className="pocketmoney-item-price">{formatAmount(item.totalPrice ?? item.price)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Zatvori */}
                <Button
                  kind="secondary"
                  size="md"
                  style={{ marginTop: 'auto' }}
                  onClick={() => setSelectedTransaction(null)}
                >
                  Zatvori
                </Button>
              </div>
            </div>
          </>
        );
      })()}

      {/* Image Zoom Modal */}
      {imageModalOpen && (
        <Modal
          open
          onRequestClose={() => {
            setImageModalOpen(false);
            setZoomLevel(1);
            setZoomOrigin('50% 50%');
          }}
          onSecondarySubmit={() => {
            setImageModalOpen(false);
            setZoomLevel(1);
            setZoomOrigin('50% 50%');
          }}
          modalHeading="Račun"
          size="sm"
          passiveModal
        >
          {selectedTransaction?.imgSrc && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '0.5rem 0',
                overflow: 'hidden'
              }}
            >
              <img
                src={selectedTransaction.imgSrc}
                alt="Račun"
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = ((e.clientX - rect.left) / rect.width) * 100;
                  const y = ((e.clientY - rect.top) / rect.height) * 100;
                  setZoomOrigin(`${x}% ${y}%`);
                }}
                onMouseEnter={() => setZoomLevel(2.5)}
                onMouseLeave={() => {
                  setZoomLevel(1);
                  setZoomOrigin('50% 50%');
                }}
                style={{
                  maxWidth: '100%',
                  maxHeight: '60vh',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  cursor: 'zoom-in',
                  transform: `scale(${zoomLevel})`,
                  transformOrigin: zoomOrigin,
                  transition: 'transform 0.15s ease-out'
                }}
              />
            </div>
          )}
        </Modal>
      )}

      {/* Receipt Upload Sidebar */}
      {receiptSidebarOpen && (
        <>
          <div className="edit-sidebar-overlay" onClick={() => setReceiptSidebarOpen(false)} />
          <div className="edit-sidebar open receipt-sidebar">
            <div className="edit-sidebar__header">
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
                Dodaj račun
              </h3>
              <Button kind="ghost" size="sm" renderIcon={Close} iconDescription="Zatvori" hasIconOnly onClick={() => setReceiptSidebarOpen(false)} />
            </div>
            <div className="edit-sidebar__content">
              <ReceiptUploadForm
                resident={resident}
                setNotification={setNotification}
                onClose={() => setReceiptSidebarOpen(false)}
                onSuccess={() => {
                  setReceiptSidebarOpen(false);
                  // Refresh the page after a successful upload so updated data is reloaded
                  window.location.reload();
                }}
              />
            </div>
          </div>
        </>
      )}

      {/* Deposit Sidebar */}
      {depositSidebarOpen && (
        <>
          <div className="edit-sidebar-overlay" onClick={() => setDepositSidebarOpen(false)} />
          <div className="edit-sidebar open">
            <div className="edit-sidebar__header">
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
                Uplata depozita
              </h3>
              <Button kind="ghost" size="sm" renderIcon={Close} iconDescription="Zatvori" hasIconOnly onClick={() => setDepositSidebarOpen(false)} />
            </div>
            <div className="edit-sidebar__content">
              <DepositForm
                resident={resident}
                setNotification={setNotification}
                onClose={() => setDepositSidebarOpen(false)}
                onSuccess={() => {
                  setDepositSidebarOpen(false);
                  // Refresh the page after a successful deposit so updated data is reloaded
                  window.location.reload();
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ===========================================
// Deposit Form (add funds to resident's pocket money)
// ===========================================
const DepositForm = ({ resident, setNotification, onClose, onSuccess }) => {
  const [amount, setAmount] = useState(0);
  const [payerName, setPayerName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!amount || Number(amount) <= 0) {
      setNotification({ kind: 'warning', title: 'Upozorenje', subtitle: 'Molimo unesite ispravan iznos uplate.' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    if (!payerName.trim()) {
      setNotification({ kind: 'warning', title: 'Upozorenje', subtitle: 'Molimo unesite ime uplatioca.' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        amount: Number(amount),
        residentId: resident.id,
        payerName: payerName.trim()
      };

      const response = await axiosInstance.post('/api/Receipt/add-funds', payload);

      if (response.data?.success) {
        setNotification({ kind: 'success', title: 'Uspešno', subtitle: 'Depozit je uspešno uplaćen.' });
        setTimeout(() => setNotification(null), 3000);
        onClose();
        if (onSuccess) onSuccess();
      } else {
        setNotification({ kind: 'error', title: 'Greška', subtitle: response.data?.message || 'Došlo je do greške pri uplati depozita.' });
        setTimeout(() => setNotification(null), 4000);
      }
    } catch (err) {
      console.error('Greška pri uplati depozita:', err);
      setNotification({ kind: 'error', title: 'Greška', subtitle: err.response?.data?.message || 'Došlo je do greške pri uplati depozita.' });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="edit-sidebar__form" onSubmit={handleSubmit}>
      <NumberInput
        id="deposit-amount"
        label="Iznos uplate (RSD)"
        helperText="Unesite količinu novca koja se uplaćuje."
        value={amount}
        min={0}
        step={0.01}
        onChange={(e) => setAmount(e.target.value)}
        disabled={isSubmitting}
      />

      <TextInput
        id="deposit-payer"
        labelText="Ime uplatioca"
        placeholder="Npr. Milica Petrović"
        helperText="Ko je uplatio novac."
        value={payerName}
        onChange={(e) => setPayerName(e.target.value)}
        disabled={isSubmitting}
      />

      <div className="edit-sidebar__actions">
        <Button kind="secondary" onClick={onClose} disabled={isSubmitting}>Otkaži</Button>
        <Button kind="primary" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Uplaćivanje...' : 'Uplati'}
        </Button>
      </div>
    </form>
  );
};

// ===========================================
// Receipt Upload Form (parse + confirm)
// ===========================================
const ReceiptUploadForm = ({ resident, setNotification, onClose, onSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [parsedReceipt, setParsedReceipt] = useState(null);
  const [parseError, setParseError] = useState(null);
  // Editable draft of the parsed receipt
  const [draftReceipt, setDraftReceipt] = useState(null);
  // Which field is currently being edited (click-to-edit)
  const [editingField, setEditingField] = useState(null);
  // Current step of the 3-step flow
  const [currentStep, setCurrentStep] = useState(0);
  // Step transition animation state
  const [stepTransition, setStepTransition] = useState('entering');

  const steps = [
    { label: 'Slika', description: 'Izaberite sliku računa' },
    { label: 'Parsiranje', description: 'Izdvajanje podataka' },
    { label: 'Potvrda', description: 'Pregled i potvrda' },
  ];

  // Animate step change: fade out current, then switch and fade in next
  const goToStep = (targetStep) => {
    if (targetStep === currentStep) return;
    setStepTransition('exiting');
    setTimeout(() => {
      setCurrentStep(targetStep);
      setStepTransition('entering');
    }, 150);
  };

  const prevStep = () => {
    if (currentStep > 0) {
      goToStep(currentStep - 1);
    }
  };

  const handleStepClick = (targetStep) => {
    // Allow going back
    if (targetStep < currentStep) {
      goToStep(targetStep);
      return;
    }
    if (targetStep === currentStep) return;

    // Going forward
    if (targetStep > currentStep + 1) {
      setNotification({ kind: 'warning', title: 'Upozorenje', subtitle: 'Morate završiti trenutni korak pre nego što nastavite dalje.' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    // Step 1 requires a selected file
    if (targetStep === 1 && !selectedFile) {
      setNotification({ kind: 'warning', title: 'Upozorenje', subtitle: 'Prvo morate izabrati sliku računa.' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }
    // Step 2 requires parsed data
    if (targetStep === 2 && !draftReceipt) {
      setNotification({ kind: 'warning', title: 'Upozorenje', subtitle: 'Prvo morate parsirati račun.' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    goToStep(targetStep);
  };

  // Reset state when a new file is selected
  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    setParsedReceipt(null);
    setDraftReceipt(null);
    setEditingField(null);
    setParseError(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(file ? URL.createObjectURL(file) : null);
    // Automatically advance to the parsing step once an image is selected
    if (file) {
      goToStep(1);
    }
  };

  // Update a top-level field of the draft receipt
  const updateField = (key, value) => {
    setDraftReceipt(prev => ({ ...prev, [key]: value }));
  };

  const handleParse = async () => {
    if (!selectedFile) {
      setNotification({ kind: 'warning', title: 'Upozorenje', subtitle: 'Molimo izaberite sliku računa.' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    setIsParsing(true);
    setParseError(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axiosInstance.post('/api/Receipt/parse', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data?.success) {
        const parsed = response.data.data || null;
        setParsedReceipt(parsed);
        setDraftReceipt(parsed ? JSON.parse(JSON.stringify(parsed)) : null);
        // Automatically advance to the confirmation step once parsing succeeds
        goToStep(2);
      } else {
        setParseError(response.data?.message || 'Nije moguće izvući podatke iz slike.');
      }
    } catch (err) {
      console.error('Greška pri parsiranju računa:', err);
      setParseError(err.response?.data?.message || 'Nije moguće izvući podatke iz slike.');
    } finally {
      setIsParsing(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !draftReceipt) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('residentId', resident.id);
      // Send the edited receipt as a "data" fragment (no wrapping braces)
      formData.append('receiptDetails', `"data": ${JSON.stringify(draftReceipt)}`);
      formData.append('file', selectedFile);

      const response = await axiosInstance.post('/api/Receipt/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data?.success) {
        setNotification({ kind: 'success', title: 'Uspešno', subtitle: 'Račun je uspešno dodat.' });
        setTimeout(() => setNotification(null), 3000);
        onClose();
        if (onSuccess) onSuccess();
      } else {
        setNotification({ kind: 'error', title: 'Greška', subtitle: response.data?.message || 'Došlo je do greške pri dodavanju računa.' });
        setTimeout(() => setNotification(null), 4000);
      }
    } catch (err) {
      console.error('Greška pri dodavanju računa:', err);
      setNotification({ kind: 'error', title: 'Greška', subtitle: err.response?.data?.message || 'Došlo je do greške pri dodavanju računa.' });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsUploading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('sr-RS').format(Number(amount) || 0) + ' RSD';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('sr-Latn-RS', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <form className="edit-sidebar__form" onSubmit={(e) => { e.preventDefault(); if (draftReceipt) handleUpload(); }}>
      {/* Progress indicator (custom circle style, consistent with activity creation) */}
      <div style={{
        padding: '1rem 1.5rem 0.75rem',
        borderBottom: '1px solid var(--cds-border-subtle)',
        backgroundColor: 'var(--cds-layer-01)',
        margin: '-1rem -1.5rem 1.5rem'
      }}>
        <div className="custom-progress-bar">
          <div className="progress-steps">
            {steps.map((step, idx) => (
              <React.Fragment key={idx}>
                <div
                  className={`progress-step ${currentStep >= idx ? 'completed' : ''} ${currentStep === idx ? 'active' : ''}`}
                  onClick={() => handleStepClick(idx)}
                  style={{ cursor: idx <= currentStep ? 'pointer' : 'default' }}
                >
                  <div className="step-circle">
                    {currentStep > idx ? <Checkmark size={16} /> : idx + 1}
                  </div>
                  <div className="step-label">
                    <div className="step-title">{step.label}</div>
                    {currentStep > idx && step.description && (
                      <div className="step-description">{step.description}</div>
                    )}
                  </div>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`progress-line ${currentStep > idx ? 'completed' : ''}`}></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <Tile style={{ padding: '1.25rem', marginBottom: '1.5rem' }}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '500' }}>
          {steps[currentStep].label}
        </h3>

        {/* STEP 1: Slika */}
        {currentStep === 0 && (
          <div className={`receipt-step-content ${stepTransition}`}>
            <FileUploader
              labelTitle=""
              labelDescription="Uploadujte sliku računa (JPG, PNG)"
              buttonLabel="Izaberi sliku"
              filenameStatus="edit"
              accept={['.jpg', '.jpeg', '.png', '.webp', '.bmp']}
              multiple={false}
              disabled={isParsing || isUploading}
              onChange={handleFileChange}
            />
            {selectedFile && (
              <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginTop: '0.5rem' }}>
                Izabran fajl: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
              </p>
            )}

            {previewUrl && (
              <div style={{ marginTop: '0.5rem' }}>
                <img
                  src={previewUrl}
                  alt="Pregled računa"
                  style={{
                    width: '100%',
                    maxHeight: '180px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    border: '1px solid var(--cds-border-subtle)'
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* STEP 2: Parsiranje */}
        {currentStep === 1 && (
          <div className={`receipt-step-content ${stepTransition}`}>
            {previewUrl && (
              <div style={{ marginBottom: '1rem' }}>
                <img
                  src={previewUrl}
                  alt="Pregled računa"
                  style={{
                    width: '100%',
                    maxHeight: '140px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    border: '1px solid var(--cds-border-subtle)'
                  }}
                />
              </div>
            )}

            {parseError && (
              <div style={{
                marginTop: '0.75rem',
                padding: '0.75rem',
                backgroundColor: 'var(--r1-status-error-bg, rgba(218, 30, 40, 0.1))',
                color: 'var(--r1-status-error)',
                borderRadius: '8px',
                fontSize: '0.8125rem'
              }}>
                {parseError}
              </div>
            )}

            {parsedReceipt && (
              <div style={{
                padding: '0.75rem',
                backgroundColor: 'var(--r1-status-success-bg, rgba(24, 128, 56, 0.1))',
                color: 'var(--r1-status-success)',
                borderRadius: '8px',
                fontSize: '0.8125rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Checkmark size={16} />
                Podaci su uspešno izdvojeni iz slike. Pregledajte ih u sledećem koraku.
              </div>
            )}
          </div>
        )}

        {/* STEP 3: Potvrda */}
        {currentStep === 2 && draftReceipt && (
          <div className={`receipt-step-content ${stepTransition}`} style={{
            padding: '1rem',
            backgroundColor: 'var(--cds-layer-01)',
            borderRadius: '8px',
            border: '1px solid var(--cds-border-subtle-01)'
          }}>
            <h4 style={{
              fontSize: '0.875rem',
              fontWeight: '600',
              margin: '0 0 0.75rem 0',
              color: 'var(--cds-text-primary)'
            }}>
              Detalji računa
            </h4>

            {/* Prodavnica (editable) */}
            <div className="pocketmoney-detail-row" onClick={() => setEditingField('merchantName')} style={{ cursor: 'pointer' }}>
              <span className="pocketmoney-detail-row__label">Prodavnica</span>
              {editingField === 'merchantName' ? (
                <TextInput
                  id="receipt-merchant"
                  labelText=""
                  value={draftReceipt.merchantName || ''}
                  onChange={(e) => updateField('merchantName', e.target.value)}
                  onBlur={() => setEditingField(null)}
                  autoFocus
                  disabled={isUploading}
                />
              ) : (
                <span className="pocketmoney-detail-row__value">{draftReceipt.merchantName || '-'}</span>
              )}
            </div>

            {/* Datum kupovine */}
            <div className="pocketmoney-detail-row">
              <span className="pocketmoney-detail-row__label">Datum kupovine</span>
              <span className="pocketmoney-detail-row__value">{formatDate(draftReceipt.transactionDate)}</span>
            </div>

            {/* PDV */}
            <div className="pocketmoney-detail-row">
              <span className="pocketmoney-detail-row__label">PDV</span>
              <span className="pocketmoney-detail-row__value">{draftReceipt.tax != null ? formatAmount(draftReceipt.tax) : '-'}</span>
            </div>

            {/* Ukupno */}
            <div className="pocketmoney-detail-row">
              <span className="pocketmoney-detail-row__label">Ukupno</span>
              <span className="pocketmoney-detail-row__value" style={{ color: '#9fb746', fontWeight: '600' }}>
                {draftReceipt.total != null ? formatAmount(draftReceipt.total) : '-'}
              </span>
            </div>

            {/* Valuta */}
            <div className="pocketmoney-detail-row">
              <span className="pocketmoney-detail-row__label">Valuta</span>
              <span className="pocketmoney-detail-row__value">{draftReceipt.currency || '-'}</span>
            </div>

            {/* Items (read-only) */}
            {Array.isArray(draftReceipt.items) && draftReceipt.items.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <h5 style={{
                  fontSize: '0.8125rem',
                  fontWeight: '600',
                  margin: '0 0 0.5rem 0',
                  color: 'var(--cds-text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Stavke ({draftReceipt.items.length})
                </h5>
                {draftReceipt.items.map((item, idx) => (
                  <div key={idx} className="pocketmoney-item-row">
                    <div className="pocketmoney-item-desc">{item.description}</div>
                    <div className="pocketmoney-item-qty">×{item.quantity}</div>
                    <div className="pocketmoney-item-price">{formatAmount(item.totalPrice ?? item.price)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Tile>

      {/* Step navigation */}
      <div className="edit-sidebar__actions">
        <Button
          kind="secondary"
          onClick={currentStep === 0 ? onClose : prevStep}
          disabled={isParsing || isUploading}
          type="button"
          renderIcon={ArrowLeft}
        >
          {currentStep === 0 ? 'Otkaži' : 'Nazad'}
        </Button>
        {currentStep === 2 ? (
          <Button kind="primary" type="submit" disabled={isUploading}>
            {isUploading ? 'Dodavanje...' : 'Dodaj'}
          </Button>
        ) : (
          <Button kind="primary" type="button" onClick={handleParse} disabled={!selectedFile || isParsing}>
            {isParsing ? 'Parsiranje...' : 'Parsiraj račun'}
          </Button>
        )}
      </div>
    </form>
  );
};

// Format note date for display
const formatNoteDate = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  return date.toLocaleString('sr-Latn-RS', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// ===========================================
// Sidebar Component
// ===========================================
const Sidebar = ({ resident, notes, notesLoading, notesError, onAddNote, pagination, onPageChange, onDeleteNote, currentUserId, isAdmin }) => (
  <aside className="residents1-sidebar" aria-label="Bočna traka">
    {/* Current Activity - First (hidden for inactive residents) */}
    {resident?.isActive !== false && (
      <SidebarCard title="Trenutna aktivnost" icon={Icons.Activity} className="residents1-sidebar-card--current-activity">
        <CurrentActivityDisplay residentId={resident?.id} />
      </SidebarCard>
    )}
    
    {/* Resident Notes */}
    <SidebarCard title="Beleške" icon={Icons.Edit}>
      <div className="residents1-notes-list">
        {notesLoading ? (
          <div className="residents1-notes-empty">
            <div className="residents1-spinner" />
          </div>
        ) : notesError ? (
          <div className="residents1-notes-empty residents1-notes-empty--error">
            <Icons.AlertTriangle />
            <span>{notesError}</span>
          </div>
        ) : notes.length === 0 ? (
          <div className="residents1-notes-empty">
            <span>Nema beleški za ovog stanara.</span>
          </div>
        ) : (
          <ul className="residents1-notes-list__items">
            {notes.map(note => {
              // Only administrators may delete notes written by other users.
              // Everyone else may delete only their own notes.
              const isOwnNote = currentUserId && note.userId &&
                String(note.userId).toLowerCase() === String(currentUserId).toLowerCase();
              const canDelete = isAdmin || isOwnNote;
              return (
                <li key={note.id} className={`residents1-note-item ${note.severity === 1 ? 'residents1-note-item--severity-1' : ''}`}>
                  <p className="residents1-note-item__text">{note.text}</p>
                  <div className="residents1-note-item__meta">
                    <span className="residents1-note-item__author">
                      <Icons.User />
                      {note.userName || 'Nepoznat korisnik'}
                    </span>
                    <span className="residents1-note-item__date">
                      {formatNoteDate(note.createdAt)}
                    </span>
                    {canDelete && (
                      <button
                        className="residents1-note-item__delete"
                        onClick={() => onDeleteNote(note)}
                        aria-label="Obriši belešku"
                        title="Obriši belešku"
                      >
                        <TrashCan size={14} />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {pagination && pagination.totalPages > 1 && (
        <div className="residents1-notes-pagination">
          <button
            className="residents1-notes-pagination__btn"
            onClick={() => onPageChange(pagination.pageNumber - 1)}
            disabled={!pagination.hasPreviousPage || notesLoading}
            aria-label="Prethodna strana"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="residents1-notes-pagination__info">
            {pagination.pageNumber} / {pagination.totalPages}
          </span>
          <button
            className="residents1-notes-pagination__btn"
            onClick={() => onPageChange(pagination.pageNumber + 1)}
            disabled={!pagination.hasNextPage || notesLoading}
            aria-label="Sledeća strana"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
      <div className="residents1-notes-footer">
        <Button
          kind="primary"
          size="sm"
          renderIcon={Add}
          hasIconOnly
          iconDescription="Dodaj belešku"
          className="residents1-notes-add-btn"
          onClick={onAddNote}
          disabled={notesLoading}
        />
      </div>
    </SidebarCard>
    
    {/* Assigned Caregiver */}
    {resident.caregiver && (
      <SidebarCard title="Zadužena osoba" icon={Icons.User}>
        <div className="residents1-caregiver">
          <div className="residents1-caregiver-avatar">{resident.caregiver.initials}</div>
          <div className="residents1-caregiver-info">
            <h5 className="residents1-caregiver-name">{resident.caregiver.name}</h5>
            <p className="residents1-caregiver-role">{resident.caregiver.role}</p>
          </div>
        </div>
        <div className="residents1-caregiver-contact">
          <Button variant="ghost" icon={Icons.Phone}>
            {resident.caregiver.phone}
          </Button>
        </div>
      </SidebarCard>
    )}
    
  </aside>
);

// ===========================================
// Main Page Component
// ===========================================
const ResidentProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme } = useTheme();
  const { user } = useAuth();
  const { can, loading: permissionsLoading } = usePermissions();

  // Current user's ID and role, used to decide who may delete resident notes.
  // Only administrators may delete notes written by other users.
  const currentUserId = user?.sub || user?.nameid || null;
  const isAdmin = authService.getUserRole() === 'administrator';
  // Caregivers, physiotherapists, nurses and caretakers may view resident photos but must not upload/replace them.
  const isCaregiver = ['caregiver', 'physiotherapist', 'nurse', 'caretaker'].includes(authService.getUserRole());
  const canUploadAvatar = !isCaregiver;

  // Per-user permission gating (layered on top of role gating).
  // Administrators implicitly have every permission, so keep the isAdmin fallback
  // in case the /me call has not resolved yet.
  const canManageFamily = isAdmin || can('residents.family.manage');
  const canManageHealth = isAdmin || can('residents.health.manage');
  const canManageDiet = isAdmin || can('residents.diet.manage');
  const canManageSupplies = isAdmin || can('supplies.manage');
  const canUploadDocuments = isAdmin || can('documents.upload');
  // Deaktivacija stanara je zasebna dozvola (residents.deactivate): bez nje se
  // stavka u padajućem meniju ("tri tačkice") ne prikazuje i ne može se pokrenuti.
  const canDeactivateResident = isAdmin || can('residents.deactivate');
  // Premeštanje stanara u drugu sobu (POST /api/Room/move) zahteva `rooms.move`:
  // bez te dozvole se dugme "Premesti" ne prikazuje i akcija se ne pokreće.
  const canTransferResident = isAdmin || can('rooms.move');
  // Izmena ličnih/administrativnih podataka stanara (PUT /api/Resident/...).
  const canUpdateResident = isAdmin || can('residents.update');
  // Aktivnosti stanara (POST/PUT/DELETE /api/ResidentActivity).
  const canManageResidentActivities = isAdmin || can('resident-activities.manage');
  // Pregled dokumenata (GET /api/ResidentDocument/...).
  const canViewDocuments = isAdmin || can('documents.view');

  // Tabovi vidljivi trenutnom korisniku (na osnovu njegovih dozvola).
  // Tab bez `permissions` je uvek vidljiv; inače je dovoljna BILO KOJA dozvola.
  const visibleTabs = useMemo(
    () => TABS.filter((tab) => !tab.permissions || tab.permissions.some((p) => isAdmin || can(p))),
    [can, isAdmin]
  );

  // Persist the selected tab per user (and per resident) so it survives a page refresh.
  const tabStorageKey = `resident-tab:${user?.sub || user?.nameid || 'anonymous'}:${id}`;
  const getInitialTab = () => {
    // A ?tab=<id> query param (e.g. from a notification deep-link) wins over
    // the last tab the user had open for this resident.
    const tabParam = searchParams.get('tab');
    if (tabParam && visibleTabs.some(t => t.id === tabParam)) return tabParam;
    try {
      const saved = localStorage.getItem(tabStorageKey);
      if (saved && visibleTabs.some(t => t.id === saved)) return saved;
    } catch (e) { /* ignore */ }
    return 'personal';
  };
  const [activeTab, setActiveTab] = useState(getInitialTab);

  // Ako aktivni tab postane nedostupan (npr. dozvole se učitaju posle mount-a,
  // ili se promene), prebaci na prvi vidljivi tab.
  useEffect(() => {
    if (!visibleTabs.some(t => t.id === activeTab)) {
      setActiveTab(visibleTabs[0]?.id || 'personal');
    }
  }, [visibleTabs, activeTab]);

  // Pošto se komponenta prvi put renderuje pre nego što dozvole stignu,
  // getInitialTab tada ne može da vrati sačuvani/deep-link tab. Kada se
  // dozvole učitaju, primeni ga jednom (ako je vidljiv).
  const initialTabAppliedRef = useRef(false);
  useEffect(() => {
    if (permissionsLoading || initialTabAppliedRef.current) return;
    initialTabAppliedRef.current = true;
    const initial = getInitialTab();
    if (initial !== activeTab) setActiveTab(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsLoading]);

  // Save the active tab whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(tabStorageKey, activeTab);
    } catch (e) { /* ignore */ }
  }, [activeTab, tabStorageKey]);
  const [resident, setResident] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [residentNotes, setResidentNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState(null);
  const [notesPage, setNotesPage] = useState(1);
  const [notesPageSize] = useState(5);
  const [notesPagination, setNotesPagination] = useState(null);
  const [isNoteSidebarOpen, setIsNoteSidebarOpen] = useState(false);
  const [isNoteSaving, setIsNoteSaving] = useState(false);
  const [isDeleteNoteModalOpen, setIsDeleteNoteModalOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [isNoteDeleting, setIsNoteDeleting] = useState(false);
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isEditSidebarOpen, setIsEditSidebarOpen] = useState(false);
  const [editSection, setEditSection] = useState('identity');
  const [isEditSaving, setIsEditSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isTransferSidebarOpen, setIsTransferSidebarOpen] = useState(false);
  const [isAddToGroupSidebarOpen, setIsAddToGroupSidebarOpen] = useState(false);
  const [isDeactivateSidebarOpen, setIsDeactivateSidebarOpen] = useState(false);
  const [isHealthRecordSidebarOpen, setIsHealthRecordSidebarOpen] = useState(false);
  const [isHealthEditSidebarOpen, setIsHealthEditSidebarOpen] = useState(false);
  const [healthEditSection, setHealthEditSection] = useState('mobility');
  const [isDietPlanSidebarOpen, setIsDietPlanSidebarOpen] = useState(false);
  const [dietData, setDietData] = useState(null);
  const [dietLoading, setDietLoading] = useState(false);
  const [roomData, setRoomData] = useState(null);
  const [familyData, setFamilyData] = useState([]);
  const [familyLoading, setFamilyLoading] = useState(false);
  const [isFamilySidebarOpen, setIsFamilySidebarOpen] = useState(false);
  const [familyMemberToEdit, setFamilyMemberToEdit] = useState(null);
  const [isDeleteFamilyModalOpen, setIsDeleteFamilyModalOpen] = useState(false);
  const [familyMemberToDelete, setFamilyMemberToDelete] = useState(null);
  const [isFamilyDeleting, setIsFamilyDeleting] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState(null);
  const [documentsPage, setDocumentsPage] = useState(1);
  const [documentsPageSize] = useState(8);
  const [documentsPagination, setDocumentsPagination] = useState(null);
  const [isDocumentUploadSidebarOpen, setIsDocumentUploadSidebarOpen] = useState(false);
  const [isDeleteDocumentModalOpen, setIsDeleteDocumentModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [isDocumentDeleting, setIsDocumentDeleting] = useState(false);
  const [isDocumentPreviewOpen, setIsDocumentPreviewOpen] = useState(false);
  const [documentToPreview, setDocumentToPreview] = useState(null);
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState(null);
  const [documentPreviewLoading, setDocumentPreviewLoading] = useState(false);
  const [documentPreviewError, setDocumentPreviewError] = useState(null);
  const tabListRef = useRef(null);
  const overflowMenuRef = useRef(null);
  const avatarMenuRef = useRef(null);
  const avatarFileInputRef = useRef(null);
  const prevTabIndexRef = useRef(visibleTabs.findIndex(t => t.id === getInitialTab()));

  // On mobile, scroll the active tab into view. When navigating to the right,
  // the active tab aligns to the left edge; when navigating to the left, it
  // aligns to the right edge (opposite direction).
  useEffect(() => {
    const listEl = tabListRef.current;
    if (!listEl) return;
    const activeTabEl = listEl.querySelector('.residents1-tab--active');
    if (!activeTabEl) return;

    const currentIndex = visibleTabs.findIndex(t => t.id === activeTab);
    const prevIndex = prevTabIndexRef.current;
    const goingRight = currentIndex >= prevIndex;

    // Use getBoundingClientRect for precise, ancestor-independent positioning.
    const listRect = listEl.getBoundingClientRect();
    const tabRect = activeTabEl.getBoundingClientRect();

    let targetScrollLeft;
    if (goingRight) {
      // Align the active tab's left edge to the container's left edge.
      targetScrollLeft = listEl.scrollLeft + (tabRect.left - listRect.left);
    } else {
      // Align the active tab's right edge to the container's right edge.
      targetScrollLeft = listEl.scrollLeft + (tabRect.right - listRect.right);
    }

    listEl.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
    prevTabIndexRef.current = currentIndex;
  }, [activeTab]);
  // Tracks which tabs have already had their data fetched, so we only load
  // each tab's data once (lazy loading on demand instead of eagerly on mount).
  const loadedTabsRef = useRef(new Set());
  
  // Open edit sidebar with specific section (requires the residents.update permission)
  const openEditSidebar = (section) => {
    if (!canUpdateResident) return;
    setEditSection(section);
    setIsEditSidebarOpen(true);
  };

  // ---------- Avatar photo (self-service) ----------
  // Upload a new profile image for the resident using the same endpoint as the
  // edit sidebar: POST /api/Resident/upload-image (multipart: residentId + file).
  const handleAvatarUpload = async (file) => {
    if (!file || !resident?.id) return;
    // Caregivers are not allowed to upload resident profile photos.
    if (isCaregiver) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setNotification({
        kind: 'error',
        title: 'Nepodržan format',
        subtitle: 'Dozvoljeni formati su JPG, PNG, WEBP, GIF i BMP.',
      });
      setTimeout(() => setNotification(null), 4000);
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      setNotification({
        kind: 'error',
        title: 'Slika je prevelika',
        subtitle: 'Maksimalna dozvoljena veličina je 5MB.',
      });
      setTimeout(() => setNotification(null), 4000);
      return;
    }

    setIsUploadingAvatar(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('residentId', resident.id);
      uploadFormData.append('file', file);

      const response = await axiosInstance.post('/api/Resident/upload-image', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const newImgSrc = response.data?.data?.url || response.data?.data?.imgSrc;

      if (response.data?.success && newImgSrc) {
        setResident((prev) => ({ ...prev, imgSrc: newImgSrc, avatar: newImgSrc }));
        setNotification({
          kind: 'success',
          title: 'Fotografija ažurirana',
          subtitle: 'Profilna fotografija stanara je uspešno sačuvana.',
        });
        // Refresh the page shortly after the success notification is shown
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else {
        throw new Error(response.data?.message || 'Greška pri uploadu slike');
      }
    } catch (err) {
      console.error('Greška pri uploadu slike:', err);
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle:
          err.response?.data?.message ||
          'Nije moguće uploadovati sliku. Pokušajte ponovo.',
      });
    } finally {
      setIsUploadingAvatar(false);
      setTimeout(() => setNotification(null), 4000);
    }
  };

  const handleAvatarFileChange = (e) => {
    const file = e.target.files?.[0];
    // Reset so selecting the same file again still triggers onChange
    e.target.value = '';
    if (file) handleAvatarUpload(file);
  };

  // Avatar click: no photo -> open upload directly; has photo -> show options menu
  const handleAvatarClick = () => {
    if (isUploadingAvatar) return;
    if (!resident?.avatar) {
      avatarFileInputRef.current?.click();
      return;
    }
    setIsAvatarMenuOpen((prev) => !prev);
  };

  const handleAvatarViewImage = () => {
    setIsAvatarMenuOpen(false);
    setIsImageModalOpen(true);
  };

  const handleAvatarNewPhoto = () => {
    setIsAvatarMenuOpen(false);
    avatarFileInputRef.current?.click();
  };

  // Close the avatar menu on outside click / Escape
  useEffect(() => {
    if (!isAvatarMenuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (avatarMenuRef.current && !avatarMenuRef.current.contains(event.target)) {
        setIsAvatarMenuOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsAvatarMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isAvatarMenuOpen]);
  
  // Open transfer sidebar (requires the rooms.move permission)
  const openTransferSidebar = () => {
    if (!canTransferResident) return;
    setIsTransferSidebarOpen(true);
  };

  // Open add to group sidebar
  const openAddToGroupSidebar = () => {
    setIsOverflowMenuOpen(false);
    setIsAddToGroupSidebarOpen(true);
  };

  // Open deactivate sidebar (requires the residents.deactivate permission)
  const openDeactivateSidebar = () => {
    if (!canDeactivateResident) return;
    setIsOverflowMenuOpen(false);
    setIsDeactivateSidebarOpen(true);
  };

  // Handle successful deactivation
  const handleResidentDeactivated = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/api/Resident/${id}`);
      if (response.data.success) {
        const residentData = response.data.data;
        setResident({
          ...residentData,
          avatar: residentData.imgSrc || null,
        });
      }
    } catch (err) {
      console.error('Greška pri osvežavanju podataka o korisniku:', err);
    }
    // Navigate back to the residents list after 3 seconds so the list reloads
    setTimeout(() => {
      navigate('/residents');
    }, 3000);
  }, [id, navigate]);

  // Refresh resident data after a successful transfer
  const handleTransferSuccess = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/api/Resident/${id}`);
      if (response.data.success) {
        const residentData = response.data.data;
        setResident({
          ...residentData,
          avatar: residentData.imgSrc || null,
        });
      }
    } catch (err) {
      console.error('Greška pri osvežavanju podataka o korisniku:', err);
    }
  }, [id]);
  
  // Open health record sidebar
  const openHealthRecordSidebar = () => {
    // Requires the residents.health.manage permission.
    if (!canManageHealth) return;
    setIsHealthRecordSidebarOpen(true);
  };
  
  // Open health edit sidebar
  const openHealthEditSidebar = (section = 'mobility') => {
    setHealthEditSection(section);
    setIsHealthEditSidebarOpen(true);
  };
  
  // Handle health record created
  const handleHealthRecordCreated = (newHealthData) => {
    setHealthData(newHealthData);
  };

  const openDietPlanSidebar = () => {
    // Requires the residents.diet.manage permission.
    if (!canManageDiet) return;
    setIsDietPlanSidebarOpen(true);
  };

  const handleDietPlanCreated = (newDietData) => {
    setDietData(newDietData);
  };

  // Fetch family members from API
  const fetchFamilyData = useCallback(async () => {
    if (!id) return;
    setFamilyLoading(true);
    try {
      const response = await axiosInstance.get(`/api/ResidentFamily/resident/${id}`);
      if (response.data.success) {
        setFamilyData(response.data.data || []);
      } else {
        setFamilyData([]);
      }
    } catch (err) {
      console.error('Greška pri učitavanju srodnika:', err);
      setFamilyData([]);
    } finally {
      setFamilyLoading(false);
    }
  }, [id]);

  // (Family data is now fetched lazily via loadTabData when the "Rodbina" tab opens)

  // Fetch resident documents from API
  const fetchDocuments = useCallback(async (page = 1) => {
    if (!id) return;
    setDocumentsLoading(true);
    setDocumentsError(null);
    // TEMP: simulacija sporog učitavanja da se vidi loading stanje
    await new Promise(resolve => setTimeout(resolve, 3000));
    try {
      const response = await axiosInstance.get(`/api/ResidentDocument/resident/${id}`, {
        params: {
          pageNumber: page,
          pageSize: documentsPageSize
        }
      });
      if (response.data.success) {
        setDocuments(response.data.data || []);
        setDocumentsPagination(response.data.pagination || null);
      } else {
        setDocuments([]);
        setDocumentsPagination(null);
      }
    } catch (err) {
      console.error('Greška pri učitavanju dokumenata:', err);
      setDocumentsError('Nije moguće učitati dokumente');
      setDocuments([]);
      setDocumentsPagination(null);
    } finally {
      setDocumentsLoading(false);
    }
  }, [id, documentsPageSize]);

  // (Documents are now fetched lazily via loadTabData when the "Dokumenti" tab opens)

  // Pagination handlers
  const goToDocumentsPage = (page) => {
    if (page < 1 || (documentsPagination && page > documentsPagination.totalPages)) return;
    setDocumentsPage(page);
    // Refetch the requested page from the API. Without this the page state
    // changes but the list is never reloaded, so the arrows appear dead.
    fetchDocuments(page);
  };

  // Open document upload sidebar
  const openDocumentUploadSidebar = () => {
    setIsDocumentUploadSidebarOpen(true);
  };

  // Handle document uploaded
  const handleDocumentUploaded = useCallback((newDocument) => {
    setDocuments(prev => [newDocument, ...prev]);
    setDocumentsPagination(prev => prev ? {
      ...prev,
      totalCount: (prev.totalCount || 0) + 1,
      totalPages: Math.ceil(((prev.totalCount || 0) + 1) / documentsPageSize)
    } : prev);
  }, [documentsPageSize]);

  // Download a document
  const handleDownloadDocument = useCallback(async (doc) => {
    if (!doc?.id) return;
    try {
      // The endpoint returns the file directly (content-disposition: attachment).
      // Fetch it as a blob from the API (same origin) to avoid CORS / SAS URL issues.
      const response = await axiosInstance.get(`/api/ResidentDocument/download/${doc.id}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.documentName || 'dokument';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Greška pri preuzimanju dokumenta:', err);
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle: 'Nije moguće preuzeti dokument'
      });
      setTimeout(() => setNotification(null), 4000);
    }
  }, []);

  // Open document preview
  const openDocumentPreview = useCallback(async (doc) => {
    if (!doc?.id) return;
    setDocumentToPreview(doc);
    setDocumentPreviewUrl(null);
    setDocumentPreviewError(null);
    setDocumentPreviewLoading(true);
    setIsDocumentPreviewOpen(true);
    try {
      const response = await axiosInstance.get(`/api/ResidentDocument/${doc.id}/sas-url`);
      const sasUrl = response.data?.data?.url;
      if (!sasUrl) {
        throw new Error('Nedostaje SAS URL');
      }
      setDocumentPreviewUrl(sasUrl);
    } catch (err) {
      console.error('Greška pri dobijanju SAS URL-a:', err);
      setDocumentPreviewError('Nije moguće učitati pregled dokumenta');
    } finally {
      setDocumentPreviewLoading(false);
    }
  }, []);

  // Close document preview
  const closeDocumentPreview = useCallback(() => {
    setIsDocumentPreviewOpen(false);
    setDocumentToPreview(null);
    setDocumentPreviewUrl(null);
    setDocumentPreviewError(null);
  }, []);

  // Open delete document confirmation modal
  const openDeleteDocumentModal = (doc) => {
    setDocumentToDelete(doc);
    setIsDeleteDocumentModalOpen(true);
  };

  // Confirm delete document
  const confirmDeleteDocument = async () => {
    if (!documentToDelete) return;
    setIsDocumentDeleting(true);
    try {
      await axiosInstance.delete(`/api/ResidentDocument/${documentToDelete.id}`);
      setNotification({
        kind: 'success',
        title: 'Uspešno obrisano',
        subtitle: 'Dokument je uspešno obrisan.'
      });
      setTimeout(() => setNotification(null), 3000);
      setDocuments(prev => prev.filter(d => d.id !== documentToDelete.id));
      setDocumentsPagination(prev => prev ? {
        ...prev,
        totalCount: Math.max((prev.totalCount || 0) - 1, 0),
        totalPages: Math.max(Math.ceil(((prev.totalCount || 0) - 1) / documentsPageSize), 1)
      } : prev);
    } catch (err) {
      console.error('Greška pri brisanju dokumenta:', err);
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle: err.response?.data?.message || 'Nije moguće obrisati dokument.'
      });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsDeleteDocumentModalOpen(false);
      setDocumentToDelete(null);
      setIsDocumentDeleting(false);
    }
  };

  // Open family sidebar for adding a new member
  const openFamilySidebar = () => {
    // Requires the residents.family.manage permission.
    if (!canManageFamily) return;
    setFamilyMemberToEdit(null);
    setIsFamilySidebarOpen(true);
  };

  // Open family sidebar for editing an existing member
  const openFamilyEditSidebar = (member) => {
    // Requires the residents.family.manage permission.
    if (!canManageFamily) return;
    setFamilyMemberToEdit(member);
    setIsFamilySidebarOpen(true);
  };

  // Handle family member created/updated
  const handleFamilyMemberSaved = useCallback((newMember) => {
    setFamilyData(prev => {
      const exists = prev.some(m => m.id === newMember.id);
      if (exists) {
        return prev.map(m => m.id === newMember.id ? newMember : m);
      }
      return [...prev, newMember];
    });
  }, []);

  // Open delete family member confirmation modal
  const openDeleteFamilyModal = (member) => {
    // Requires the residents.family.manage permission.
    if (!canManageFamily) return;
    setFamilyMemberToDelete(member);
    setIsDeleteFamilyModalOpen(true);
  };

  // Confirm delete family member
  const confirmDeleteFamily = async () => {
    if (!familyMemberToDelete) return;
    setIsFamilyDeleting(true);
    try {
      await axiosInstance.delete(`/api/ResidentFamily/${familyMemberToDelete.id}`);
      setNotification({
        kind: 'success',
        title: 'Uspešno obrisano',
        subtitle: 'Srodnik je uspešno obrisan.'
      });
      setTimeout(() => setNotification(null), 3000);
      setFamilyData(prev => prev.filter(m => m.id !== familyMemberToDelete.id));
    } catch (err) {
      console.error('Greška pri brisanju srodnika:', err);
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle: err.response?.data?.message || 'Nije moguće obrisati srodnika.'
      });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsDeleteFamilyModalOpen(false);
      setFamilyMemberToDelete(null);
      setIsFamilyDeleting(false);
    }
  };
  
  // Fetch health data (lazy - only when the "Zdravlje" tab is opened)
  const fetchHealthData = useCallback(async () => {
    if (!id) return;
    setHealthLoading(true);
    try {
      const response = await axiosInstance.get(`/api/ResidentHealth/resident/${id}`);
      if (response.data.success) {
        setHealthData(response.data.data);
      }
    } catch (err) {
      console.error('Greška pri učitavanju zdravstvenih podataka:', err);
      // Don't set error state, health data is optional
    } finally {
      setHealthLoading(false);
    }
  }, [id]);

  // Fetch diet data (lazy - only when the "Ishrana" tab is opened)
  const fetchDietData = useCallback(async () => {
    if (!id) return;
    setDietLoading(true);
    try {
      const response = await axiosInstance.get(`/api/ResidentDiet/resident/${id}`);
      if (response.data.success && response.data.data) {
        const diet = response.data.data;
        try {
          const planResponse = await axiosInstance.get(`/api/DietPlan/${diet.dietPlanId}`);
          if (planResponse.data.success) diet.dietPlanName = planResponse.data.data.name;
        } catch {}
        setDietData(diet);
      }
    } catch (err) {
      console.error('Greška pri učitavanju plana ishrane:', err);
    } finally {
      setDietLoading(false);
    }
  }, [id]);

  // Fetch room data (lazy - only when the "Lični podaci" tab is opened)
  const fetchRoomData = useCallback(async (roomId) => {
    if (!roomId) return;
    // TEMP: simulacija sporog učitavanja da se vidi loading stanje
    await new Promise(resolve => setTimeout(resolve, 3000));
    try {
      const response = await axiosInstance.get(`/api/Room/${roomId}`);
      if (response.data.success) {
        setRoomData(response.data.data);
      }
    } catch (err) {
      console.error('Greška pri učitavanju podataka o sobi:', err);
    }
  }, []);

  // Fetch resident data from API (only the resident itself, not the sub-tabs)
  useEffect(() => {
    const fetchResident = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axiosInstance.get(`/api/Resident/${id}`);
        
        if (response.data.success) {
          const residentData = response.data.data;
          // Map API field names to component field names
          const mappedData = {
            ...residentData,
            avatar: residentData.imgSrc || null, // Map imgSrc to avatar for ResidentAvatar component
          };
          setResident(mappedData);
        } else {
          setError('Nije moguće učitati podatke o korisniku.');
        }
      } catch (err) {
        console.error('Greška pri učitavanju korisnika:', err);
        setError('Došlo je do greške pri učitavanju podataka.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchResident();
  }, [id]);

  // Load the data for the initially active tab once the resident is available.
  // This keeps the first paint fast while still populating the default tab.
  // Also runs when the active tab changes (e.g. after a permission-based
  // redirect); loadTabData is idempotent per tab via loadedTabsRef.
  useEffect(() => {
    if (resident) {
      loadTabData(activeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resident, activeTab]);
  
  // Fetch resident notes from API
  const fetchResidentNotes = useCallback(async (page = 1) => {
    if (!id) return;
    setNotesLoading(true);
    setNotesError(null);
    try {
      const response = await axiosInstance.get(`/api/ResidentNote/resident/${id}`, {
        params: {
          pageNumber: page,
          pageSize: notesPageSize
        }
      });
      if (response.data.success) {
        setResidentNotes(response.data.data || []);
        setNotesPagination(response.data.pagination || null);
      } else {
        setResidentNotes([]);
        setNotesPagination(null);
      }
    } catch (err) {
      console.error('Greška pri učitavanju beleški:', err);
      setNotesError('Nije moguće učitati beleške');
      setResidentNotes([]);
      setNotesPagination(null);
    } finally {
      setNotesLoading(false);
    }
  }, [id, notesPageSize]);
  
  useEffect(() => {
    fetchResidentNotes(notesPage);
  }, [fetchResidentNotes, notesPage]);
  
  // Open note sidebar
  const openNoteSidebar = () => {
    setIsNoteSidebarOpen(true);
  };
  
  // Handle note created
  const handleNoteCreated = useCallback((newNote) => {
    setResidentNotes(prev => [newNote, ...prev]);
  }, []);
  
  // Pagination handlers
  const goToNotesPage = (page) => {
    if (page < 1 || (notesPagination && page > notesPagination.totalPages)) return;
    setNotesPage(page);
  };
  
  // Open delete note confirmation modal
  const openDeleteNoteModal = (note) => {
    setNoteToDelete(note);
    setIsDeleteNoteModalOpen(true);
  };
  
  // Confirm delete note
  const confirmDeleteNote = async () => {
    if (!noteToDelete) return;
    
    setIsNoteDeleting(true);
    try {
      await axiosInstance.delete(`/api/ResidentNote/${noteToDelete.id}`);
      
      setNotification({
        kind: 'success',
        title: 'Uspešno obrisano',
        subtitle: 'Beleška je uspešno obrisana'
      });
      setTimeout(() => setNotification(null), 3000);
      
      // Remove from local list and refresh pagination
      setResidentNotes(prev => prev.filter(n => n.id !== noteToDelete.id));
      fetchResidentNotes(notesPage);
    } catch (err) {
      console.error('Greška pri brisanju beleške:', err);
      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle: err.response?.data?.message || 'Nije moguće obrisati belešku'
      });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsDeleteNoteModalOpen(false);
      setNoteToDelete(null);
      setIsNoteDeleting(false);
    }
  };
  
  // Header collapse on scroll is intentionally disabled so the header layout
  // (avatar, name, meta row, action buttons) stays stable while scrolling.
  
  // Close overflow menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (overflowMenuRef.current && !overflowMenuRef.current.contains(event.target)) {
        setIsOverflowMenuOpen(false);
      }
    };
    
    if (isOverflowMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOverflowMenuOpen]);
  
  // Handle edit sidebar save
  const handleEditSave = useCallback(async (formData) => {
    setIsEditSaving(true);
    try {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        jmbg: formData.jmbg,
        isWoman: formData.isWoman,
        citizenship: formData.citizenship,
        placeOfBirth: formData.placeOfBirth,
        placeOfResidence: formData.placeOfResidence,
        identificationNumber: formData.identificationNumber,
        identityCardNumber: formData.identityCardNumber,
        parentName: formData.parentName,
        primaryLang: formData.primaryLang,
        careLevel: formData.careLevel,
        roomId: formData.roomId || null,
        birthDate: formData.birthDate ? new Date(formData.birthDate).toISOString() : null,
        imgSrc: formData.imgSrc,
        isActive: formData.isActive,
      };
      
      const response = await axiosInstance.put(`/api/Resident/${id}`, payload);
      
      if (response.data.success) {
        // Update local resident data
        setResident(prev => ({
          ...prev,
          ...payload,
          fullName: `${payload.firstName} ${payload.lastName}`,
          gender: payload.isWoman ? 'Ženski' : 'Muški',
          status: payload.isActive ? 'active' : 'inactive',
          nationalId: payload.jmbg,
          language: payload.primaryLang,
          dateOfBirth: payload.birthDate,
          avatar: payload.imgSrc,
        }));
        setIsEditSidebarOpen(false);
        
        // Show success notification
        setNotification({
          kind: 'success',
          title: 'Uspešno sačuvano',
          subtitle: 'Podaci stanara su uspešno ažurirani'
        });
        setTimeout(() => setNotification(null), 3000);
        // Refresh the page after the notification so updated data is reloaded
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        console.error('Greška pri čuvanju:', response.data.message);
        setNotification({
          kind: 'error',
          title: 'Greška',
          subtitle: response.data.message || 'Greška pri čuvanju podataka'
        });
        setTimeout(() => setNotification(null), 4000);
      }
    } catch (err) {
      console.error('Greška pri čuvanju stanara:', err);

      if (isConcurrencyConflictError(err)) {
        // Neko drugi je izmenio stanara između učitavanja i čuvanja. Forma je sada
        // zasnovana na zastarelom stanju, pa je prikazujemo kao grešku i osvežavamo
        // ekran — korisnik vidi stvarne podatke umesto da ponovo pregazi tuđu izmenu.
        setNotification({
          kind: 'error',
          title: CONCURRENCY_CONFLICT_TITLE,
          subtitle: CONCURRENCY_CONFLICT_MESSAGE
        });
        setTimeout(() => {
          setNotification(null);
          window.location.reload();
        }, 2500);
        return;
      }

      setNotification({
        kind: 'error',
        title: 'Greška',
        subtitle: getApiErrorMessage(err, 'Došlo je do greške pri čuvanju podataka')
      });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setIsEditSaving(false);
    }
  }, [id]);
  
  // Keyboard navigation for tabs
  const handleTabKeyDown = (e, index) => {
    const tabs = visibleTabs;
    let newIndex = index;
    
    if (e.key === 'ArrowRight') {
      newIndex = (index + 1) % tabs.length;
    } else if (e.key === 'ArrowLeft') {
      newIndex = (index - 1 + tabs.length) % tabs.length;
    } else if (e.key === 'Home') {
      newIndex = 0;
    } else if (e.key === 'End') {
      newIndex = tabs.length - 1;
    } else {
      return;
    }
    
    e.preventDefault();
    handleTabSelect(tabs[newIndex].id);
    
    // Focus the new tab
    const tabButtons = tabListRef.current?.querySelectorAll('.residents1-tab');
    tabButtons?.[newIndex]?.focus();
  };

  // Load the data required by a given tab, but only once per tab (lazy loading).
  // Each tab's data is fetched on first open instead of all at once on mount,
  // which keeps the initial page load fast on slower (test) environments.
  const loadTabData = useCallback((tabId) => {
    if (loadedTabsRef.current.has(tabId)) return;
    loadedTabsRef.current.add(tabId);

    switch (tabId) {
      case 'personal':
        // Room data is shown in the "Lični podaci" tab.
        if (resident?.roomId) fetchRoomData(resident.roomId);
        break;
      case 'health':
        fetchHealthData();
        break;
      case 'nutrition':
        fetchDietData();
        break;
      case 'documents':
        // Bez dozvole za pregled dokumenata (documents.view) GET endpoint
        // (/api/ResidentDocument/resident/{id}) se uopšte ne poziva.
        if (canViewDocuments) fetchDocuments(documentsPage);
        break;
      case 'relatives':
        fetchFamilyData();
        break;
      default:
        // daily-plan, activities, pocket-money, inventory fetch their own data
        // inside their own components on mount.
        break;
    }
  }, [resident, fetchRoomData, fetchHealthData, fetchDietData, fetchDocuments, documentsPage, fetchFamilyData, canViewDocuments]);

  // Deep-link: when the URL carries ?tab=<id> (e.g. from a notification),
  // switch to that tab and load its data. Runs after loadTabData is defined.
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (!tabParam || !visibleTabs.some(t => t.id === tabParam)) return;
    setActiveTab(tabParam);
    loadTabData(tabParam);
  }, [searchParams, loadTabData]);

  // Synchronously lock page scroll before switching to the Zalihe tab so the
  // vertical scrollbar never flickers in during the loading skeleton.
  const handleTabSelect = (tabId) => {
    if (tabId === 'inventory') {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      // Release the lock on the next frame so it doesn't persist after load.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          document.body.style.overflow = prevOverflow;
        });
      });
    }
    // Drop a deep-link ?tab= param once the user picks a tab manually, so the
    // URL no longer overrides the persisted tab on the next visit.
    if (searchParams.has('tab')) {
      const next = new URLSearchParams(searchParams);
      next.delete('tab');
      setSearchParams(next, { replace: true });
    }
    setActiveTab(tabId);
    loadTabData(tabId);
  };
  
  // Render tab content
  const renderTabContent = () => {
    if (!resident) return null;
    
    switch (activeTab) {
      case 'daily-plan':
        return <DailyPlanTab resident={resident} />;
      case 'personal':
        return (
          <PersonalInfoTab 
            resident={resident} 
            onEditIdentity={() => openEditSidebar('identity')} 
            onEditAdministrative={() => openEditSidebar('administrative')}
            onTransferClick={openTransferSidebar}
            roomData={roomData}
            setNotification={setNotification}
            canTransfer={canTransferResident}
            canUpdate={canUpdateResident}
          />
        );
      case 'documents':
        return (
          <DocumentsTab
            documents={documents}
            loading={documentsLoading}
            error={documentsError}
            pagination={documentsPagination}
            onPageChange={goToDocumentsPage}
            onUpload={openDocumentUploadSidebar}
            onDownload={handleDownloadDocument}
            onDelete={openDeleteDocumentModal}
            onPreview={openDocumentPreview}
            currentUserId={currentUserId}
            isAdmin={isAdmin}
            canUpload={canUploadDocuments}
            canView={canViewDocuments}
          />
        );
      case 'activities':
        return <ActivitiesTab resident={resident} setNotification={setNotification} canManageActivities={canManageResidentActivities} />;
      case 'health':
        return <HealthTab resident={resident} healthData={healthData} healthLoading={healthLoading} onAddHealthRecord={openHealthRecordSidebar} onEditHealth={openHealthEditSidebar} canAddHealthRecord={canManageHealth} />;
      case 'nutrition':
        return <NutritionTab resident={resident} dietData={dietData} dietLoading={dietLoading} onAddDietPlan={openDietPlanSidebar} canEditDiet={canManageDiet} />;
      case 'relatives':
        return (
          <RelativesTab
            resident={resident}
            familyData={familyData}
            loading={familyLoading}
            onAdd={openFamilySidebar}
            onEdit={openFamilyEditSidebar}
            onDelete={openDeleteFamilyModal}
            canManage={canManageFamily}
          />
        );
      case 'pocket-money':
        return <PocketMoneyTab resident={resident} setNotification={setNotification} />;
      case 'inventory':
        return <InventoryTab resident={resident} setNotification={setNotification} canManage={canManageSupplies} />;
      default:
        return (
          <PersonalInfoTab 
            resident={resident} 
            onEditIdentity={() => openEditSidebar('identity')} 
            onEditAdministrative={() => openEditSidebar('administrative')}
            onTransferClick={openTransferSidebar}
            roomData={roomData}
            setNotification={setNotification}
            canTransfer={canTransferResident}
            canUpdate={canUpdateResident}
          />
        );
    }
  };
  
  // Loading state (also wait for permissions so tabs don't flash before
  // permission-based filtering is applied).
  if (loading || permissionsLoading) {
    return (
      <div className={`residents1-page ${theme === 'g100' ? 'residents1-page--dark' : ''}`}>
        <div className="residents1-container">
          {/* Header skeleton */}
          <div className="rph-skeleton rph-skeleton--header">
            <div className="rph-skeleton__avatar" />
            <div className="rph-skeleton__identity-text">
              <SkeletonText heading width="220px" />
              <SkeletonText width="160px" />
            </div>
          </div>

          {/* Tabs skeleton */}
          <div className="rph-skeleton rph-skeleton--tabs">
            {[...Array(6)].map((_, i) => (
              <SkeletonPlaceholder key={i} className="rph-skeleton__tab" />
            ))}
          </div>

          {/* Content skeleton */}
          <div className="rph-skeleton rph-skeleton--content">
            <div className="rph-skeleton__main">
              <div className="residents1-cards-grid">
                <SkeletonPlaceholder className="rph-skeleton__card" />
                <SkeletonPlaceholder className="rph-skeleton__card" />
              </div>
            </div>
            <div className="rph-skeleton__sidebar">
              <SkeletonPlaceholder className="rph-skeleton__card" />
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error || !resident) {
    return (
      <div className={`residents1-page ${theme === 'g100' ? 'residents1-page--dark' : ''}`}>
        <div className="residents1-container">
          <div className="residents1-empty-state">
            <div className="residents1-empty-icon"><Icons.User /></div>
            <h2 className="residents1-empty-title">{error ? 'Greška' : 'Stanar nije pronađen'}</h2>
            <p className="residents1-empty-description">
              {error || 'Ne možemo pronaći traženi profil stanara.'}
            </p>
            <Link to="/residents" className="residents1-btn residents1-btn--primary" style={{ marginTop: '16px', display: 'inline-flex' }}>
              Nazad na listu
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`residents1-page ${theme === 'g100' ? 'residents1-page--dark' : ''}`}>
      {/* Floating Notification */}
      {notification && (
        <div className="rph-notification" style={{ 
          position: 'fixed', 
          top: '4rem', 
          right: '1rem', 
          zIndex: 10000,
          animation: 'slideInFromRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <style>
            {`
              @keyframes slideInFromRight {
                from {
                  transform: translateX(calc(100% + 1rem));
                  opacity: 0;
                }
                to {
                  transform: translateX(0);
                  opacity: 1;
                }
              }
            `}
          </style>
          <InlineNotification
            kind={notification.kind}
            title={notification.title}
            subtitle={notification.subtitle}
            onCloseButtonClick={() => setNotification(null)}
            hideCloseButton={false}
            lowContrast
          />
        </div>
      )}

      {/* Skip Link for Accessibility */}
      <a href="#main-content" className="residents1-skip-link">
        Preskoči na glavni sadržaj
      </a>
      
      <div className="residents1-container">
        {/* Image Modal */}
        <ImageModal
          isOpen={isImageModalOpen}
          onClose={() => setIsImageModalOpen(false)}
          imageSrc={resident.avatar}
          altText={`Fotografija: ${resident.fullName}`}
        />
        
        {/* Edit Sidebar */}
        <EditSidebar
          isOpen={isEditSidebarOpen}
          onClose={() => setIsEditSidebarOpen(false)}
          resident={resident}
          onSave={handleEditSave}
          isSaving={isEditSaving}
          editSection={editSection}
        />
        
        {/* Transfer Sidebar */}
        <TransferSidebar
          isOpen={isTransferSidebarOpen}
          onClose={() => setIsTransferSidebarOpen(false)}
          resident={resident}
          setNotification={setNotification}
          onSuccess={handleTransferSuccess}
        />

        {/* Add To Group Sidebar */}
        <AddToGroupSidebar
          isOpen={isAddToGroupSidebarOpen}
          onClose={() => setIsAddToGroupSidebarOpen(false)}
          resident={resident}
          setNotification={setNotification}
        />

        {/* Deactivate Sidebar */}
        <DeactivateSidebar
          isOpen={isDeactivateSidebarOpen}
          onClose={() => setIsDeactivateSidebarOpen(false)}
          resident={resident}
          setNotification={setNotification}
          onSuccess={handleResidentDeactivated}
        />
        
        {/* Health Record Sidebar */}
        <HealthRecordSidebar
          isOpen={isHealthRecordSidebarOpen}
          onClose={() => setIsHealthRecordSidebarOpen(false)}
          resident={resident}
          setNotification={setNotification}
          onSuccess={handleHealthRecordCreated}
        />

        {/* Health Edit Sidebar */}
        <HealthEditSidebar
          isOpen={isHealthEditSidebarOpen}
          onClose={() => setIsHealthEditSidebarOpen(false)}
          resident={resident}
          healthData={healthData}
          setNotification={setNotification}
          onSuccess={handleHealthRecordCreated}
          section={healthEditSection}
        />

        {/* Diet Plan Sidebar */}
        <DietPlanSidebar
          isOpen={isDietPlanSidebarOpen}
          onClose={() => setIsDietPlanSidebarOpen(false)}
          resident={resident}
          setNotification={setNotification}
          onSuccess={handleDietPlanCreated}
          existingDiet={dietData}
        />

        {/* Family Sidebar */}
        <FamilySidebar
          isOpen={isFamilySidebarOpen}
          onClose={() => setIsFamilySidebarOpen(false)}
          resident={resident}
          setNotification={setNotification}
          onSuccess={handleFamilyMemberSaved}
          existingMember={familyMemberToEdit}
        />

        {/* Document Upload Sidebar */}
        <DocumentUploadSidebar
          isOpen={isDocumentUploadSidebarOpen}
          onClose={() => setIsDocumentUploadSidebarOpen(false)}
          resident={resident}
          setNotification={setNotification}
          onSuccess={handleDocumentUploaded}
        />
        
        {/* Enterprise Header - Microsoft Fluent / Azure Portal Style */}
        <header 
          className="rph-header"
          role="banner"
        >
          <IdentitySection
            avatar={resident.avatar}
            fullName={resident.fullName}
            status={resident.status}
            age={resident.age}
            roomNumber={resident.roomNumber}
            careLevel={resident.careLevel}
            isCollapsed={false}
            onAvatarClick={handleAvatarClick}
            canUploadAvatar={canUploadAvatar}
            isUploading={isUploadingAvatar}
            avatarMenuOpen={isAvatarMenuOpen}
            onViewImage={handleAvatarViewImage}
            onNewPhoto={handleAvatarNewPhoto}
            avatarWrapperRef={avatarMenuRef}
            fileInputRef={avatarFileInputRef}
            onFileChange={handleAvatarFileChange}
          />
          
          <ActionsSection
            isCollapsed={false}
            isOverflowOpen={isOverflowMenuOpen}
            onOverflowToggle={() => setIsOverflowMenuOpen(!isOverflowMenuOpen)}
            onOverflowClose={() => setIsOverflowMenuOpen(false)}
            overflowMenuRef={overflowMenuRef}
            onEditProfile={() => {
              setIsOverflowMenuOpen(false);
              openEditSidebar('identity');
            }}
            onTransferClick={() => {
              setIsOverflowMenuOpen(false);
              openTransferSidebar();
            }}
            onAddNote={() => {
              setIsOverflowMenuOpen(false);
              openNoteSidebar();
            }}
            onAddToGroup={openAddToGroupSidebar}
            onDeactivate={openDeactivateSidebar}
            canDeactivate={canDeactivateResident}
            canTransfer={canTransferResident}
            canUpdate={canUpdateResident}
          />
        </header>
        
        {/* Navigation Tabs */}
        <nav className="residents1-tabs" aria-label="Navigacija profila">
          <div 
            className="residents1-tabs__list" 
            role="tablist" 
            ref={tabListRef}
            aria-label="Sekcije profila stanara"
          >
            {visibleTabs.map((tab, index) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  id={`tab-${tab.id}`}
                  aria-selected={activeTab === tab.id}
                  aria-controls={`panel-${tab.id}`}
                  tabIndex={activeTab === tab.id ? 0 : -1}
                  className={`residents1-tab ${activeTab === tab.id ? 'residents1-tab--active' : ''}`}
                  onClick={() => handleTabSelect(tab.id)}
                  onKeyDown={(e) => handleTabKeyDown(e, index)}
                >
                  <IconComponent />
                  {tab.label}
                  {tab.id === 'documents' && documentsPagination?.totalCount > 0 && (
                    <span className="residents1-tab__badge">{documentsPagination.totalCount}</span>
                  )}
                </button>
              );
            })}
          </div>
        </nav>
        
        {/* Content Area */}
        <div className="residents1-content-wrapper">
          {/* Main Content */}
          <main 
            id={`panel-${activeTab}`}
            className="residents1-main"
            role="tabpanel"
            aria-labelledby={`tab-${activeTab}`}
          >
            {renderTabContent()}
          </main>
          
          {/* Sidebar */}
          <Sidebar 
            resident={resident}
            notes={residentNotes}
            notesLoading={notesLoading}
            notesError={notesError}
            onAddNote={openNoteSidebar}
            pagination={notesPagination}
            onPageChange={goToNotesPage}
            onDeleteNote={openDeleteNoteModal}
            currentUserId={currentUserId}
            isAdmin={isAdmin}
          />
        </div>
      </div>
      
      {/* Note Sidebar */}
      <NoteSidebar
        isOpen={isNoteSidebarOpen}
        onClose={() => setIsNoteSidebarOpen(false)}
        resident={resident}
        setNotification={setNotification}
        onSuccess={handleNoteCreated}
      />
      
      {/* Delete Note Confirmation Modal */}
      <Modal
        open={isDeleteNoteModalOpen}
        onRequestClose={() => setIsDeleteNoteModalOpen(false)}
        modalHeading="Brisanje beleške"
        primaryButtonText="Obriši"
        secondaryButtonText="Otkaži"
        onRequestSubmit={confirmDeleteNote}
        danger
        size="sm"
      >
        <p>
          Da li ste sigurni da želite da obrišete ovu belešku?
        </p>
        <p style={{ marginTop: '1rem', fontSize: '0.875rem', opacity: 0.7 }}>
          Ova akcija se ne može poništiti.
        </p>
      </Modal>

      {/* Delete Family Member Confirmation Modal */}
      <Modal
        open={isDeleteFamilyModalOpen}
        onRequestClose={() => setIsDeleteFamilyModalOpen(false)}
        modalHeading="Brisanje srodnika"
        primaryButtonText={isFamilyDeleting ? 'Brisanje...' : 'Obriši'}
        secondaryButtonText="Otkaži"
        onRequestSubmit={confirmDeleteFamily}
        danger
        size="sm"
      >
        <p>
          Da li ste sigurni da želite da obrišete srodnika{' '}
          <strong>{familyMemberToDelete?.name}</strong>?
        </p>
        <p style={{ marginTop: '1rem', fontSize: '0.875rem', opacity: 0.7 }}>
          Ova akcija se ne može poništiti.
        </p>
      </Modal>

      {/* Delete Document Confirmation Modal */}
      <Modal
        open={isDeleteDocumentModalOpen}
        onRequestClose={() => setIsDeleteDocumentModalOpen(false)}
        modalHeading="Brisanje dokumenta"
        primaryButtonText={isDocumentDeleting ? 'Brisanje...' : 'Obriši'}
        secondaryButtonText="Otkaži"
        onRequestSubmit={confirmDeleteDocument}
        danger
        size="sm"
      >
        <p>
          Da li ste sigurni da želite da obrišete dokument{' '}
          <strong>{documentToDelete?.documentName}</strong>?
        </p>
        <p style={{ marginTop: '1rem', fontSize: '0.875rem', opacity: 0.7 }}>
          Ova akcija se ne može poništiti.
        </p>
      </Modal>

      {/* Document Preview Sidebar */}
      <DocumentPreviewSidebar
        isOpen={isDocumentPreviewOpen}
        onClose={closeDocumentPreview}
        document={documentToPreview}
        url={documentPreviewUrl}
        loading={documentPreviewLoading}
        error={documentPreviewError}
        onDownload={handleDownloadDocument}
      />
    </div>
  );
};

export default ResidentProfile;
